import { trpc } from "@/lib/trpc";
import { formatBJ, smartInterval, checkTradingHours } from "@/lib/timeUtils";
import SimPerformanceDashboard from "@/components/SimPerformanceDashboard";
import { AgentAnomalyBanner } from "@/components/AgentAnomalyBanner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PARAM_DESCS } from "@/lib/paramDescs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  Bot, Play, Pause, RefreshCw, Loader2, Activity, Shield, TrendingUp,
  AlertTriangle, Zap, Clock, Target, BarChart3, Settings2, Briefcase,
  Plus, Trash2, Edit3, Save, ChevronRight, FlaskConical, ArrowUpDown,
  ChevronDown, ChevronUp, Search, Star, Eye, DollarSign, History,
  ArrowUp, ArrowDown, X, Check, Gauge, Timer, RotateCcw, Info,
  CircleDot, Crosshair, ShieldAlert, TrendingDown, Layers,
  FileText, GitCompare, Trophy, Percent, AlertCircle, Terminal,
  Copy, PlayCircle, StopCircle, Wand2, LineChart, Brain, Wallet, Film,
  Library, Server, Cpu, MemoryStick, Wifi, HardDrive, Landmark,
} from "lucide-react";
import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { useIsMobile } from "@/hooks/useMobile";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import SwipeCard from "@/components/SwipeCard";
import MobileQuickBar from "@/components/MobileQuickBar";
import DailyAdvicePanel from "@/components/DailyAdvicePanel";
import EngineStatusPanel from "@/components/EngineStatusPanel";

const EngineReplayPanel = lazy(() => import("./EngineReplay"));
const AtrLearningPanel = lazy(() => import("./AtrLearning"));
const CapitalAllocationPanel = lazy(() => import("./CapitalAllocation"));
const SimulationTest = lazy(() => import("./SimulationTest"));
const ParamAnalysis = lazy(() => import("./ParamAnalysis"));
const Backtest = lazy(() => import("./Backtest"));
const Portfolio = lazy(() => import("./Portfolio"));
const StrategyTemplates = lazy(() => import("./StrategyTemplates"));
const TemplateCompare = lazy(() => import("@/components/TemplateCompare"));
const TradingSuitabilityPanel = lazy(() => import("@/components/TradingSuitabilityPanel"));
import OptimizeMonitorContent from "@/components/OptimizeMonitorContent";
import { StockSuitabilityBadge } from "@/components/StockSuitabilityBadge";
// [15b 20260815] 自动化交易合并页: 常驻两线盈亏条 + 波段线内容区
import TradingOverviewStrip from "@/components/TradingOverviewStrip";
import SwingTrade from "@/pages/SwingTrade";
const TradingDiagnosisPanel = lazy(() => import("@/components/TradingDiagnosisPanel").then(m => ({ default: m.TradingDiagnosisPanel })));

// ========== 引擎状态颜色映射 ==========
const ENGINE_STATUS_MAP: Record<string, { label: string; color: string; icon: any }> = {
  idle: { label: "空闲", color: "bg-muted text-muted-foreground", icon: Clock },
  watching: { label: "监控中", color: "bg-blue-500/20 text-blue-400", icon: Eye },
  tracking_high: { label: "跟踪高点", color: "bg-gain/20 text-gain", icon: TrendingUp},
  tracking_low: { label: "跟踪低点", color: "bg-loss/20 text-loss", icon: TrendingDown},
  confirmed: { label: "已确认", color: "bg-cyan-500/20 text-cyan-400", icon: Check },
  trading: { label: "交易中", color: "bg-primary/20 text-primary", icon: Zap},
  waiting_fill: { label: "等待做平", color: "bg-purple-500/20 text-purple-400", icon: Timer},
  cooldown: { label: "冷却中", color: "bg-orange-500/20 text-orange-400", icon: Timer},
  paused: { label: "已暂停", color: "bg-yellow-500/20 text-yellow-400", icon: Pause},
  stopped: { label: "已停止", color: "bg-destructive/20 text-destructive", icon: X },
  starting: { label: "启动中", color: "bg-emerald-500/20 text-emerald-400 animate-pulse", icon: Loader2 },
};

// ========== 日志类型映射 ==========
const LOG_TYPE_MAP: Record<string, { label: string; color: string; icon: any }> = {
  tick: { label: "Tick", color: "text-muted-foreground", icon: CircleDot },
  direction: { label: "方向确认", color: "text-blue-400", icon: ArrowUpDown },
  basis_update: { label: "基准更新", color: "text-cyan-400", icon: Target },
  trade_buy: { label: "买入成交", color: "text-gain", icon: ArrowUp },
  trade_sell: { label: "卖出成交", color: "text-loss", icon: ArrowDown },
  stop_loss: { label: "止损触发", color: "text-red-500", icon: ShieldAlert },
  pause: { label: "暂停", color: "text-yellow-400", icon: Pause },
  resume: { label: "恢复", color: "text-green-400", icon: Play },
  start: { label: "启动", color: "text-green-500", icon: Play },
  stop: { label: "停止", color: "text-red-400", icon: X },
  error: { label: "错误", color: "text-red-500", icon: AlertCircle },
  tail_protect: { label: "尾盘保护", color: "text-orange-400", icon: Shield },
  slippage: { label: "滑点保护", color: "text-orange-500", icon: AlertTriangle },
  signal: { label: "信号触发", color: "text-purple-400", icon: Zap },
  observe: { label: "观察窗口", color: "text-indigo-400", icon: Eye },
  cooldown: { label: "冷却中", color: "text-amber-400", icon: Timer },
};

// ========== 持仓显示 helper [Steven 2026-06-03 数据源稳定化] ==========
// 优先用 realHolding (server 用 broker + trade_records 算的稳定值)
// fallback simulatedHolding/currentHolding (旧字段, 引擎重启会清零, 7/8 不准)
// 详见 routers.ts dashboard 内 realHolding 计算
function displayHolding(monitor: any, tradeMode: string): number {
  // 优先用稳定源 realHolding
  if (monitor?.realHolding !== undefined && monitor.realHolding !== null) {
    return Number(monitor.realHolding) || 0;
  }
  // fallback (server 旧版没 realHolding 字段时)
  if (tradeMode === 'simulation') {
    return Number(monitor?.simulatedHolding ?? 10000) || 0;
  }
  return Number(monitor?.currentHolding ?? 0) || 0;
}
// 真实今日盈利: 用 trade_records 累加 (跨 SIM/LIVE 统一, 不被引擎重启清零)
function displayTodayPnl(monitor: any): number {
  if (monitor?.realTodayPnl !== undefined && monitor.realTodayPnl !== null) {
    return Number(monitor.realTodayPnl) || 0;
  }
  return Number(monitor?.todayProfit ?? 0) || 0;
}

// ========== 模板标签解析 helper [2026-06-02 模板一致性彻底修复] ==========
// 把 conservative/balanced/aggressive/highfreq + pyramid_X/v2_X + custom/defensive/paused 统一映射为可显示标签
function templateLabel(tpl: string | null | undefined, opts?: { short?: boolean }): { label: string; color: string } {
  const t = String(tpl || '');
  if (!t) return { label: '未设置', color: 'border-muted-foreground/30 text-muted-foreground' };
  const short = !!opts?.short;
  const baseLabel = (s: string) => s === 'conservative' ? (short ? '保守' : '保守型')
    : s === 'balanced' ? (short ? '稳健' : '稳健型')
    : s === 'aggressive' ? (short ? '积极进取' : '积极进取型')
    : s === 'highfreq' ? (short ? '高频赚取' : '高频赚取型')
    : s;
  if (t === 'custom') return { label: '自定义', color: 'border-purple-500/50 text-purple-400' };
  if (t === 'defensive') return { label: short ? '护盘' : '🛡 护盘模式 (系统自动)', color: 'border-cyan-500/50 text-cyan-400' };
  if (t === 'paused') return { label: short ? '暂停' : '⏸ 暂停 (系统自动)', color: 'border-amber-500/50 text-amber-400' };
  if (t.startsWith('v2_')) {
    const base = t.slice(3);
    const c = base === 'conservative' ? 'border-blue-500/50 text-blue-400'
      : base === 'balanced' ? 'border-green-500/50 text-green-400'
      : base === 'aggressive' ? 'border-orange-500/50 text-orange-400'
      : base === 'highfreq' ? 'border-pink-500/50 text-pink-400'
      : 'border-muted-foreground/30 text-muted-foreground';
    return { label: (short ? 'V2·' : 'V2: ') + baseLabel(base), color: c };
  }
  if (t.startsWith('pyramid_')) {
    const base = t.slice(8);
    const c = base === 'conservative' ? 'border-blue-500/50 text-blue-400'
      : base === 'balanced' ? 'border-green-500/50 text-green-400'
      : base === 'aggressive' ? 'border-orange-500/50 text-orange-400'
      : base === 'highfreq' ? 'border-pink-500/50 text-pink-400'
      : 'border-muted-foreground/30 text-muted-foreground';
    return { label: (short ? '塔·' : '金字塔: ') + baseLabel(base), color: c };
  }
  const c = t === 'conservative' ? 'border-blue-500/50 text-blue-400'
    : t === 'balanced' ? 'border-green-500/50 text-green-400'
    : t === 'aggressive' ? 'border-orange-500/50 text-orange-400'
    : t === 'highfreq' ? 'border-pink-500/50 text-pink-400'
    : 'border-muted-foreground/30 text-muted-foreground';
  return { label: baseLabel(t), color: c };
}

// ========== T+0 交易参数编辑弹窗 ==========
// ========== 场景化流程式参数设置弹窗 ==========
// [参数悬浮 v2 20260719] 有描述则包 Tooltip: 桌面hover即显, 手机点按toggle+5s自动关(ui/tooltip.tsx Mobile fix)
// ⓘ 仅视觉提示, 点击事件冒泡到 TooltipTrigger 统一处理, 不要给它加 onClick(会双 toggle 抵消)
function ParamWrap({ pkey, children }: { pkey?: string; children: any }) {
  const desc = pkey ? PARAM_DESCS[pkey] : undefined;
  if (!desc) return <span className="inline-flex items-center gap-0.5 mx-0.5">{children}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-0.5 mx-0.5 cursor-help">
          {children}
          <Info className="h-2.5 w-2.5 shrink-0 text-muted-foreground/50" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left text-xs leading-relaxed whitespace-pre-wrap">
        <span className="block font-mono text-[10px] opacity-60 mb-0.5">{pkey}</span>{desc}
      </TooltipContent>
    </Tooltip>
  );
}

function InlineParam({ value, defaultValue, unit, step, onChange, width, pkey }: {
  value: number | null; defaultValue: number; unit: string; step: number; onChange: (v: number | null) => void; width?: string; pkey?: string;
}) {
  return (
    <ParamWrap pkey={pkey}>
      <Input type="number" step={step}
        className={`h-7 text-xs text-center font-mono bg-primary/5 border-primary/30 focus:border-primary ${width || 'w-16'}`}
        placeholder={`${defaultValue}`}
        value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))} />
      {unit && <span className="text-[10px] text-muted-foreground">{unit}</span>}
    </ParamWrap>
  );
}

function PctParam({ value, defaultValue, step, onChange, width, pkey }: {
  value: number | null; defaultValue: number; step?: number; onChange: (v: number | null) => void; width?: string; pkey?: string;
}) {
  // 存储为小数(0.008), 输入/展示为百分数(0.8%)
  return (
    <ParamWrap pkey={pkey}>
      <Input type="number" step={step ?? 0.1}
        className={`h-7 text-xs text-center font-mono bg-primary/5 border-primary/30 focus:border-primary ${width || 'w-16'}`}
        placeholder={`${+(defaultValue * 100).toFixed(4)}`}
        value={value === null || value === undefined ? "" : +(value * 100).toFixed(4)}
        onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value) / 100)} />
      <span className="text-[10px] text-muted-foreground">%</span>
    </ParamWrap>
  );
}

function TradingParamDialog({
  open, onOpenChange, monitor, defaultParams, onSave, isSaving,
}: {
  open: boolean; onOpenChange: (open: boolean) => void; monitor: any; defaultParams: any; onSave: (params: any) => void; isSaving: boolean;
}) {
  const dp = defaultParams || {};
  const [form, setForm] = useState(() => ({
    // 方向一
    s1CallbackPct: monitor?.s1CallbackPct ?? null,
    s1ObserveWindowSec: monitor?.s1ObserveWindowSec ?? null,
    s1ConfirmDropMaxPct: monitor?.s1ConfirmDropMaxPct ?? null,
    s1FillWaitSec: monitor?.s1FillWaitSec ?? null,
    s1BuybackDropPct: monitor?.s1BuybackDropPct ?? null,
    s1StopLossRisePct: monitor?.s1StopLossRisePct ?? null,
    // 方向二
    s2BouncePct: monitor?.s2BouncePct ?? null,
    s2ObserveWindowSec: monitor?.s2ObserveWindowSec ?? null,
    s2ConfirmRiseMinPct: monitor?.s2ConfirmRiseMinPct ?? null,
    s2FillWaitSec: monitor?.s2FillWaitSec ?? null,
    s2SellRisePct: monitor?.s2SellRisePct ?? null,
    s2StopLossDropPct: monitor?.s2StopLossDropPct ?? null,
    // 通用
    tradeLotSize: monitor?.tradeLotSize ?? null,
    maxPositionShares: monitor?.maxPositionShares ?? null,
    maxDailyRounds: monitor?.maxDailyRounds ?? null,
    tailProtectMinutes: monitor?.tailProtectMinutes ?? null,
    slippageProtectPct: monitor?.slippageProtectPct ?? null,
    joinAuction: monitor?.joinAuction ?? null,
    maxDailyLossAmount: monitor?.maxDailyLossAmount ?? null,
    dailyProfitTarget: monitor?.dailyProfitTarget ?? null,
    stopLossCooldownSeconds: monitor?.stopLossCooldownSeconds ?? null,
    maxConsecutiveStopLoss: monitor?.maxConsecutiveStopLoss ?? null,
    stopLossPauseMinutes: monitor?.stopLossPauseMinutes ?? null,
    // 开盘暂停（新增）
    openPauseMinutes: monitor?.openPauseMinutes ?? null,
    openPauseEnabled: monitor?.openPauseEnabled ?? null,
    // 信号质量三重过滤（新增）
    signalMinVolumeRatio: monitor?.signalMinVolumeRatio ?? null,
    signalMaxSpreadPct: monitor?.signalMaxSpreadPct ?? null,
    signalMinExpectedProfit: monitor?.signalMinExpectedProfit ?? null,
    signalMinNetProfitPct: monitor?.signalMinNetProfitPct ?? null,
    // 开盘窗口延长
    openPhase1Minutes: monitor?.openPhase1Minutes ?? null,
    openPhase1WindowMultiplier: monitor?.openPhase1WindowMultiplier ?? null,
    openPhase2Minutes: monitor?.openPhase2Minutes ?? null,
    openPhase2WindowMultiplier: monitor?.openPhase2WindowMultiplier ?? null,
    // 涨跌停板观察窗口
    limitUpOpenWindowMultiplier: monitor?.limitUpOpenWindowMultiplier ?? null,
    limitDownOpenWindowMultiplier: monitor?.limitDownOpenWindowMultiplier ?? null,
    // 均线斜率观察窗口
    slopeHighThreshold: monitor?.slopeHighThreshold ?? null,
    slopeHighWindowMultiplier: monitor?.slopeHighWindowMultiplier ?? null,
    slopeMidThreshold: monitor?.slopeMidThreshold ?? null,
    slopeMidWindowMultiplier: monitor?.slopeMidWindowMultiplier ?? null,
    slopeCalcPeriodSec: monitor?.slopeCalcPeriodSec ?? null,
    // 挂单策略
    orderStrategy: monitor?.orderStrategy ?? null,
    smartChaseMaxTicks: monitor?.smartChaseMaxTicks ?? null,
    // 高级
    adaptiveThreshold: monitor?.adaptiveThreshold ?? null,
    volumeConfirmMultiple: monitor?.volumeConfirmMultiple ?? null,
    basisDecayMinutes: monitor?.basisDecayMinutes ?? null,
    basisDecayPct: monitor?.basisDecayPct ?? null,
    // 动态阈值参数
    dynamicThresholdWindow: monitor?.dynamicThresholdWindow ?? null,
    dynamicThresholdMinMult: monitor?.dynamicThresholdMinMult ?? null,
    dynamicThresholdMaxMult: monitor?.dynamicThresholdMaxMult ?? null,
    dynamicThresholdBaseVol: monitor?.dynamicThresholdBaseVol ?? null,
    // VWAP方向辅助
    useVwap: monitor?.useVwap ?? null,
    vwapFilterStrength: monitor?.vwapFilterStrength ?? null,
    vwapAtrMultiplier: monitor?.vwapAtrMultiplier ?? null,
    // 盘口数据分析
    useOrderbookAnalysis: monitor?.useOrderbookAnalysis ?? null,
    orderbookImbalanceThreshold: monitor?.orderbookImbalanceThreshold ?? null,
    orderbookLargeOrderMultiple: monitor?.orderbookLargeOrderMultiple ?? null,
    // 波动率预筛选
    volatilityFilter: monitor?.volatilityFilter ?? null,
    minDayRangePct: monitor?.minDayRangePct ?? null,
    maxDayRangePct: monitor?.maxDayRangePct ?? null,
    minTurnoverVal: monitor?.minTurnoverVal ?? null,
    // 限价委托参数
    limitOrderOffsetPct: monitor?.limitOrderOffsetPct ?? null,
    limitOrderTimeoutSec: monitor?.limitOrderTimeoutSec ?? null,
    // [Steven 2026-06-04] T+1 持仓过夜功能已完整移除 (3 模型评审一致)
    // [trail v2 S1/S2 5/31] 业务 S1/S2 各一份 + 4 技术参数全局
    takeProfitModeS1: (monitor as any)?.takeProfitModeS1 ?? null,
    takeProfitActivationPctS1: (monitor as any)?.takeProfitActivationPctS1 ?? null,
    takeProfitTrailPctS1: (monitor as any)?.takeProfitTrailPctS1 ?? null,
    takeProfitModeS2: (monitor as any)?.takeProfitModeS2 ?? null,
    takeProfitActivationPctS2: (monitor as any)?.takeProfitActivationPctS2 ?? null,
    takeProfitTrailPctS2: (monitor as any)?.takeProfitTrailPctS2 ?? null,
    trailTickFloorMult: (monitor as any)?.trailTickFloorMult ?? null,
    trailMinTickVolumeS1: (monitor as any)?.trailMinTickVolumeS1 ?? null,
    trailMinTickVolumeS2: (monitor as any)?.trailMinTickVolumeS2 ?? null,
    trailForceCloseMaxHoldSecS1: (monitor as any)?.trailForceCloseMaxHoldSecS1 ?? null,
    trailForceCloseMaxHoldSecS2: (monitor as any)?.trailForceCloseMaxHoldSecS2 ?? null,
    trailSkipOpenMinutes: (monitor as any)?.trailSkipOpenMinutes ?? null,
    // [Phase 1 schedule 5/31] 时段调度 (全局)
    takeProfitSchedule: (monitor as any)?.takeProfitSchedule ?? null,
    // [Phase 1 trade_mode 6/1] monitor 级实盘控制
    tradeMode: (monitor as any)?.tradeMode ?? null,
    // 智能风格自适应
    smartStyleAdapt: monitor?.smartStyleAdapt ?? null,
    smartStyleLowRange: monitor?.smartStyleLowRange ?? null,
    smartStyleHighRange: monitor?.smartStyleHighRange ?? null,
    // [2026-06-17 位置+分钟反转快进]
    fastEntryAtExtreme: (monitor as any)?.fastEntryAtExtreme ?? null,
    fastEntryPosPct: (monitor as any)?.fastEntryPosPct ?? null,
    entryMinDayPositionPct: (monitor as any)?.entryMinDayPositionPct ?? null,
    entryMaxDayPositionPct: (monitor as any)?.entryMaxDayPositionPct ?? null,
    extremeHoldEntry: (monitor as any)?.extremeHoldEntry ?? null,
    extremeHoldSec: (monitor as any)?.extremeHoldSec ?? null,
    extremeHoldBreakBufferPct: (monitor as any)?.extremeHoldBreakBufferPct ?? null,
    singleSideBlockEnabled: (monitor as any)?.singleSideBlockEnabled ?? null,
    singleSideEffThreshold: (monitor as any)?.singleSideEffThreshold ?? null,
    singleSideMinRangePct: (monitor as any)?.singleSideMinRangePct ?? null,
    v2MaxDayRangeATR: (monitor as any)?.v2MaxDayRangeATR ?? null,
    // 尾盘快速做平
    tailQuickTrade: monitor?.tailQuickTrade ?? null,
    tailQuickDropPct: monitor?.tailQuickDropPct ?? null,
    tailQuickRisePct: monitor?.tailQuickRisePct ?? null,
    tailQuickOffsetPct: monitor?.tailQuickOffsetPct ?? null,
    tailQuickTimeoutSec: monitor?.tailQuickTimeoutSec ?? null,
    // 券商绑定
    brokerName: monitor?.brokerName ?? "xingye",
    marketDataSource: monitor?.marketDataSource ?? "xtquant",
    // 金字塔加仓模式
    positionMode: monitor?.positionMode ?? "extreme_pair",
    // V2 参数
    v2SellZoneMode: monitor?.v2SellZoneMode ?? "atr",
    v2SellZoneValue: monitor?.v2SellZoneValue ?? 0.4,
    v2BuyZoneMode: monitor?.v2BuyZoneMode ?? "atr",
    v2BuyZoneValue: monitor?.v2BuyZoneValue ?? 0.4,
    v2TargetMode: monitor?.v2TargetMode ?? "atr",
    v2TargetValue: monitor?.v2TargetValue ?? 0.3,
    v2StopLossMode: monitor?.v2StopLossMode ?? "atr",
    v2StopLossValue: monitor?.v2StopLossValue ?? 0.5,
    v2MaxConsecutive: monitor?.v2MaxConsecutive ?? 3,
    v2StepATR: monitor?.v2StepATR ?? 0.3,
    v2MaxExposure: monitor?.v2MaxExposure ?? 3000,
    v2ConfirmSec: monitor?.v2ConfirmSec ?? 30,
    v2TimeoutSec: monitor?.v2TimeoutSec ?? 300,
    v2MaxDailyLossMode: monitor?.v2MaxDailyLossMode ?? "amount",
    v2MaxDailyLossValue: monitor?.v2MaxDailyLossValue ?? 500,
    v2MaxDailyRounds: monitor?.v2MaxDailyRounds ?? 3,
    v2MinDayRangeATR: monitor?.v2MinDayRangeATR ?? 0.3,
    v2FlatDeadlineMin: monitor?.v2FlatDeadlineMin ?? 20,
    v2TimeoutIsStopLoss: monitor?.v2TimeoutIsStopLoss ?? 1,
    v2EntryOrderStrategy: monitor?.v2EntryOrderStrategy ?? "weighted_mid",
    v2ExitOrderStrategy: monitor?.v2ExitOrderStrategy ?? "counterparty",
    pyramidMaxLayers: monitor?.pyramidMaxLayers ?? null,
    pyramidMaxExposure: monitor?.pyramidMaxExposure ?? null,
    pyramidScaleMode: monitor?.pyramidScaleMode ?? "equal",
    pyramidConfirmPct: monitor?.pyramidConfirmPct ?? null,
    pyramidTailAutoClose: monitor?.pyramidTailAutoClose ?? 1,
    pyramidTailCloseMinutes: monitor?.pyramidTailCloseMinutes ?? null,
    // [2026-06-25 Cat1] zigzag 9 + A2 11 参数 (per-stock, null=全局默认)
    zzMult: (monitor as any)?.zzMult ?? null,
    zzTrailMult: (monitor as any)?.zzTrailMult ?? null,
    zzSlMult: (monitor as any)?.zzSlMult ?? null,
    zzGateMult: (monitor as any)?.zzGateMult ?? null,
    zzCth: (monitor as any)?.zzCth ?? null,
    zzMaxRounds: (monitor as any)?.zzMaxRounds ?? null,
    zzSkipExtremePos: (monitor as any)?.zzSkipExtremePos ?? null,
    zzPriorRangePct: (monitor as any)?.zzPriorRangePct ?? null,
    zzDirection: (monitor as any)?.zzDirection ?? null,
    zzTailNoOpenMin: (monitor as any)?.zzTailNoOpenMin ?? null,
    atrTrailingK: (monitor as any)?.atrTrailingK ?? null,
    s2MinProfitPct: (monitor as any)?.s2MinProfitPct ?? null,
    forceMarketDeviationMult: (monitor as any)?.forceMarketDeviationMult ?? null,
    timeStopProfitPct: (monitor as any)?.timeStopProfitPct ?? null,
    timeStopAtrMax: (monitor as any)?.timeStopAtrMax ?? null,
    timeStopElapsedFrac: (monitor as any)?.timeStopElapsedFrac ?? null,
    volStopAtrPct: (monitor as any)?.volStopAtrPct ?? null,
    entryRequireTrend: (monitor as any)?.entryRequireTrend ?? null,
    trendStrictSkip: (monitor as any)?.trendStrictSkip ?? null,
    afternoonS1PauseStartHm: (monitor as any)?.afternoonS1PauseStartHm ?? null,
    afternoonS1PauseEndHm: (monitor as any)?.afternoonS1PauseEndHm ?? null,
    // [2026-06-02 模板一致性彻底修复] 当前生效模板 — 作为 form 字段统一管理, "当前"显示/按钮高亮/保存都用这个
    // [刀3 20260805 词典P1] ep出场族水合(修: 面板显默认值而非DB实值 — 0803"全员放宽trail"错写takeProfit族的根源)
    epActMult: (monitor as any)?.epActMult ?? null,
    epActFloor: (monitor as any)?.epActFloor ?? null,
    epTrailMult: (monitor as any)?.epTrailMult ?? null,
    epTrailFloor: (monitor as any)?.epTrailFloor ?? null,
    epSlMult: (monitor as any)?.epSlMult ?? null,
    epSlFloor: (monitor as any)?.epSlFloor ?? null,
    epLockFrac: (monitor as any)?.epLockFrac ?? null,
    // [20260715] ⑥ W_OPEN/sp15 字段水合(修: 面板显示默认值而非DB实值)
    epWopenEnable: (monitor as any)?.epWopenEnable ?? null,
    epWopenRp: (monitor as any)?.epWopenRp ?? null,
    epWopenMinSod: (monitor as any)?.epWopenMinSod ?? null,
    epWopenWinEnd: (monitor as any)?.epWopenWinEnd ?? null,
    epWopenSurgeFrac: (monitor as any)?.epWopenSurgeFrac ?? null,
    epWopenRetraceFrac: (monitor as any)?.epWopenRetraceFrac ?? null,
    epWopenPeakAgeSec: (monitor as any)?.epWopenPeakAgeSec ?? null,
    epWopenTickRatio: (monitor as any)?.epWopenTickRatio ?? null,
    epWopenStopFrac: (monitor as any)?.epWopenStopFrac ?? null,
    epWopenQtyFrac: (monitor as any)?.epWopenQtyFrac ?? null,
    epWopenCAuto: (monitor as any)?.epWopenCAuto ?? null,
    epWopenCBreakFrac: (monitor as any)?.epWopenCBreakFrac ?? null,
    epWopenCMomSec: (monitor as any)?.epWopenCMomSec ?? null,
    epWopenCMomFrac: (monitor as any)?.epWopenCMomFrac ?? null,
    epWopenCActbuyMax: (monitor as any)?.epWopenCActbuyMax ?? null,
    epWopenCTickRatio: (monitor as any)?.epWopenCTickRatio ?? null,
    epWopenCExtCapFrac: (monitor as any)?.epWopenCExtCapFrac ?? null,
    epOpenSpMult: (monitor as any)?.epOpenSpMult ?? null,
    epOpenGateMin: (monitor as any)?.epOpenGateMin ?? null,
    activeTemplate: (monitor?.activeTemplate as string | null) ?? null,
  }));
  // [Steven 6/2 qwen UX] V2 模式默认进 v2_entry tab, 防用户看到 s1/s2 paired 描述误以为 V2 用固定 pct
  const [activeTab, setActiveTab] = useState<string>(
    (monitor as any)?.positionMode === 'vwap_atr' ? 'v2_entry'
      : (monitor as any)?.positionMode === 'zigzag_fusion' ? 'zigzag'
      : (monitor as any)?.positionMode === 'extreme_dynamic' ? 'extreme'
      : (monitor as any)?.positionMode === 'extreme_pair' ? 'extreme_pair'
      : (monitor as any)?.positionMode === 'huimaqiang' ? 'common' : 's1'
  );
  // [Steven 6/2 qwen UX] positionMode 切换时, activeTab 跳到匹配的入场 tab
  useEffect(() => {
    const isV2 = form.positionMode === 'vwap_atr';
    const isZigzag = form.positionMode === 'zigzag_fusion';
    const isEd = form.positionMode === 'extreme_dynamic';
    const isEp = form.positionMode === 'extreme_pair';
    const isHmq = form.positionMode === 'huimaqiang';
    const isPyramid = form.positionMode === 'pyramid' || form.positionMode === 'pyramid_long_hold';
    // [2026-06-25 per-mode tab] 切模式时若当前 tab 对该模式无效, 跳到该模式默认入场 tab
    if (isV2 && activeTab !== 'v2_entry' && activeTab !== 'v2_risk' && activeTab !== 'common') {
      setActiveTab('v2_entry');
    } else if (isZigzag && activeTab !== 'zigzag' && activeTab !== 'common') {
      setActiveTab('zigzag');
    } else if (isEd && activeTab !== 'extreme' && activeTab !== 'common') {
      setActiveTab('extreme');
    } else if (isEp && activeTab !== 'extreme_pair' && activeTab !== 'common') {
      setActiveTab('extreme_pair');
    } else if (isHmq && activeTab !== 'common') {
      setActiveTab('common');
    } else if (!isV2 && !isZigzag && !isEd && !isEp && !isHmq && (activeTab === 'v2_entry' || activeTab === 'v2_risk' || activeTab === 'zigzag' || activeTab === 'extreme' || activeTab === 'extreme_pair')) {
      setActiveTab('s1');
    }
    // [Steven 6/2 Q4] 切策略引擎时, activeTemplate 自动重置为对应 balanced (避免 V2 选中但 tpl=pyramid_xxx)
    const curTpl = form.activeTemplate || '';
    if (isV2 && (curTpl === 'balanced' || curTpl.startsWith('pyramid_'))) {
      setField('activeTemplate', 'v2_balanced');
    } else if (isPyramid && (curTpl === 'balanced' || curTpl.startsWith('v2_'))) {
      setField('activeTemplate', 'pyramid_balanced');
    } else if (!isV2 && !isPyramid && (curTpl.startsWith('v2_') || curTpl.startsWith('pyramid_'))) {
      setField('activeTemplate', 'balanced');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.positionMode]);
  // [2026-06-02 模板一致性彻底修复] 删 selectedTemplate 冗余 state, 改用 form.activeTemplate (按钮高亮/显示/保存统一来源)

  const setField = (key: string, val: number | null) => setForm(prev => ({ ...prev, [key]: val }));

  const { data: templates } = trpc.agentMonitor.paramTemplates.useQuery();
  const applyTemplate = trpc.agentMonitor.applyTemplate.useMutation({
    onSuccess: () => { toast.success("模板已应用"); onOpenChange(false); },
    onError: (err: any) => toast.error(err.message),
  });
  const autoCalcR = trpc.agentMonitor.autoCalcR.useMutation({
    onSuccess: (d: any) => { setField('epPriorRangePct', d.R); toast.success(`已按近${d.days}日均振幅算得 R=${d.R}%，核对后点保存`); },
    onError: (e: any) => toast.error("自动算R失败: " + (e?.message || "")),
  });

  // [Steven 2026-06-01 Layer 1 防护] 实盘 entry_leg 前查全局 mode + monitor 持仓
  const { data: _ifConfigForTradeMode } = trpc.interfaceConfig.get.useQuery(undefined, { staleTime: 30000 });
  const _globalLive = ((_ifConfigForTradeMode as any)?.tradeMode || 'simulation') === 'smtq';
  const _monitorHolding = Number((monitor as any)?.currentHolding || 0);
  const _hasNoPosition = _monitorHolding <= 0;

  const handleApplyTemplate = (templateId: string) => {
    if (!monitor?.id) return;
    const tpl = (templates || []).find((t: any) => t.id === templateId);
    if (!tpl) return;
    const p = tpl.params as any;
    // [Bug fix 5/31] 用 functional setForm + ...prev 保留 positionMode / pyramid* / v2* 等模板不覆盖字段
    // 之前 setForm({...}) 完全替换 → positionMode 丢失 → 渲染 fallback paired → V2 模式被误切 V1
    setForm((prev: any) => ({ ...prev,
      s1CallbackPct: p.s1CallbackPct ?? null, s1ObserveWindowSec: p.s1ObserveWindowSec ?? null,
      s1ConfirmDropMaxPct: p.s1ConfirmDropMaxPct ?? null, s1FillWaitSec: p.s1FillWaitSec ?? null,
      s1BuybackDropPct: p.s1BuybackDropPct ?? null, s1StopLossRisePct: p.s1StopLossRisePct ?? null,
      s2BouncePct: p.s2BouncePct ?? null, s2ObserveWindowSec: p.s2ObserveWindowSec ?? null,
      s2ConfirmRiseMinPct: p.s2ConfirmRiseMinPct ?? null, s2FillWaitSec: p.s2FillWaitSec ?? null,
      s2SellRisePct: p.s2SellRisePct ?? null, s2StopLossDropPct: p.s2StopLossDropPct ?? null,
      tradeLotSize: p.tradeLotSize ?? null, maxPositionShares: p.maxPositionShares ?? null,
      maxDailyRounds: p.maxDailyRounds ?? null, tailProtectMinutes: p.tailProtectMinutes ?? null,
      slippageProtectPct: p.slippageProtectPct ?? null, joinAuction: 0,
      maxDailyLossAmount: p.maxDailyLossAmount ?? null, stopLossCooldownSeconds: p.stopLossCooldownSeconds ?? null,
      dailyProfitTarget: p.dailyProfitTarget ?? null,
      maxConsecutiveStopLoss: p.maxConsecutiveStopLoss ?? null, stopLossPauseMinutes: p.stopLossPauseMinutes ?? null,
      openPauseMinutes: p.openPauseMinutes ?? null, openPauseEnabled: p.openPauseEnabled ?? null,
      signalMinVolumeRatio: p.signalMinVolumeRatio ?? null, signalMaxSpreadPct: p.signalMaxSpreadPct ?? null,
      signalMinExpectedProfit: p.signalMinExpectedProfit ?? null, signalMinNetProfitPct: p.signalMinNetProfitPct ?? null,
      openPhase1Minutes: p.openPhase1Minutes ?? null, openPhase1WindowMultiplier: p.openPhase1WindowMultiplier ?? null,
      openPhase2Minutes: p.openPhase2Minutes ?? null, openPhase2WindowMultiplier: p.openPhase2WindowMultiplier ?? null,
      limitUpOpenWindowMultiplier: p.limitUpOpenWindowMultiplier ?? null, limitDownOpenWindowMultiplier: p.limitDownOpenWindowMultiplier ?? null,
      slopeHighThreshold: p.slopeHighThreshold ?? null, slopeHighWindowMultiplier: p.slopeHighWindowMultiplier ?? null,
      slopeMidThreshold: p.slopeMidThreshold ?? null, slopeMidWindowMultiplier: p.slopeMidWindowMultiplier ?? null,
      slopeCalcPeriodSec: p.slopeCalcPeriodSec ?? null,
      orderStrategy: p.orderStrategy ?? null, smartChaseMaxTicks: p.smartChaseMaxTicks ?? null,
      adaptiveThreshold: p.adaptiveThreshold ?? null, volumeConfirmMultiple: p.volumeConfirmMultiple ?? null,
      basisDecayMinutes: p.basisDecayMinutes ?? null, basisDecayPct: p.basisDecayPct ?? null,
      dynamicThresholdWindow: p.dynamicThresholdWindow ?? null, dynamicThresholdMinMult: p.dynamicThresholdMinMult ?? null,
      dynamicThresholdMaxMult: p.dynamicThresholdMaxMult ?? null, dynamicThresholdBaseVol: p.dynamicThresholdBaseVol ?? null,
      useVwap: p.useVwap ?? null, vwapFilterStrength: p.vwapFilterStrength ?? null, vwapAtrMultiplier: p.vwapAtrMultiplier ?? null,
      useOrderbookAnalysis: p.useOrderbookAnalysis ?? null, orderbookImbalanceThreshold: p.orderbookImbalanceThreshold ?? null,
      orderbookLargeOrderMultiple: p.orderbookLargeOrderMultiple ?? null,
      volatilityFilter: p.volatilityFilter ?? null, minDayRangePct: p.minDayRangePct ?? null,
      maxDayRangePct: p.maxDayRangePct ?? null, minTurnoverVal: p.minTurnoverVal ?? null,
      limitOrderOffsetPct: p.limitOrderOffsetPct ?? null, limitOrderTimeoutSec: p.limitOrderTimeoutSec ?? null,
      // [trail v2 S1/S2 5/31] 业务 S1/S2 各一份 + 4 技术参数全局
      takeProfitModeS1: (p as any).takeProfitModeS1 ?? null,
      takeProfitActivationPctS1: (p as any).takeProfitActivationPctS1 ?? null,
      takeProfitTrailPctS1: (p as any).takeProfitTrailPctS1 ?? null,
      takeProfitModeS2: (p as any).takeProfitModeS2 ?? null,
      takeProfitActivationPctS2: (p as any).takeProfitActivationPctS2 ?? null,
      takeProfitTrailPctS2: (p as any).takeProfitTrailPctS2 ?? null,
      trailTickFloorMult: (p as any).trailTickFloorMult ?? null,
      trailMinTickVolumeS1: (p as any).trailMinTickVolumeS1 ?? null,
      trailMinTickVolumeS2: (p as any).trailMinTickVolumeS2 ?? null,
      trailForceCloseMaxHoldSecS1: (p as any).trailForceCloseMaxHoldSecS1 ?? null,
      trailForceCloseMaxHoldSecS2: (p as any).trailForceCloseMaxHoldSecS2 ?? null,
      trailSkipOpenMinutes: (p as any).trailSkipOpenMinutes ?? null,
      // [Phase 1 schedule 5/31] 时段调度 (全局)
      takeProfitSchedule: (p as any).takeProfitSchedule ?? null,
      // [Phase 1 trade_mode 6/1] monitor 级实盘控制
      tradeMode: (p as any).tradeMode ?? null,
      smartStyleAdapt: p.smartStyleAdapt ?? null, smartStyleLowRange: p.smartStyleLowRange ?? null,
      smartStyleHighRange: p.smartStyleHighRange ?? null,
      fastEntryAtExtreme: (p as any).fastEntryAtExtreme ?? null, fastEntryPosPct: (p as any).fastEntryPosPct ?? null,
      entryMinDayPositionPct: (p as any).entryMinDayPositionPct ?? null, entryMaxDayPositionPct: (p as any).entryMaxDayPositionPct ?? null,
      extremeHoldEntry: (p as any).extremeHoldEntry ?? null, extremeHoldSec: (p as any).extremeHoldSec ?? null,
      extremeHoldBreakBufferPct: (p as any).extremeHoldBreakBufferPct ?? null, singleSideBlockEnabled: (p as any).singleSideBlockEnabled ?? null,
      singleSideEffThreshold: (p as any).singleSideEffThreshold ?? null, singleSideMinRangePct: (p as any).singleSideMinRangePct ?? null, v2MaxDayRangeATR: (p as any).v2MaxDayRangeATR ?? null,
      tailQuickTrade: p.tailQuickTrade ?? null, tailQuickDropPct: p.tailQuickDropPct ?? null,
      tailQuickRisePct: p.tailQuickRisePct ?? null, tailQuickOffsetPct: p.tailQuickOffsetPct ?? null,
      tailQuickTimeoutSec: p.tailQuickTimeoutSec ?? null,
      // [2026-06-02 模板一致性彻底修复] 同步 positionMode (v2_/pyramid_ 模板要切引擎) + activeTemplate
      positionMode: tpl.id?.startsWith('v2_') ? 'vwap_atr'
        : tpl.id?.startsWith('pyramid_') ? 'pyramid'
        : (prev.positionMode === 'zigzag_fusion' ? 'zigzag_fusion' : 'paired'),  // [zigzag切割 2026-06-26] 不把zigzag翻回paired

      activeTemplate: tpl.id,
      // V2 nested 子参数 (之前 handleApplyTemplate 只填了 V1 字段, V2 模板里 v2*/pyramid* 没被填进 form)
      v2SellZoneMode: (p as any).v2SellZoneMode ?? prev.v2SellZoneMode,
      v2SellZoneValue: (p as any).v2SellZoneValue ?? prev.v2SellZoneValue,
      v2BuyZoneMode: (p as any).v2BuyZoneMode ?? prev.v2BuyZoneMode,
      v2BuyZoneValue: (p as any).v2BuyZoneValue ?? prev.v2BuyZoneValue,
      v2TargetMode: (p as any).v2TargetMode ?? prev.v2TargetMode,
      v2TargetValue: (p as any).v2TargetValue ?? prev.v2TargetValue,
      v2StopLossMode: (p as any).v2StopLossMode ?? prev.v2StopLossMode,
      v2StopLossValue: (p as any).v2StopLossValue ?? prev.v2StopLossValue,
      v2MaxConsecutive: (p as any).v2MaxConsecutive ?? prev.v2MaxConsecutive,
      v2StepATR: (p as any).v2StepATR ?? prev.v2StepATR,
      v2MaxExposure: (p as any).v2MaxExposure ?? prev.v2MaxExposure,
      v2ConfirmSec: (p as any).v2ConfirmSec ?? prev.v2ConfirmSec,
      v2TimeoutSec: (p as any).v2TimeoutSec ?? prev.v2TimeoutSec,
      v2MaxDailyLossMode: (p as any).v2MaxDailyLossMode ?? prev.v2MaxDailyLossMode,
      v2MaxDailyLossValue: (p as any).v2MaxDailyLossValue ?? prev.v2MaxDailyLossValue,
      v2MaxDailyRounds: (p as any).v2MaxDailyRounds ?? prev.v2MaxDailyRounds,
      v2MinDayRangeATR: (p as any).v2MinDayRangeATR ?? prev.v2MinDayRangeATR,
      v2FlatDeadlineMin: (p as any).v2FlatDeadlineMin ?? prev.v2FlatDeadlineMin,
      v2TimeoutIsStopLoss: (p as any).v2TimeoutIsStopLoss ?? prev.v2TimeoutIsStopLoss,
      v2EntryOrderStrategy: (p as any).v2EntryOrderStrategy ?? prev.v2EntryOrderStrategy,
      v2ExitOrderStrategy: (p as any).v2ExitOrderStrategy ?? prev.v2ExitOrderStrategy,
      // pyramid 子参数 (同理)
      pyramidMaxLayers: (p as any).pyramidMaxLayers ?? prev.pyramidMaxLayers,
      pyramidMaxExposure: (p as any).pyramidMaxExposure ?? prev.pyramidMaxExposure,
      pyramidScaleMode: (p as any).pyramidScaleMode ?? prev.pyramidScaleMode,
      pyramidConfirmPct: (p as any).pyramidConfirmPct ?? prev.pyramidConfirmPct,
      pyramidTailAutoClose: (p as any).pyramidTailAutoClose ?? prev.pyramidTailAutoClose,
      pyramidTailCloseMinutes: (p as any).pyramidTailCloseMinutes ?? prev.pyramidTailCloseMinutes,
    }));
    toast.info(`已加载「${tpl.name}」模板参数，请点击保存生效`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            T+0 交易参数 - {monitor?.stockName} ({monitor?.stockCode})
          </DialogTitle>
          <DialogDescription className="text-xs">
            按交易流程设置参数，留空使用全局默认值。参数调整后下一个交易周期生效。
          </DialogDescription>
        </DialogHeader>

        {/* 策略引擎切换 */}
        <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500/5 to-green-500/5 border border-border/30 mb-1">
          <div className="flex items-center gap-3">
            <Tooltip><TooltipTrigger asChild><span className="text-xs font-medium text-muted-foreground flex items-center gap-1 cursor-help">策略引擎 <Info className="h-2.5 w-2.5" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-sm text-xs"><p>V1（配对模式）：基于开盘价判断方向，回调/反弹百分比触发交易，一买一卖严格配对。适合已调参成熟的股票。</p><p className="mt-1">V2（VWAP+ATR）：用实时成交量加权平均价（VWAP）锚定方向，平均真实波幅（ATR）自动适配不同股票的参数。支持连续同方向操作。暂停后需人工恢复。</p></TooltipContent></Tooltip>
            <div className="flex gap-1 flex-1">
              {[
                { value: 'extreme_pair', label: '极值配对', desc: '观察窗+反转·∝R止盈', color: 'purple',
                  tip: '极值配对(extreme_pair)：观察窗内低点持稳N秒不创新低→反弹该比例确认买入；止盈/止损全部∝个股振幅R动态；真逐笔主动买盘否决器+单边行情ER保护(均可关)；尾盘禁开新仓。参数见 极值配对 tab。⚠️回测多数股票OOS≈负, SIM验证中。' },
                { value: 'huimaqiang', label: '回马枪HMQ01', desc: '回马枪·待定义', color: 'amber',
                  tip: '回马枪HMQ01(huimaqiang)：策略待定义。' },
              ].map(opt => (
                <Tooltip key={opt.value}>
                  <TooltipTrigger asChild>
                    <button
                      className={`flex-1 px-3 py-2 rounded-lg text-left transition-all ${
                        form.positionMode === opt.value
                          ? `bg-${opt.color}-500/20 border border-${opt.color}-500/50 ring-1 ring-${opt.color}-500/30`
                          : 'bg-muted/30 border border-transparent hover:bg-muted/50'
                      }`}
                      onClick={() => setForm(prev => ({ ...prev, positionMode: opt.value }))}>
                      <div className={`text-xs font-semibold ${form.positionMode === opt.value ? `text-${opt.color}-400` : 'text-muted-foreground'}`}>{opt.label}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs"><p>{opt.tip}</p></TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>

        {/* 参数模板 */}
        {/* [2026-06-30 模板面板对zigzag/ed隐藏] 模板是V1配对/V2专用; zigzag/ed/extreme_t0 不用模板, 参数在各自tab */}
        {templates && templates.length > 0 && form.positionMode !== 'zigzag_fusion' && form.positionMode !== 'extreme_dynamic' && form.positionMode !== 'extreme_t0' && form.positionMode !== 'extreme_pair' && form.positionMode !== 'huimaqiang' && (
          <div className="p-3 rounded-lg bg-muted/20 border border-border/30 mb-1">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium">快速应用模板</span>
              {/* [2026-06-02 模板一致性彻底修复 + 方案 A 已微调] "当前"显示读 form.activeTemplate, 加 v2_/pyramid_/defensive/paused 解析 */}
              {/* "已微调" 提示: 比对 DB monitor 数值字段 vs 当前模板预设值, 不同 = 用户已基于模板调过 */}
              {(() => {
                const tplCur = String(form.activeTemplate || '');
                const tplDb = String(monitor?.activeTemplate || '');
                if (!tplCur && !tplDb) return null;
                const unsaved = tplCur !== tplDb;
                const { label, color } = templateLabel(tplCur || tplDb);
                // 微调检测: 仅对预设模板 (非 custom/defensive/paused) 比对
                const checkTpl = tplDb && tplDb !== 'custom' && tplDb !== 'defensive' && tplDb !== 'paused' ? tplDb : '';
                let isTuned = false;
                if (checkTpl) {
                  const tplData = (templates || []).find((t: any) => t.id === checkTpl);
                  const tplP = (tplData as any)?.params || {};
                  for (const [k, v] of Object.entries(tplP)) {
                    if (v == null || typeof v === 'object') continue;
                    const mv = (monitor as any)?.[k];
                    if (mv == null) continue;
                    // 数字宽松比较 (容忍 "0.5" vs 0.5 decimal string)
                    if (typeof v === 'number' || /^-?\d+(\.\d+)?$/.test(String(v))) {
                      if (Number(mv) !== Number(v)) { isTuned = true; break; }
                    } else if (String(mv) !== String(v)) {
                      isTuned = true; break;
                    }
                  }
                }
                return (
                  <Badge variant="outline" className={`ml-auto text-[9px] px-1.5 ${color}`}>
                    当前: {label}
                    {isTuned && !unsaved ? <span className="ml-1 text-muted-foreground" title="参数已基于此模板微调">✏️</span> : null}
                    {unsaved ? <span className="ml-1 text-amber-400">(未保存)</span> : null}
                  </Badge>
                );
              })()}
            </div>
            <TooltipProvider delayDuration={300}>
            <div className="grid grid-cols-3 gap-2">
              {(templates || []).filter((tpl: any) => {
                // [Bug fix 2026-04-30] 按 positionMode 三态过滤,避免 V1 配对模式下混入金字塔模板
                const tplEngine = tpl.engine;
                if (form.positionMode === 'vwap_atr') return tplEngine === 'vwap_atr';
                if (form.positionMode === 'pyramid') return tplEngine === 'pyramid';
                if (form.positionMode === 'zigzag_fusion') return false;  // [zigzag切割 2026-06-26] zigzag无模板, 隐藏全部防套用翻回paired
                return tplEngine === 'paired';
              }).map((tpl: any) => {
                const p = tpl.params || {};
                const isV2 = tpl.id?.startsWith('v2_');
                const tooltip = isV2
                  ? `${tpl.description}\n\n` +
                    `▸ 卖出区域: VWAP + ${p.v2SellZoneValue ?? '?'}×ATR\n` +
                    `▸ 买入区域: VWAP - ${p.v2BuyZoneValue ?? '?'}×ATR\n` +
                    `▸ 利润目标: ${p.v2TargetValue ?? '?'}×ATR\n` +
                    `▸ 止损: ${p.v2StopLossValue ?? '?'}×ATR\n` +
                    `▸ 确认时间: ${p.v2ConfirmSec ?? '?'}秒\n` +
                    `▸ 配对超时: ${p.v2TimeoutSec ?? '?'}秒\n` +
                    `▸ 日最大轮次: ${p.v2MaxDailyRounds ?? '?'}\n` +
                    `▸ 连续同方向: 最多${p.v2MaxConsecutive ?? '?'}次\n` +
                    `▸ 加仓步距: ${p.v2StepATR ?? '?'}×ATR\n` +
                    `▸ 日亏损上限: ¥${p.v2MaxDailyLossValue ?? '?'}\n` +
                    `▸ 入场挂单: ${p.v2EntryOrderStrategy === 'weighted_mid' ? '加权中间价' : '对手价'}\n` +
                    `▸ 最小振幅: ${p.v2MinDayRangeATR ?? '?'}×ATR`
                  : `${tpl.description}\n\n` +
                    `▸ 回调触发: ${p.s1CallbackPct ?? '?'}%\n` +
                    `▸ 反弹触发: ${p.s2BouncePct ?? '?'}%\n` +
                    `▸ 观察窗口: ${p.s1ObserveWindowSec ?? '?'}秒\n` +
                    `▸ 买回目标: ${p.s1BuybackDropPct ?? '?'}%\n` +
                    `▸ 卖出目标: ${p.s2SellRisePct ?? '?'}%\n` +
                    `▸ 止损: ${p.s1StopLossRisePct ?? '?'}%/${p.s2StopLossDropPct ?? '?'}%\n` +
                    `▸ 日最大轮次: ${p.maxDailyRounds ?? '?'}\n` +
                    `▸ 日亏损上限: ¥${p.maxDailyLossAmount ?? '?'}\n` +
                    `▸ 连续止损暂停: ${p.maxConsecutiveStopLoss ?? '?'}次`;
                return (<Tooltip key={tpl.id}>
                  <TooltipTrigger asChild>
                    <button
                      className={`p-2 rounded-lg border transition-colors text-left ${
                        form.activeTemplate === tpl.id
                          ? 'border-primary bg-primary/10 ring-1 ring-primary/50 shadow-sm shadow-primary/20'
                          : 'border-border/50 hover:border-primary/50 hover:bg-primary/5'
                      }`}
                      onClick={() => handleApplyTemplate(tpl.id)}>
                      <div className="text-xs font-medium">{tpl.name}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{tpl.description}</div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-sm text-xs whitespace-pre-line p-3">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
                );;
              })}
            </div>
            </TooltipProvider>
            {/* [2026-04-27] Restore default values button (Phase A2 - 修复 v2: 仅识别预设模板) */}
            {/* [2026-06-02 模板一致性彻底修复] 读 form.activeTemplate 而非 monitor.activeTemplate, 与 UI 状态同步 */}
            {(() => {
              // 计算当前 activeTemplate 是否为合法预设
              const tplId = String(form.activeTemplate || monitor?.activeTemplate || '');
              const VALID_BASE = ['conservative', 'balanced', 'aggressive', 'highfreq'];
              const VALID_V2 = VALID_BASE.map(b => 'v2_' + b);
              const VALID_PYR = VALID_BASE.map(b => 'pyramid_' + b);
              const isValidPreset = VALID_BASE.includes(tplId) || VALID_V2.includes(tplId) || VALID_PYR.includes(tplId);
              const isCustom = tplId === 'custom' || !tplId;

              const engineLabel = form.positionMode === 'paired' ? 'V1 配对'
                : form.positionMode === 'pyramid' ? 'V1 金字塔' : 'V2 VWAP+ATR';

              // 推荐恢复目标 (按 positionMode 选 balanced 作为安全默认)
              const fallbackTemplateId = form.positionMode === 'vwap_atr' ? 'v2_balanced'
                : form.positionMode === 'pyramid' ? 'pyramid_balanced' : 'balanced';

              if (isCustom) return null; // custom 不显示按钮

              return (
                <div className="mt-2 pt-2 border-t border-border/30 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-muted-foreground">
                    引擎: <span className="text-foreground">{engineLabel}</span>
                    {' '}/ 模板: <span className="text-foreground">{tplId}</span>
                    {!isValidPreset && (
                      <span className="ml-2 text-amber-400">
                        {tplId === 'defensive' ? '(系统自动: 护盘模式, 触发护盘条件)' :
                         tplId === 'paused' ? '(系统自动: 已暂停)' :
                         tplId === 'aggressive_global' ? '(系统自动: 激进模式)' :
                         '(自定义/非预设)'}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[10px] px-2 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                    onClick={() => {
                      const stockLabel = (monitor.stockName || '') + ' (' + (monitor.stockCode || '') + ')';
                      const targetTpl = isValidPreset ? tplId : fallbackTemplateId;
                      const targetLabel = isValidPreset ? tplId : `${fallbackTemplateId} (因 ${tplId} 非预设, 回退到稳健型)`;
                      const confirmMsg = `恢复 ${stockLabel} 的初始值?\n\n将覆盖所有 T+0 参数为出厂模板默认: ${targetLabel}\n\n此操作不可撤销。`;
                      if (window.confirm(confirmMsg)) {
                        applyTemplate.mutate({ monitorId: monitor.id, templateId: targetTpl });
                      }
                    }}
                  >
                    🔄 恢复初始值
                  </Button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Tab切换 */}
        <div className="flex gap-1 border-b border-border/50 pb-2">
          {(form.positionMode === 'vwap_atr' ? [
            { id: "v2_entry", title: "入场/出场", icon: TrendingUp, color: "text-green-400" },
            { id: "v2_risk", title: "风控/连续操作", icon: Shield, color: "text-orange-400" },
            { id: "common", title: "通用设置", icon: Settings2, color: "text-muted-foreground" },
          ] : form.positionMode === 'extreme_dynamic' ? [
            { id: "extreme", title: "极值动态止盈", icon: TrendingUp, color: "text-gain" },
            { id: "common", title: "通用设置", icon: Settings2, color: "text-muted-foreground" },
          ] : form.positionMode === 'extreme_pair' ? [
            { id: "extreme_pair", title: "极值配对", icon: TrendingUp, color: "text-gain" },
            { id: "common", title: "通用设置", icon: Settings2, color: "text-muted-foreground" },
          ] : form.positionMode === 'huimaqiang' ? [
            { id: "common", title: "通用设置", icon: Settings2, color: "text-muted-foreground" },
          ] : form.positionMode === 'zigzag_fusion' ? [
            { id: "zigzag", title: "ZigZag高抛低吸", icon: TrendingUp, color: "text-gain" },
            { id: "common", title: "通用设置", icon: Settings2, color: "text-muted-foreground" },
          ] : [
            { id: "s1", title: "方向一：追高回调", icon: TrendingUp, color: "text-gain" },
            { id: "s2", title: "方向二：探底反弹", icon: TrendingDown, color: "text-loss" },
            { id: "common", title: "通用设置", icon: Settings2, color: "text-muted-foreground" },
          ]).map(t => {
            const Icon = t.icon;
            return (
              <Button key={t.id} variant={activeTab === t.id ? "default" : "ghost"} size="sm" className="h-7 text-xs px-3"
                onClick={() => setActiveTab(t.id)}>
                <Icon className={`h-3 w-3 mr-1 ${activeTab !== t.id ? t.color : ''}`} />{t.title}
              </Button>
            );
          })}
        </div>

        {/* 方向一：追高回调（先卖后买）*/}
        {/* ===== V2 入场/出场参数 ===== */}
        {activeTab === "v2_entry" && form.positionMode === 'vwap_atr' && (
          <div className="space-y-4 py-2">
            <Tooltip><TooltipTrigger asChild><div className="text-xs font-semibold text-green-400 mb-2 flex items-center gap-1 cursor-help">VWAP 区域设定 <Info className="h-2.5 w-2.5 text-muted-foreground" /></div></TooltipTrigger><TooltipContent side="top" className="max-w-sm text-xs"><p>VWAP（Volume Weighted Average Price）= 当日累计成交额 ÷ 累计成交量，代表全市场的平均买入成本。价格高于VWAP说明偏贵（倾向卖出），低于VWAP说明偏便宜（倾向买入）。</p><p className="mt-1">ATR（Average True Range）= 股票的平均日波幅。用ATR倍数设参数，¥12的用友网络和¥1460的茅台自动适配，不需要逐只调百分比。</p><p className="mt-1">每个参数支持三种模式：ATR倍数（推荐）、百分比、固定金额。</p></TooltipContent></Tooltip>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">卖出区域 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当价格高于 VWAP + N×ATR 时进入卖出区域。VWAP是当日成交量加权平均价，代表市场平均成本。ATR是平均真实波幅，自动适配不同股票的波动特征。值越大入场越谨慎。</p></TooltipContent></Tooltip>
                <div className="flex gap-1 mt-1">
                  <select className="w-20 text-xs bg-muted/30 border border-border/50 rounded px-1 py-1" value={form.v2SellZoneMode || 'atr'} onChange={e => setForm(p => ({...p, v2SellZoneMode: e.target.value}))}>
                    <option value="atr">ATR倍数</option><option value="pct">百分比%</option><option value="amount">金额¥</option>
                  </select>
                  <input type="number" step="0.05" className="flex-1 text-xs bg-muted/30 border border-border/50 rounded px-2 py-1" value={form.v2SellZoneValue ?? ""} onChange={e => setForm(p => ({...p, v2SellZoneValue: e.target.value === "" ? null : (parseFloat(e.target.value) || 0)}))} placeholder={`默认: 0.4`} />
                </div>
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">买入区域 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当价格低于 VWAP - N×ATR 时进入买入区域。价格低于全市场平均成本一定幅度后买入，等待回归均值时卖出获利。值越大入场越谨慎。</p></TooltipContent></Tooltip>
                <div className="flex gap-1 mt-1">
                  <select className="w-20 text-xs bg-muted/30 border border-border/50 rounded px-1 py-1" value={form.v2BuyZoneMode || 'atr'} onChange={e => setForm(p => ({...p, v2BuyZoneMode: e.target.value}))}>
                    <option value="atr">ATR倍数</option><option value="pct">百分比%</option><option value="amount">金额¥</option>
                  </select>
                  <input type="number" step="0.05" className="flex-1 text-xs bg-muted/30 border border-border/50 rounded px-2 py-1" value={form.v2BuyZoneValue ?? ""} onChange={e => setForm(p => ({...p, v2BuyZoneValue: e.target.value === "" ? null : (parseFloat(e.target.value) || 0)}))} placeholder={`默认: 0.4`} />
                </div>
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">利润目标 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>配对交易的利润目标。卖出后等价格跌多少再买回，或买入后等涨多少再卖出。也会在价格回归VWAP附近时自动了结。ATR模式下自动适配不同股票。</p></TooltipContent></Tooltip>
                <div className="flex gap-1 mt-1">
                  <select className="w-20 text-xs bg-muted/30 border border-border/50 rounded px-1 py-1" value={form.v2TargetMode || 'atr'} onChange={e => setForm(p => ({...p, v2TargetMode: e.target.value}))}>
                    <option value="atr">ATR倍数</option><option value="pct">百分比%</option><option value="amount">金额¥</option>
                  </select>
                  <input type="number" step="0.05" className="flex-1 text-xs bg-muted/30 border border-border/50 rounded px-2 py-1" value={form.v2TargetValue ?? ""} onChange={e => setForm(p => ({...p, v2TargetValue: e.target.value === "" ? null : (parseFloat(e.target.value) || 0)}))} placeholder={`默认: 0.3`} />
                </div>
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">止损 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>价格继续向不利方向偏离超过此值时止损了结。触发后进入冷却期，不自动恢复（需人工确认）。ATR模式下低价股和高价股自动适配。</p></TooltipContent></Tooltip>
                <div className="flex gap-1 mt-1">
                  <select className="w-20 text-xs bg-muted/30 border border-border/50 rounded px-1 py-1" value={form.v2StopLossMode || 'atr'} onChange={e => setForm(p => ({...p, v2StopLossMode: e.target.value}))}>
                    <option value="atr">ATR倍数</option><option value="pct">百分比%</option><option value="amount">金额¥</option>
                  </select>
                  <input type="number" step="0.05" className="flex-1 text-xs bg-muted/30 border border-border/50 rounded px-2 py-1" value={form.v2StopLossValue ?? ""} onChange={e => setForm(p => ({...p, v2StopLossValue: e.target.value === "" ? null : (parseFloat(e.target.value) || 0)}))} placeholder={`默认: 0.5`} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">区域确认(秒) <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>价格进入卖出/买入区域后，需要在区域内持续停留N秒才确认交易。防止瞬间穿越造成的假信号。</p></TooltipContent></Tooltip>
                <input type="number" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2ConfirmSec ?? ""} onChange={e => setForm(p => ({...p, v2ConfirmSec: e.target.value === "" ? null : (parseInt(e.target.value) || 30)}))} placeholder={`默认: 30`} />
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">配对超时(秒) <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>首笔交易成交后，等待反向配对交易的最大时间。超时后按市价强制了结。可设置超时是否计入止损统计。</p></TooltipContent></Tooltip>
                <input type="number" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2TimeoutSec ?? ""} onChange={e => setForm(p => ({...p, v2TimeoutSec: e.target.value === "" ? null : (parseInt(e.target.value) || 300)}))} placeholder={`默认: 300`} />
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最小振幅(×ATR) <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>日内振幅低于历史ATR的N倍时不交易。避免在窄幅震荡的行情中频繁交易亏手续费。</p></TooltipContent></Tooltip>
                <input type="number" step="0.05" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2MinDayRangeATR ?? ""} onChange={e => setForm(p => ({...p, v2MinDayRangeATR: e.target.value === "" ? null : (parseFloat(e.target.value) || 0.3)}))} placeholder={`默认: 0.3`} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">入场挂单策略 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>正常入场时的挂单方式。加权中间价：按买卖盘口量加权计算，省成本但可能不成交。对手价：买入挂卖一价/卖出挂买一价，成交率高但成本略高。</p></TooltipContent></Tooltip>
                <select className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2EntryOrderStrategy || 'weighted_mid'} onChange={e => setForm(p => ({...p, v2EntryOrderStrategy: e.target.value}))}>
                  <option value="weighted_mid">加权中间价（省成本）</option>
                  <option value="counterparty">对手价（快成交）</option>
                </select>
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">出场挂单策略 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>配对交易出场时的挂单方式。止损和超时强制了结始终使用对手价+1档确保成交。</p></TooltipContent></Tooltip>
                <select className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2ExitOrderStrategy || 'counterparty'} onChange={e => setForm(p => ({...p, v2ExitOrderStrategy: e.target.value}))}>
                  <option value="counterparty">对手价</option>
                  <option value="counterparty_plus1">对手价+1档</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* V2 风控/连续操作 */}
        {activeTab === "v2_risk" && form.positionMode === 'vwap_atr' && (
          <div className="space-y-4 py-2">
            <Tooltip><TooltipTrigger asChild><div className="text-xs font-semibold text-orange-400 mb-2 flex items-center gap-1 cursor-help">连续操作 & 风控 <Info className="h-2.5 w-2.5 text-muted-foreground" /></div></TooltipTrigger><TooltipContent side="top" className="max-w-sm text-xs"><p>V2支持在趋势明确时连续同方向操作（如连续卖出3次），但有严格限制：每次需额外偏离一个步距、总敞口不超过上限、收盘前必须全部做平。</p><p className="mt-1">风控方面：暂停后必须人工恢复（不会像V1那样自动恢复继续亏损），超时可配置为计入止损统计。</p></TooltipContent></Tooltip>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">同方向最大次数 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>允许连续同方向操作的最大次数。例如趋势明确时可以连续卖出最多N次（每次需额外偏离一个步距），收盘前必须全部做平。</p></TooltipContent></Tooltip>
                <input type="number" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2MaxConsecutive ?? ""} onChange={e => setForm(p => ({...p, v2MaxConsecutive: e.target.value === "" ? null : (parseInt(e.target.value) || 3)}))} placeholder={`默认: 3`} />
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">加仓步距(×ATR) <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>连续同方向加仓时，每次新入场价格必须比上一次额外偏离N×ATR。防止在同一价位重复操作。</p></TooltipContent></Tooltip>
                <input type="number" step="0.05" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2StepATR ?? ""} onChange={e => setForm(p => ({...p, v2StepATR: e.target.value === "" ? null : (parseFloat(e.target.value) || 0.3)}))} placeholder={`默认: 0.3`} />
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最大敞口(股) <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>日内允许的最大净偏离股数。例如底仓10000股，敞口3000表示持仓可在7000-13000之间波动。设为0则自动按底仓10%计算。</p></TooltipContent></Tooltip>
                <input type="number" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2MaxExposure ?? ""} onChange={e => setForm(p => ({...p, v2MaxExposure: e.target.value === "" ? null : (parseInt(e.target.value) || 3000)}))} placeholder={`默认: 3000`} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">日最大轮次 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>每天允许的最大完整交易轮次（一买一卖或一卖一买为一轮）。达到上限后当天不再开新仓。</p></TooltipContent></Tooltip>
                <input type="number" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2MaxDailyRounds ?? ""} onChange={e => setForm(p => ({...p, v2MaxDailyRounds: e.target.value === "" ? null : (parseInt(e.target.value) || 3)}))} placeholder={`默认: 3`} />
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">日最大亏损 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当日累计亏损达到此值后暂停交易，需人工恢复才能继续（不会自动恢复）。可选金额、百分比或ATR倍数模式。</p></TooltipContent></Tooltip>
                <div className="flex gap-1 mt-1">
                  <select className="w-16 text-xs bg-muted/30 border border-border/50 rounded px-1 py-1" value={form.v2MaxDailyLossMode || 'amount'} onChange={e => setForm(p => ({...p, v2MaxDailyLossMode: e.target.value}))}>
                    <option value="amount">¥</option><option value="pct">%</option><option value="atr">ATR</option>
                  </select>
                  <input type="number" step="50" className="flex-1 text-xs bg-muted/30 border border-border/50 rounded px-2 py-1" value={form.v2MaxDailyLossValue ?? ""} onChange={e => setForm(p => ({...p, v2MaxDailyLossValue: e.target.value === "" ? null : (parseFloat(e.target.value) || 500)}))} placeholder={`默认: 500`} />
                </div>
              </div>
              <div>
                <Tooltip><TooltipTrigger asChild><label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">做平截止 <Info className="h-2.5 w-2.5" /></label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>收盘前N分钟开始强制做平所有未配对的敞口。使用对手价+1档确保成交。T+0必须当天做平。</p></TooltipContent></Tooltip>
                <input type="number" className="w-full text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 mt-1" value={form.v2FlatDeadlineMin ?? ""} onChange={e => setForm(p => ({...p, v2FlatDeadlineMin: e.target.value === "" ? null : (parseInt(e.target.value) || 20)}))} placeholder={`默认: 20`} />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={(form.v2TimeoutIsStopLoss ?? 1) === 1} onChange={e => setForm(p => ({...p, v2TimeoutIsStopLoss: e.target.checked ? 1 : 0}))} />
                超时计入止损统计
              </label>
            </div>
          </div>
        )}

                {activeTab === "s1" && (
          <div className="space-y-4">
            {form.positionMode === 'extreme_pair' && (
              /* [刀3 20260805 词典P1] takeProfit族对extreme_pair不生效(唯一消费点=配对非自包含路径), 防面板认知错位 */
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-500">
                ⚠️ 当前为极值对(extreme_pair)模式：本节 S1/S2 止盈/止损参数为 legacy，<b>对该模式不生效</b>。
                实际生效的出场参数（激活线/追踪回撤/保底锁/止损）请到「极值对」页签设置。
              </div>
            )}
            <div className="p-3 rounded-lg bg-gain/5 border border-gain/20">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-gain" />
                <span className="text-sm font-medium text-gain">Agent监控时上涨 → 先卖后买</span>
              </div>
              <div className="space-y-3 text-xs leading-relaxed text-muted-foreground">
                {/* [trail v3 5/31] 止盈方式 radio (顶部, 控制步3+超时话术) */}
                <div className="flex items-center gap-3 pb-2 mb-1 border-b border-border/30 flex-wrap">
                  <Badge className="bg-purple-500/20 text-purple-400 border-0 shrink-0">止盈方式</Badge>
                <label className={"flex items-center gap-1 " + (form.takeProfitSchedule && form.takeProfitSchedule.trim() ? "opacity-40 cursor-not-allowed" : "cursor-pointer")}>
                    <input type="radio" name="s1Mode" checked={(form.takeProfitModeS1 ?? 0) === 0}
                      disabled={!!(form.takeProfitSchedule && form.takeProfitSchedule.trim())}
                      onChange={() => setField('takeProfitModeS1', 0)} />
                    <span>固定止盈</span>
                  </label>
                  <label className={"flex items-center gap-1 " + (form.takeProfitSchedule && form.takeProfitSchedule.trim() ? "opacity-40 cursor-not-allowed" : "cursor-pointer")}>
                    <input type="radio" name="s1Mode" checked={(form.takeProfitModeS1 ?? 0) === 1}
                      disabled={!!(form.takeProfitSchedule && form.takeProfitSchedule.trim())}
                      onChange={() => setField('takeProfitModeS1', 1)} />
                    <span className="text-purple-400">动态止盈</span>
                  </label>
                  {form.takeProfitSchedule && form.takeProfitSchedule.trim() && (
                    <>
                      <span className="text-[10px] text-muted-foreground ml-2">已被 schedule 接管</span>
                      <button type="button"
                        className="text-[10px] text-blue-400 hover:text-blue-300 underline ml-1 cursor-pointer"
                        onClick={() => setField('takeProfitSchedule', '')}
                        title="清空通用设置的 schedule, radio 立即生效">
                        [清空 schedule]
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-gain/20 text-gain border-0 shrink-0 mt-0.5">步1</Badge>
                  <p>引擎追踪波段最高价，从高点回调超过
                    <InlineParam pkey="s1CallbackPct" value={form.s1CallbackPct} defaultValue={dp.s1CallbackPct || 0.5} unit="%" step={0.1} onChange={v => setField('s1CallbackPct', v)} />
                    ，该高点设为基准价。
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-gain/20 text-gain border-0 shrink-0 mt-0.5">步2</Badge>
                  <p>在
                    <InlineParam pkey="s1ObserveWindowSec" value={form.s1ObserveWindowSec} defaultValue={dp.s1ObserveWindowSec || 60} unit="秒" step={10} onChange={v => setField('s1ObserveWindowSec', v)} />
                    观察窗口结束时，未再超过基准值，且跌幅不超过
                    <InlineParam pkey="s1ConfirmDropMaxPct" value={form.s1ConfirmDropMaxPct} defaultValue={dp.s1ConfirmDropMaxPct || 1.0} unit="%" step={0.1} onChange={v => setField('s1ConfirmDropMaxPct', v)} />
                    ，则挂卖出委托。
                  </p>
                </div>
                {/* 步3 — 替换式 */}
                <div className="flex items-start gap-2">
                  <Badge className="bg-gain/20 text-gain border-0 shrink-0 mt-0.5">步3</Badge>
                  {(form.takeProfitModeS1 ?? 0) === 0 ? (
                    <p>卖出成交价成为新基准价，立即以低于基准价
                      <InlineParam pkey="limitOrderOffsetPct" value={form.limitOrderOffsetPct} defaultValue={dp.limitOrderOffsetPct || 1.5} unit="%" step={0.1} onChange={v => setField('limitOrderOffsetPct', v)} />
                      挂买入委托。
                    </p>
                  ) : (
                    <p className="text-purple-300/90">
                      卖出成交价成为新基准价，引擎追踪最低价 — 下跌
                      <InlineParam pkey="takeProfitActivationPctS1" value={form.takeProfitActivationPctS1} defaultValue={dp.takeProfitActivationPctS1 || 0.5} unit="%" step={0.1} onChange={v => setField('takeProfitActivationPctS1', v)} />
                      激活，从最低点反弹
                      <InlineParam pkey="takeProfitTrailPctS1" value={form.takeProfitTrailPctS1} defaultValue={dp.takeProfitTrailPctS1 || 0.3} unit="%" step={0.05} onChange={v => setField('takeProfitTrailPctS1', v)} />
                      立即市价买回。
                    </p>
                  )}
                </div>
                {/* 超时 — 替换式 (mode=1 用 S1 自己的强平兜底) */}
                <div className="flex items-start gap-2">
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-0 shrink-0 mt-0.5">超时</Badge>
                  {(form.takeProfitModeS1 ?? 0) === 0 ? (
                    <p>等待
                      <InlineParam pkey="limitOrderTimeoutSec" value={form.limitOrderTimeoutSec} defaultValue={dp.limitOrderTimeoutSec || 600} unit="秒" step={60} onChange={v => setField('limitOrderTimeoutSec', v)} width="w-20" />
                      内未成交，立即以当前市价买入 → <span className="text-gain font-medium">一轮完成</span>
                    </p>
                  ) : (
                    <p className="text-purple-300/90">
                      持仓超过
                      <InlineParam pkey="trailForceCloseMaxHoldSecS1" value={form.trailForceCloseMaxHoldSecS1} defaultValue={dp.trailForceCloseMaxHoldSecS1 || 600} unit="秒" step={60} onChange={v => setField('trailForceCloseMaxHoldSecS1', v)} width="w-20" />
                      未触发回撤，立即市价买回 → <span className="text-gain font-medium">一轮完成</span>
                    </p>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-red-500/20 text-red-400 border-0 shrink-0 mt-0.5">止损</Badge>
                  <p>卖出后股价反涨超过卖出价
                    <InlineParam pkey="s1StopLossRisePct" value={form.s1StopLossRisePct} defaultValue={dp.s1StopLossRisePct || 1.0} unit="%" step={0.1} onChange={v => setField('s1StopLossRisePct', v)} />
                    ，立即买入止损 → <span className="text-gain font-medium">一轮完成</span>
                  </p>
                </div>
                {/* [trail v4 5/31] mode=1 时显示 S1 技术参数 (tick 量过滤, 跟方向独立) */}
                {(form.takeProfitModeS1 ?? 0) === 1 && (
                  <div className="flex items-start gap-2 pt-2 border-t border-purple-500/20">
                    <Badge className="bg-purple-500/20 text-purple-400 border-0 shrink-0 mt-0.5">tick</Badge>
                    <p className="text-purple-300/90">
                      tick 成交量 &lt;
                      <InlineParam pkey="trailMinTickVolumeS1" value={form.trailMinTickVolumeS1} defaultValue={dp.trailMinTickVolumeS1 || 100} unit="手" step={10} onChange={v => setField('trailMinTickVolumeS1', v)} width="w-16" />
                      时不更新极值（过滤散户假尖刺）。0=禁用。
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 方向二：探底反弹（先买后卖）*/}
        {activeTab === "s2" && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-loss/5 border border-loss/20">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="h-4 w-4 text-loss" />
                <span className="text-sm font-medium text-loss">Agent监控时下跌 → 先买后卖</span>
              </div>
              <div className="space-y-3 text-xs leading-relaxed text-muted-foreground">
                {/* [trail v3 5/31] 止盈方式 radio (顶部, 控制步3+超时话术) */}
                <div className="flex items-center gap-3 pb-2 mb-1 border-b border-border/30 flex-wrap">
                  <Badge className="bg-purple-500/20 text-purple-400 border-0 shrink-0">止盈方式</Badge>
                <label className={"flex items-center gap-1 " + (form.takeProfitSchedule && form.takeProfitSchedule.trim() ? "opacity-40 cursor-not-allowed" : "cursor-pointer")}>
                    <input type="radio" name="s2Mode" checked={(form.takeProfitModeS2 ?? 0) === 0}
                      disabled={!!(form.takeProfitSchedule && form.takeProfitSchedule.trim())}
                      onChange={() => setField('takeProfitModeS2', 0)} />
                    <span>固定止盈</span>
                  </label>
                  <label className={"flex items-center gap-1 " + (form.takeProfitSchedule && form.takeProfitSchedule.trim() ? "opacity-40 cursor-not-allowed" : "cursor-pointer")}>
                    <input type="radio" name="s2Mode" checked={(form.takeProfitModeS2 ?? 0) === 1}
                      disabled={!!(form.takeProfitSchedule && form.takeProfitSchedule.trim())}
                      onChange={() => setField('takeProfitModeS2', 1)} />
                    <span className="text-purple-400">动态止盈</span>
                  </label>
                  {form.takeProfitSchedule && form.takeProfitSchedule.trim() && (
                    <>
                      <span className="text-[10px] text-muted-foreground ml-2">已被 schedule 接管</span>
                      <button type="button"
                        className="text-[10px] text-blue-400 hover:text-blue-300 underline ml-1 cursor-pointer"
                        onClick={() => setField('takeProfitSchedule', '')}
                        title="清空通用设置的 schedule, radio 立即生效">
                        [清空 schedule]
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-loss/20 text-loss border-0 shrink-0 mt-0.5">步1</Badge>
                  <p>引擎追踪波段最低价，从低点反弹超过
                    <InlineParam pkey="s2BouncePct" value={form.s2BouncePct} defaultValue={dp.s2BouncePct || 0.5} unit="%" step={0.1} onChange={v => setField('s2BouncePct', v)} />
                    ，该低点设为基准价。
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-loss/20 text-loss border-0 shrink-0 mt-0.5">步2</Badge>
                  <p>在
                    <InlineParam pkey="s2ObserveWindowSec" value={form.s2ObserveWindowSec} defaultValue={dp.s2ObserveWindowSec || 60} unit="秒" step={10} onChange={v => setField('s2ObserveWindowSec', v)} />
                    观察窗口结束时，未继续下跌，且涨幅达到
                    <InlineParam pkey="s2ConfirmRiseMinPct" value={form.s2ConfirmRiseMinPct} defaultValue={dp.s2ConfirmRiseMinPct || 1.0} unit="%" step={0.1} onChange={v => setField('s2ConfirmRiseMinPct', v)} />
                    ，则挂买入委托。
                  </p>
                </div>
                {/* 步3 — 替换式 */}
                <div className="flex items-start gap-2">
                  <Badge className="bg-loss/20 text-loss border-0 shrink-0 mt-0.5">步3</Badge>
                  {(form.takeProfitModeS2 ?? 0) === 0 ? (
                    <p>买入成交价成为新基准价，立即以高于基准价
                      <InlineParam pkey="limitOrderOffsetPct" value={form.limitOrderOffsetPct} defaultValue={dp.limitOrderOffsetPct || 1.5} unit="%" step={0.1} onChange={v => setField('limitOrderOffsetPct', v)} />
                      挂卖出委托。
                    </p>
                  ) : (
                    <p className="text-purple-300/90">
                      买入成交价成为新基准价，引擎追踪最高价 — 上涨
                      <InlineParam pkey="takeProfitActivationPctS2" value={form.takeProfitActivationPctS2} defaultValue={dp.takeProfitActivationPctS2 || 0.5} unit="%" step={0.1} onChange={v => setField('takeProfitActivationPctS2', v)} />
                      激活，从最高点回落
                      <InlineParam pkey="takeProfitTrailPctS2" value={form.takeProfitTrailPctS2} defaultValue={dp.takeProfitTrailPctS2 || 0.3} unit="%" step={0.05} onChange={v => setField('takeProfitTrailPctS2', v)} />
                      立即市价卖出。
                    </p>
                  )}
                </div>
                {/* 超时 — 替换式 (mode=1 用 S2 自己的强平兜底) */}
                <div className="flex items-start gap-2">
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-0 shrink-0 mt-0.5">超时</Badge>
                  {(form.takeProfitModeS2 ?? 0) === 0 ? (
                    <p>等待
                      <InlineParam pkey="limitOrderTimeoutSec" value={form.limitOrderTimeoutSec} defaultValue={dp.limitOrderTimeoutSec || 600} unit="秒" step={60} onChange={v => setField('limitOrderTimeoutSec', v)} width="w-20" />
                      内未成交，立即以当前市价卖出 → <span className="text-loss font-medium">一轮完成</span>
                    </p>
                  ) : (
                    <p className="text-purple-300/90">
                      持仓超过
                      <InlineParam pkey="trailForceCloseMaxHoldSecS2" value={form.trailForceCloseMaxHoldSecS2} defaultValue={dp.trailForceCloseMaxHoldSecS2 || 600} unit="秒" step={60} onChange={v => setField('trailForceCloseMaxHoldSecS2', v)} width="w-20" />
                      未触发回撤，立即市价卖出 → <span className="text-loss font-medium">一轮完成</span>
                    </p>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-red-500/20 text-red-400 border-0 shrink-0 mt-0.5">止损</Badge>
                  <p>买入后股价继续下跌，低于买入价
                    <InlineParam pkey="s2StopLossDropPct" value={form.s2StopLossDropPct} defaultValue={dp.s2StopLossDropPct || 1.0} unit="%" step={0.1} onChange={v => setField('s2StopLossDropPct', v)} />
                    ，立即卖出止损 → <span className="text-loss font-medium">一轮完成</span>
                  </p>
                </div>
                {/* [trail v4 5/31] mode=1 时显示 S2 技术参数 (tick 量过滤) */}
                {(form.takeProfitModeS2 ?? 0) === 1 && (
                  <div className="flex items-start gap-2 pt-2 border-t border-purple-500/20">
                    <Badge className="bg-purple-500/20 text-purple-400 border-0 shrink-0 mt-0.5">tick</Badge>
                    <p className="text-purple-300/90">
                      tick 成交量 &lt;
                      <InlineParam pkey="trailMinTickVolumeS2" value={form.trailMinTickVolumeS2} defaultValue={dp.trailMinTickVolumeS2 || 100} unit="手" step={10} onChange={v => setField('trailMinTickVolumeS2', v)} width="w-16" />
                      时不更新极值（过滤散户假尖刺）。0=禁用。
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 通用设置 */}
        {activeTab === "common" && (
          <TooltipProvider delayDuration={200}>
          <div className="space-y-4">
            {/* [Phase 1 trade_mode 6/1] monitor 级实盘控制 (覆盖全局接口配置) */}
            <div className="p-3 rounded-lg bg-red-500/5 border-2 border-red-500/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-red-400 text-base">⚠️</span>
                <Tooltip><TooltipTrigger asChild>
                  <span className="text-sm font-medium flex items-center gap-1 cursor-help">实盘控制 (单 monitor 覆盖) <Info className="h-2.5 w-2.5" /></span>
                </TooltipTrigger><TooltipContent side="top" className="max-w-sm text-xs">
                  <p><b>实盘控制优先级</b> (fail-closed, 20260717 起):</p>
                  <p className="mt-1">• 全局接口配置 = 模拟 → 所有 monitor 强制模拟</p>
                  <p>• 全局 = 实盘 + 此 monitor = '强制实盘' → 实盘 (还需引擎侧 Gate3 已授权)</p>
                  <p>• 其余任何配置 ('默认'/'强制模拟') → 模拟</p>
                  <p className="mt-1 text-orange-400">⚠️ 实盘必须逐监控显式选择, 默认永远模拟</p>
                </TooltipContent></Tooltip>
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-muted/30">
                  <input type="radio" name="tradeMode" value="" className="mt-0.5"
                    checked={!form.tradeMode || form.tradeMode === ''}
                    onChange={() => {
                      // [MIX-RUN 20260717] ''=模拟 (fail-closed), 旧"跟随全局→实盘"警告已随语义废除
                      setField('tradeMode', '');
                    }} />
                  <div className="flex-1">
                    <div className="text-xs font-medium">默认 (模拟)</div>
                    <div className="text-[10px] text-muted-foreground">
                      未显式配置 = 模拟 (fail-closed). 即使全局=实盘也不真单
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-muted/30">
                  <input type="radio" name="tradeMode" value="simulation" className="mt-0.5"
                    checked={form.tradeMode === 'simulation'}
                    onChange={() => {
                      // [Steven 2026-06-06] 切模拟盘需 1 次确认
                      const wasLive = form.tradeMode === 'live';
                      if (wasLive) {
                        if (!window.confirm('确认从【实盘】切回【模拟盘】?\n\n此 monitor 将停止真单, 改为不下实盘单的模拟交易.')) {
                          return;
                        }
                      }
                      setField('tradeMode', 'simulation');
                    }} />
                  <div className="flex-1">
                    <div className="text-xs font-medium text-blue-400">强制模拟 (覆盖全局)</div>
                    <div className="text-[10px] text-muted-foreground">此 monitor 永不真单, 即使全局是实盘. 用于"其他股票仅观察"场景</div>
                  </div>
                </label>
                <label className={"flex items-start gap-2 cursor-pointer p-2 rounded " + (_hasNoPosition ? "hover:bg-red-700/20 bg-red-900/10" : "hover:bg-red-500/10")}>
                  <input type="radio" name="tradeMode" value="live" className="mt-0.5"
                    checked={form.tradeMode === 'live'}
                    onChange={() => {
                      // [Steven 2026-06-06] 切实盘统一 2 次确认 (无持仓时第 1 次是额外红警告 3 次)
                      // 第 1 次: 无持仓红警告 (条件)
                      if (_hasNoPosition) {
                        if (!window.confirm(`🚨 危险: 该股票当前持仓 ${_monitorHolding} 股 (无底仓)\n\n强制实盘 + 无持仓 = 极大概率误开新仓 (T+0 策略本质是有底仓做差价).\n\n方向二触发 → 真买入开新仓 (今天卖不掉, 变 T+1)\n方向一触发 → 卖单被券商拒 (无可卖)\n\nPython 后端有 fail-closed 拦截, 但你不应该走到这一步.\n\n确认坚持要开? (强烈建议改"强制模拟")`)) {
                          return;
                        }
                      }
                      // 第 2 次: 通用强制实盘确认
                      if (!window.confirm('⚠️ 第 1/2 次确认: 切换为【实盘】\n\n此 monitor 将真金白银下单.\n继续?')) {
                        return;
                      }
                      // 第 3 次: 最终确认
                      if (!window.confirm('🔴 第 2/2 次确认: 即将切实盘\n\n确认要为此股启用实盘? 此操作会立即热切, 下一笔信号即真单.')) {
                        return;
                      }
                      setField('tradeMode', 'live');
                    }} />
                  <div className="flex-1">
                    <div className="text-xs font-medium text-red-400">强制实盘 ⚠️ (覆盖全局)</div>
                    <div className="text-[10px] text-muted-foreground">
                      唯一实盘入口: 全局=实盘 且 引擎 Gate3 已授权时此 monitor 真单; 其余监控不受影响
                      {_hasNoPosition && (
                        <span className="block text-red-400 mt-0.5">🚨 该股票当前 0 持仓, 选实盘会误开新仓 (T+0 需底仓), 强烈不建议</span>
                      )}
                    </div>
                  </div>
                </label>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 italic">💡 混跑模式: 仅要实盘的 1-2 只选"强制实盘", 其余保持默认(模拟)即可. 全局接口配置在 [接口配置页] 改</p>
              <p className="text-[10px] text-muted-foreground mt-1">当前: 全局={_globalLive ? '🔴 实盘 (smtq)' : '🟢 模拟'} · 该股持仓={_monitorHolding} 股</p>
            </div>

            {/* 仓位控制 */}
            <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">仓位控制</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">每笔股数 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>每轮 T+0 交易买入/卖出的股票数量，是下单的基本单位。<br/>配对模式：首腿和做平腿都按此量下单。<br/>金字塔模式：以此为基数按层缩放（取整到 100 股）。<br/>必须是 100 的整数倍（科创板 200 起）。建议综合个股价格、底仓量和单笔资金占用设定。</p></TooltipContent></Tooltip>
                  <Input type="number" step={100} className="h-8 text-xs" placeholder={`默认: ${dp.tradeLotSize || 1000}`}
                    value={form.tradeLotSize ?? ""} onChange={e => setField('tradeLotSize', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最大持仓 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>单只股票 T+0 期间的持仓上限，防止过度加仓。<br/><b>注：为保证配对交易（买后必有卖）顺畅，引擎实际允许临时超出 1 个「每笔股数」</b>（如上限 10000、每笔 1000，则实际最多到 11000），做平后回落。<br/>建议设为底仓量或略高，不超过总资金 20% 对应股数。</p></TooltipContent></Tooltip>
                  <Input type="number" step={1000} className="h-8 text-xs" placeholder={`默认: ${dp.maxPositionShares || 10000}`}
                    value={form.maxPositionShares ?? ""} onChange={e => setField('maxPositionShares', e.target.value === '' ? null : +e.target.value)} />
                </div>
                {!isEp && (<div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">单日最大轮次 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>仅 V1 引擎(配对/金字塔)模式使用。<br/>V2 引擎(VWAP+ATR)请去上方<b>风控/连续操作</b>页设置<b>日最大轮次</b>。<br/>每只股票每天最多执行的交易轮次（一轮=一买一卖配对）。过多轮次会增加手续费成本，建议3-8轮。</p></TooltipContent></Tooltip>
                  <Input type="number" step={1} className="h-8 text-xs" placeholder={`默认: ${dp.maxDailyRounds || 5}`}
                    value={form.maxDailyRounds ?? ""} onChange={e => setField('maxDailyRounds', e.target.value === '' ? null : +e.target.value)} />
                </div>)}
              </div>
            </div>
            {/* 安全机制 */}
            <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="h-4 w-4 text-orange-400" />
                <span className="text-sm font-medium">安全机制</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {!isEp && (<div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">尾盘保护（分钟） <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>收盘前 N 分钟停止开新仓（仅拦截开仓，已持仓的做平不受影响）。<br/>窗口 = [15:00−N分钟, 15:00)。期间若开启「尾盘快速做平」仍可触发一次急涨急跌的快速 T0。<br/>尾盘波动大、流动性下降，建议保留 10-15 分钟缓冲确保能平掉当日仓位。</p></TooltipContent></Tooltip>
                  <Input type="number" step={5} className="h-8 text-xs" placeholder={`默认: ${dp.tailProtectMinutes || 15}`}
                    value={form.tailProtectMinutes ?? ""} onChange={e => setField('tailProtectMinutes', e.target.value === '' ? null : +e.target.value)} />
                </div>)}
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">滑点保护（%） <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>下单价相对预期价的最大允许偏差%，超过则放弃本次下单（观察等待）。设为 0 关闭。<br/><b>注：当前为事前估算（决策价 vs 最新价），尚未接入真实成交回报，因此实战中较少触发；真实滑点防护将随挂单状态机升级完善。</b></p></TooltipContent></Tooltip>
                  <Input type="number" step={0.05} className="h-8 text-xs" placeholder={`默认: ${dp.slippageProtectPct || 0.3}`}
                    value={form.slippageProtectPct ?? ""} onChange={e => setField('slippageProtectPct', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help text-gain">当日盈利目标（元）<Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当日盈利目标在【盈利护盘】页面设置 (避免多入口冲突). 此处只读展示当前值. 可使用盈利护盘的"按市值自动设目标"功能批量设置。</p></TooltipContent></Tooltip>
                  <Input type="number" className="h-8 text-xs bg-muted/30 cursor-not-allowed" readOnly disabled
                    placeholder="去盈利护盘设置"
                    value={form.dailyProfitTarget ?? ""} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">单日最大亏损（元） <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当日该股 T+0 累计净亏损达到此金额（元）后，引擎立即暂停该股交易，需人工介入恢复——最后一道安全防线。<br/>每次手工恢复会追加一轮额度（boost），可在熔断后继续但有次数上限。<br/>设为 0 关闭（不建议）。建议按可承受日亏设定。</p></TooltipContent></Tooltip>
                  {/* [P2-63 2026-05-23] min=0 防止前端传负数（服务端 zod 已拦截，这是体验优化） */}
                  <Input type="number" min={0} step={500} className="h-8 text-xs" placeholder={`默认: ${dp.maxDailyLossAmount || 10000}`}
                    value={form.maxDailyLossAmount ?? ""} onChange={e => setField('maxDailyLossAmount', e.target.value === '' ? null : Math.max(0, +e.target.value))} />
                </div>
                {/* [Steven 2026-06-04] T+1 持仓过夜功能已完整移除 (3 模型评审一致建议) */}
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">止损冷却期（秒） <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>单次止损后暂停交易的冷却时间，防止剧烈波动中反复止损。<br/>冷却期内引擎只观察不开新仓；<b>冷却结束后仅解除冷却，不重置「连续止损」计数</b>（防止用冷却绕过连续止损暂停机制）。<br/>设为 0 关闭冷却。建议 120-300 秒。</p></TooltipContent></Tooltip>
                  <Input type="number" step={30} className="h-8 text-xs" placeholder={`默认: ${dp.stopLossCooldownSeconds || 300}`}
                    value={form.stopLossCooldownSeconds ?? ""} onChange={e => setField('stopLossCooldownSeconds', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">连续止损暂停次数 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>连续N次止损后暂停该股交易。连续止损说明行情不适合当前策略。<br/><b>注：触发后需在Web端手工恢复（不会自动恢复），以防在不利行情中反复亏损。</b></p></TooltipContent></Tooltip>
                  <Input type="number" step={1} min={1} className="h-8 text-xs" placeholder={`默认: ${dp.maxConsecutiveStopLoss || 2}`}
                    value={form.maxConsecutiveStopLoss ?? ""} onChange={e => setField('maxConsecutiveStopLoss', e.target.value === '' ? null : +e.target.value)} />
                </div>
                {/* === 开盘暂停（新增） === */}
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">开盘暂停启用 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>是否启用开盘后暂停开仓。1=启用（推荐），0=禁用。<br/>开盘集合竞价后的价格发现期波动剧烈、方向未明，暂停可避开开盘假信号。暂停时长由下方「开盘暂停分钟数」控制。</p></TooltipContent></Tooltip>
                  <Input type="number" step={1} min={0} max={1} className="h-8 text-xs" placeholder={`默认: ${dp.openPauseEnabled ?? 1}`}
                    value={form.openPauseEnabled ?? ""} onChange={e => setField('openPauseEnabled', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">开盘暂停分钟数 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>开盘后暂停开新仓的分钟数，窗口 = [9:30, 9:30+N)。默认 5 分钟（9:30-9:35）。0=不暂停。<br/>需「开盘暂停启用」=1 才生效。开盘价格发现期方向未明、假信号多，跳过可提升胜率。</p></TooltipContent></Tooltip>
                  <Input type="number" step={1} min={0} max={30} className="h-8 text-xs" placeholder={`默认: ${dp.openPauseMinutes ?? 5}`}
                    value={form.openPauseMinutes ?? ""} onChange={e => setField('openPauseMinutes', e.target.value === '' ? null : +e.target.value)} />
                </div>
                {/* === 信号质量三重过滤（新增） === */}
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最小成交量倍数 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>信号质量三重过滤之一。信号确认时当前 tick 成交量必须 ≥ 近 5 个 tick 均量 × N 倍，否则拒绝该信号。<br/>如 1.2 = 要求放量 20%，过滤无量虚假突破。引擎每 tick 独立维护成交量序列。0=不过滤。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.1} min={0} className="h-8 text-xs" placeholder={`默认: ${dp.signalMinVolumeRatio ?? 1.2}`}
                    value={form.signalMinVolumeRatio ?? ""} onChange={e => setField('signalMinVolumeRatio', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最大买卖价差（%） <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>信号质量三重过滤之二。买一/卖一价差% = (卖1−买1)/买1×100 必须 ≤ 此值才确认信号。<br/>价差大说明流动性差或盘口失衡，T+0 进出场滑点风险高。0=不过滤。建议 0.3-0.5%。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.05} min={0} className="h-8 text-xs" placeholder={`默认: ${dp.signalMaxSpreadPct ?? 0.5}`}
                    value={form.signalMaxSpreadPct ?? ""} onChange={e => setField('signalMaxSpreadPct', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最小净利率（%） <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>信号质量三重过滤之三，最关键的防亏过滤。预期净利率 = 毛利率 − 双边费率，必须 ≥ 此值才确认信号。<br/><b>费率按你的实际佣金配置动态计算</b>（非硬编码），双边约 0.08%。用%而非金额，对不同价位股票通用。<br/>设太低会做"赚的不够付手续费"的单。0=不过滤，建议 ≥0.2%。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.05} min={0} className="h-8 text-xs" placeholder={`默认: ${dp.signalMinNetProfitPct ?? 0.15}`}
                    value={form.signalMinNetProfitPct ?? form.signalMinExpectedProfit ?? ""} onChange={e => setField('signalMinNetProfitPct', e.target.value === '' ? null : +e.target.value)} />
                </div>
              </div>
            </div>
            {/* 挂单策略 */}
            <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
              <div className="flex items-center gap-2 mb-3">
                <Crosshair className="h-4 w-4 text-cyan-400" />
                <span className="text-sm font-medium">挂单策略</span>
                <Tooltip><TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>决定委托单的挂单价格。止损交易时自动升级为「对手价+1档」以确保成交。基于L1五档行情（腾讯财经/XtData）。</p></TooltipContent></Tooltip>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild><Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">默认挂单策略 <Info className="h-2.5 w-2.5" /></Label></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>入场首单的挂单方式(仅实盘生效)。<br/><b>对手价</b>：买挂卖一价/卖挂买一价，基本秒成(推荐)。<br/><b>对手价+1档</b>：再进一档，更激进保成交。<br/><b>盘口加权中间价/智能追单</b>：省成本但可能不成交，<b>当前暂按对手价执行</b>，完整撤补单逻辑开发中。</p></TooltipContent></Tooltip>
                  <Select value={form.orderStrategy ?? dp.orderStrategy ?? 'opponent'} onValueChange={(v) => setField('orderStrategy', v as any)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="opponent">对手价</SelectItem>
                      <SelectItem value="opponent_plus1">对手价+1档</SelectItem>
                      <SelectItem value="weighted_mid">盘口加权中间价</SelectItem>
                      <SelectItem value="smart_chase">智能追单</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">追单最大档数 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>智能追单/加权中间价模式下，限价单未成交时最多向对手价方向追加的价格档数。<br/><b>注：追单状态机开发中，当前入场单按对手价直接成交，此参数暂未激活。</b></p></TooltipContent></Tooltip>
                  <Input type="number" step={1} min={1} max={5} className="h-8 text-xs" placeholder={`默认: ${dp.smartChaseMaxTicks || 2}`}
                    value={form.smartChaseMaxTicks ?? ""} onChange={e => setField('smartChaseMaxTicks', e.target.value === '' ? null : +e.target.value)} />
                </div>
              </div>
            </div>
            {/* [6/17] 单边日防御 */}
            <div className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/20 mb-2">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-rose-400" />
                <span className="text-sm font-medium">单边日防御 (禁单边趋势做T)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {!isEp && (<div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">单边日禁做T <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>用方向效率=|现价−开盘|/(日高−日低)识别单边趋势日并拒绝双向新开仓(做平不受影响)。单边涨8%效率约100%拦, 跌3涨5效率约62%放行。动态实时, 单边段拦/出现回转放行。默认开。</p></TooltipContent></Tooltip>
                  <Select value={String(form.singleSideBlockEnabled ?? dp.singleSideBlockEnabled ?? 1)} onValueChange={(v) => setField('singleSideBlockEnabled', +v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">关闭</SelectItem>
                      <SelectItem value="1">开启</SelectItem>
                    </SelectContent>
                  </Select>
                </div>)}
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">方向效率阈值(%) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>方向效率≥本值%判为单边日→拒做T, 低于放行。越低越严。默认65。</p></TooltipContent></Tooltip>
                  <Input type="number" step={5} min={0} max={100} className="h-8 text-xs" placeholder="默认: 65"
                    value={form.singleSideEffThreshold ?? ""} onChange={e => setField('singleSideEffThreshold', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">单边闸最小振幅(%) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>日振幅≥本值%时单边闸才生效, 小振幅日不拦。默认2。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.5} min={0} max={50} className="h-8 text-xs" placeholder="默认: 2"
                    value={form.singleSideMinRangePct ?? ""} onChange={e => setField('singleSideMinRangePct', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">极值企稳·破极值缓冲(%) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>极值企稳判定"创新极值/突破"的容忍缓冲%, 反向超此才算破极值并重锚。默认0.05。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.01} min={0} max={5} className="h-8 text-xs" placeholder="默认: 0.05"
                    value={form.extremeHoldBreakBufferPct ?? ""} onChange={e => setField('extremeHoldBreakBufferPct', e.target.value === '' ? null : +e.target.value)} />
                </div>
              </div>
            </div>

            {/* 波动率预筛选 */}
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium">波动率预筛选</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {!isEp && (<div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">波动率筛选 <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>开启后在检测阶段筛选：日内振幅过小/过大、成交额不足的股票不开新仓（已持仓的做平不受影响）。默认关闭，避免误阻塞交易。</p></TooltipContent></Tooltip>
                  <Select value={String(form.volatilityFilter ?? dp.volatilityFilter ?? 0)} onValueChange={(v) => setField('volatilityFilter', +v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">关闭</SelectItem>
                      <SelectItem value="1">开启</SelectItem>
                    </SelectContent>
                  </Select>
                </div>)}
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最小振幅(%) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>日内振幅 =(最高−最低)/开盘价×100。低于此值说明波动太小、T+0 难赚到价差，不开仓。默认 0.2%。需「波动率筛选」开启。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.5} className="h-8 text-xs" placeholder="默认: 1.0"
                    value={form.minDayRangePct ?? ""} onChange={e => setField('minDayRangePct', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最大振幅(%) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>日内振幅超过此值说明波动过于剧烈、风险太高，不开仓。默认 8.0%。需「波动率筛选」开启。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.5} className="h-8 text-xs" placeholder="默认: 7.0"
                    value={form.maxDayRangePct ?? ""} onChange={e => setField('maxDayRangePct', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">最小成交额(万) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs">
                    <div className="space-y-1">
                      <p className="font-medium">最小成交额阈值</p>
                      <p>当日截至当前时刻的累计成交额低于此值时，引擎判定该股票流动性不足，不会开新仓。</p>
                      <p>该阈值会按交易时间比例动态调整：早盘阈值较低，尾盘阈值接近设定值。</p>
                      <p className="text-muted-foreground">单位：万元 | 默认：1000万 | 设为0则关闭此检查</p>
                    </div>
                  </TooltipContent></Tooltip>
                  <Input type="number" step={500} min={0} className="h-8 text-xs" placeholder="默认: 1000"
                    value={form.minTurnoverVal != null ? form.minTurnoverVal / 10000 : ""} onChange={e => setField('minTurnoverVal', e.target.value === '' ? null : +e.target.value * 10000)} />
                </div>
              </div>
            </div>

            {/* 限价委托参数 */}
            <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Timer className="h-4 w-4 text-cyan-400" />
                <span className="text-sm font-medium">限价委托参数</span>
              </div>
              {!isEp && (<div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">限价偏移(%) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>配对做平腿的限价单偏移%，即一轮 T+0 的目标毛利空间。卖出后挂低于成交价 X% 买回、买入后挂高于成交价 X% 卖出。需覆盖双边费用(约0.08%)才有净利。默认 0.25%。</p></TooltipContent></Tooltip>
                  <Input type="number" step={0.01} className="h-8 text-xs" placeholder="默认: 0.05"
                    value={form.limitOrderOffsetPct ?? ""} onChange={e => setField('limitOrderOffsetPct', e.target.value === '' ? null : +e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Tooltip><TooltipTrigger asChild>
                    <Label className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help">超时时间(秒) <Info className="h-2.5 w-2.5" /></Label>
                  </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>做平限价单等待成交的最大秒数，超时后转市价强制做平（防隔夜）。太短易频繁市价吃滑点，太长增加持仓风险。默认 90 秒。</p></TooltipContent></Tooltip>
                  <Input type="number" step={5} className="h-8 text-xs" placeholder="默认: 30"
                    value={form.limitOrderTimeoutSec ?? ""} onChange={e => setField('limitOrderTimeoutSec', e.target.value === '' ? null : +e.target.value)} />
                </div>
              </div>)}
            </div>

            {/* 智能风格自适应 - 已废弃 (Claude 2026-05-14 P0 删除) */}
            {/* 原因: emtStrategyGenerator.ts 没把 smartStyle* 字段传给 Python 引擎,
                     UI 显示能配置但实际未启用. 风格切换实际由 autoOptimizer 统一管理
                     (conservative/balanced/aggressive 模板). */}

            {/* 尾盘快速做平 - 已废弃 (Claude 2026-05-14 P0 删除) */}
            {/* 原因: emtStrategyGenerator.ts 没把 tailQuick* 字段传给 Python 引擎,
                     memory#10 早就标注 'tailQuick* 参数存在 DB schema 中但引擎从未读取'.
                     尾盘做平由 tailProtectMinutes (默认 15min) 统一控制. */}

          </div>
          </TooltipProvider>
        )}

        {/* [2026-06-25 stage A1] ZigZag高抛低吸参数 (zigzag_fusion 专属) — 只在 zigzag 模式+zigzag tab 显示 */}
        {activeTab === "zigzag" && form.positionMode === 'zigzag_fusion' && (
          <div className="space-y-3 py-2">
            <p className="text-[11px] text-muted-foreground">因果ZigZag拐点高抛低吸参数 (per-stock, 空=全局默认)</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {(([
                ["zzMult","ZZ频率θ"],["zzTrailMult","ZZ动态止盈"],["zzSkipExtremePos","ZZ跳极端位"],
                ["zzPriorRangePct","ZZ R振幅%"],["zzSlMult","ZZ止损"],["zzGateMult","ZZ振幅闸"],
                ["zzCth","ZZ方向确认"],["zzMaxRounds","ZZ最大轮"],["zzTailNoOpenMin","ZZ尾盘禁开min"],
              ]) as [string,string][]).map(([k,label]) => (
                <label key={k} className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-muted-foreground">{label}</span>
                  <input type="number" step="any" className="border rounded px-1 py-0.5 bg-background"
                    value={(form as any)[k] ?? ""}
                    onChange={e => setField(k, e.target.value === "" ? null : +e.target.value)} />
                </label>
              ))}
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-muted-foreground">ZZ方向</span>
                <select className="border rounded px-1 py-0.5 bg-background"
                  value={(form as any).zzDirection ?? "both"}
                  onChange={e => setForm(prev => ({...prev, zzDirection: e.target.value}))}>
                  <option value="both">both</option><option value="long">long</option><option value="short">short</option>
                </select>
              </label>
            </div>
          </div>
        )}
        {/* [2026-06-28] 极值动态止盈参数 (extreme_dynamic 专属) — 只在 ed 模式+extreme tab 显示 */}
        {activeTab === "extreme" && form.positionMode === 'extreme_dynamic' && (
          <div className="space-y-3 py-2">
            <p className="text-[11px] text-muted-foreground">极值动态止盈参数 (per-stock, 空=全局默认; 回测用友6FS信号日 +9208)</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {(([
                ["edMom","急涨跌窗(秒)"],["edRev","反转窗(秒)"],["edSp","急涨跌阈值%"],
                ["edNear","贴极值比例"],["edAct","动态止盈激活"],["edTrail","动态止盈回撤"],
                ["edStop","逆向止损"],["edCd","开仓冷却(秒)"],["edTailNoOpenMin","尾盘禁开min"],
              ]) as [string,string][]).map(([k,label]) => (
                <label key={k} className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-muted-foreground">{label}</span>
                  <input type="number" step="any" className="border rounded px-1 py-0.5 bg-background"
                    value={(form as any)[k] ?? ""}
                    onChange={e => setField(k, e.target.value === "" ? null : +e.target.value)} />
                </label>
              ))}
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-muted-foreground">ed方向</span>
                <select className="border rounded px-1 py-0.5 bg-background"
                  value={(form as any).edDirection ?? "both"}
                  onChange={e => setForm(prev => ({...prev, edDirection: e.target.value}))}>
                  <option value="both">both</option><option value="long">long</option><option value="short">short</option>
                </select>
              </label>
            </div>
          </div>
        )}
        {/* [2026-06-30] 极值配对参数 (extreme_pair 专属) — 全21参数真实透传引擎(DB列+getEffectiveParams+from_web_params+引擎getattr实读) */}
        {activeTab === "extreme_pair" && form.positionMode === 'extreme_pair' && (
          <div className="space-y-3 py-2">
            <p className="text-[11px] text-muted-foreground">极值配对参数 (per-stock, 空=全局默认; 观察窗持稳→反转入场, 止盈/止损全∝个股振幅R)</p>
            <div className="space-y-2.5 text-sm leading-relaxed">
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">① 进场</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p><b>入场模式</b>二选一，决定何时在极值处开仓（低吸/高抛对称）。</p><p className="mt-1"><b>dwell（观察持稳+反转）</b>：观察窗(秒)=回看多久找日内极值；持稳比例=极值需在窗内保持的时长占比(0.5=半窗内不创新高/低才算"站稳")；反转入场%=价格从极值反转该幅度确认进场(0.5%)。</p><p className="mt-1"><b>feishu（动量衰竭+贴极值）</b>：动量窗(秒)=看多久之前的价算涨跌幅；急跌门槛%=涨跌幅超此才触发；贴极值阈值=当前价距日内极值≤该值×当日振幅才算"贴住"(越小越严)；反转窗(秒)=需在此秒内价格已掉头。</p></TooltipContent></Tooltip><span className="mx-1">模式</span>
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={(form as any).epEntryMode ?? "dwell"}
                  onChange={e => setForm(prev => ({ ...prev, epEntryMode: e.target.value }))}>
                  <option value="dwell">观察持稳+反转</option><option value="feishu">飞书·动量衰竭+贴极值</option>
                </select>
                {((form as any).epEntryMode ?? "dwell") === "feishu" ? (
                  <span>：价格在<InlineParam pkey="epMomSec" value={(form as any).epMomSec ?? null} defaultValue={dp.epMomSec || 75} unit="秒" step={5} onChange={v => setField('epMomSec', v)} />内急涨/急跌 ≥<InlineParam pkey="epSp" value={(form as any).epSp ?? null} defaultValue={dp.epSp || 0.8} unit="%" step={0.1} onChange={v => setField('epSp', v)} />，且贴近日内极值（贴合阈值<InlineParam pkey="epNear" value={(form as any).epNear ?? null} defaultValue={dp.epNear || 0.35} unit="" step={0.05} onChange={v => setField('epNear', v)} />，越小越贴），并在<InlineParam pkey="epRevSec" value={(form as any).epRevSec ?? null} defaultValue={dp.epRevSec || 9} unit="秒" step={1} onChange={v => setField('epRevSec', v)} />内已反转时开仓（低吸/高抛对称）。</span>
                ) : (
                  <span>：在<InlineParam pkey="epObsSec" value={(form as any).epObsSec ?? null} defaultValue={dp.epObsSec || 60} unit="秒" step={10} onChange={v => setField('epObsSec', v)} />观察窗内探测极值，极值持稳<InlineParam pkey="epHoldFrac" value={(form as any).epHoldFrac ?? null} defaultValue={dp.epHoldFrac || 0.5} unit="" step={0.05} onChange={v => setField('epHoldFrac', v)} />（占窗比例，0.5=半窗）不创新高/低，价格自极值反转<PctParam pkey="epEntryRev" value={(form as any).epEntryRev ?? null} defaultValue={dp.epEntryRev || 0.005} step={0.1} onChange={v => setField('epEntryRev', v)} />时开仓（低吸/高抛对称）。</span>
                )}
              </div>
              {(() => {
                const f: any = form;
                const nv = (v: any, d: number) => (v === null || v === undefined || v === '' ? d : Number(v));
                const exitFixed = nv(f.epActMult, dp.epActMult ?? 0) === 0 && nv(f.epTrailMult, dp.epTrailMult ?? 0) === 0 && nv(f.epSlMult, dp.epSlMult ?? 0) === 0;
                return (<>
                  <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                    <span className="font-semibold text-foreground">② 动态止盈</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-sm text-xs"><p><b>激活</b>：开仓时冻结 act=max(激活下限, 激活系数×R)。每 tick 算 fav=(现价−入场价)÷入场价，fav≥act 即“武装”启用动态止盈。</p><p className="mt-1"><b>回撤出场</b>：武装后追踪极值(多=最高价/空=最低价)，价格从极值回撤≥回撤阈值即平仓。</p><p className="mt-1"><b>止损</b>：独立规则，开仓即生效，无需先武装。</p><p className="mt-1 text-amber-400">⚠️ 铁律：激活须 &gt; 回撤，否则刚武装就反转会亏——出场=极值×(1−回撤)，而极值最低仅=入场×(1+激活)。</p></TooltipContent></Tooltip>：阈值方式
                    <select className="mx-1 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                      value={exitFixed ? 'fixed' : 'adaptive'}
                      onChange={e => e.target.value === 'fixed'
                        ? setForm(prev => ({ ...prev, epActMult: 0, epTrailMult: 0, epSlMult: 0 }))
                        : setForm(prev => ({ ...prev, epActMult: null, epTrailMult: null, epSlMult: null }))}>
                      <option value="fixed">固定比例（直观）</option><option value="adaptive">随个股振幅自适应（×R）</option>
                    </select>
                    {exitFixed ? (
                      <div className="mt-1">开仓后，股价向<b className="text-foreground">有利方向</b>的高低差达到<PctParam pkey="epActFloor" value={(form as any).epActFloor ?? null} defaultValue={dp.epActFloor ?? 0.015} step={0.1} onChange={v => setField('epActFloor', v)} />时<b className="text-foreground">启动动态止盈</b>；启动后引擎持续追踪最有利的价格极值（另一端极值），一旦股价从该极值<b className="text-foreground">回撤</b><PctParam pkey="epTrailFloor" value={(form as any).epTrailFloor ?? null} defaultValue={dp.epTrailFloor ?? 0.010} step={0.1} onChange={v => setField('epTrailFloor', v)} />，立即平仓落袋。</div>
                    ) : (
                      <div className="mt-1">同上逻辑，阈值随该股波动大小自动缩放（阈值=系数×R，R 在下方④风控处设置）：<b className="text-foreground">启动</b>=高低差达<InlineParam pkey="epActMult" value={(form as any).epActMult ?? null} defaultValue={dp.epActMult ?? 0} unit="×R" step={0.05} onChange={v => setField('epActMult', v)} />（保底<PctParam pkey="epActFloor" value={(form as any).epActFloor ?? null} defaultValue={dp.epActFloor ?? 0.015} step={0.1} onChange={v => setField('epActFloor', v)} />）；<b className="text-foreground">出场</b>=自极值回撤<InlineParam pkey="epTrailMult" value={(form as any).epTrailMult ?? null} defaultValue={dp.epTrailMult ?? 0} unit="×R" step={0.05} onChange={v => setField('epTrailMult', v)} />（保底<PctParam pkey="epTrailFloor" value={(form as any).epTrailFloor ?? null} defaultValue={dp.epTrailFloor ?? 0.010} step={0.1} onChange={v => setField('epTrailFloor', v)} />）。</div>
                    )}
                    {/* [刀3 20260805 词典P1] 利润保底锁=当前全池真实出场主控(0804三笔lock_tp实锤), 补面板入口(两种模式通用) */}
                    <div className="mt-1">🔒 <b className="text-foreground">利润保底锁</b>：武装后至少锁住峰值浮盈的<InlineParam pkey="epLockFrac" value={(form as any).epLockFrac ?? null} defaultValue={dp.epLockFrac ?? 0.5} unit="×" step={0.05} onChange={v => setField('epLockFrac', v)} />（0=关，0.5=回吐到只剩一半即平 lock_tp；与追踪回撤两者先触发者出场）。</div>
                  </div>
                  <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                    <span className="font-semibold text-foreground">③ 止损（独立规则·持仓期间随时生效）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p><b>止损%</b>：股价从入场价向<b>不利</b>方向达到该幅度，立即市价止损。</p><p className="mt-1">与动态止盈<b>相互独立</b>：开仓即生效、无需先"武装"，谁先触发谁执行。</p><p className="mt-1"><b>固定模式</b>=直接就是该%；<b>自适应</b>=max(该%, 止损系数×R)，随个股波动自动缩放。</p></TooltipContent></Tooltip>：
                    {exitFixed ? (
                      <span>若股价从入场价向<b className="text-foreground">不利方向</b>达到<PctParam pkey="epSlFloor" value={(form as any).epSlFloor ?? null} defaultValue={dp.epSlFloor ?? 0.020} step={0.1} onChange={v => setField('epSlFloor', v)} />，立即<b className="text-foreground">市价止损</b>，不等动态止盈。</span>
                    ) : (
                      <span>不利方向达<InlineParam pkey="epSlMult" value={(form as any).epSlMult ?? null} defaultValue={dp.epSlMult ?? 0} unit="×R" step={0.05} onChange={v => setField('epSlMult', v)} />（保底<PctParam pkey="epSlFloor" value={(form as any).epSlFloor ?? null} defaultValue={dp.epSlFloor ?? 0.020} step={0.1} onChange={v => setField('epSlFloor', v)} />）即<b className="text-foreground">市价止损</b>。</span>
                    )}
                  </div>
                </>);
              })()}
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">④ 风控</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p><b>振幅闸(×R)</b>：当日实际振幅需 ≥ 该系数×R 才开工，过滤掉没波段可做的窄幅日。</p><p className="mt-1"><b>R</b>=个股常态日振幅基准(%)，Web 人工设或点📊自动算(近30日均振幅)；<b>非盘中实时数据、开仓时冻结</b>。<b>R下限</b>=R 的地板值，防 R 取到过小。</p><p className="mt-1"><b>冷却(秒)</b>=两次开仓最小间隔，防频繁进出。<b>开盘/尾盘禁开(分)</b>=开盘后 / 收盘前这些分钟禁开新仓(防集合竞价余波、尾盘来回小亏)。</p><p className="mt-1"><b>撮合滑点</b>=成交价相对对手价的滑点假设(0.0005=0.05%)。<b>方向</b>=both双向 / long仅低吸 / short仅高抛。</p></TooltipContent></Tooltip>：当日振幅需 ≥<InlineParam pkey="epGateMult" value={(form as any).epGateMult ?? null} defaultValue={dp.epGateMult || 0.3} unit="×R" step={0.05} onChange={v => setField('epGateMult', v)} />才开工（R=该股常态日振幅基准<InlineParam pkey="epPriorRangePct" value={(form as any).epPriorRangePct ?? null} defaultValue={dp.epPriorRangePct || 2.0} unit="%" step={0.5} onChange={v => setField('epPriorRangePct', v)} /><button type="button" className="mx-1 h-6 text-[10px] px-1.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 disabled:opacity-50" disabled={autoCalcR.isPending} onClick={() => autoCalcR.mutate({ stockCode: monitor.stockCode })}>{autoCalcR.isPending ? '\u7b97\u2026' : '\ud83d\udcca \u81ea\u52a8\u7b97'}</button>、下限<InlineParam pkey="epRFloor" value={(form as any).epRFloor ?? null} defaultValue={dp.epRFloor || 2.0} unit="%" step={0.5} onChange={v => setField('epRFloor', v)} />；<b className="text-foreground">R 为 Web 人工设置·非盘中实时数据·开仓时冻结</b>）；每次开仓后冷却<InlineParam pkey="epCd" value={(form as any).epCd ?? null} defaultValue={dp.epCd || 600} unit="秒" step={60} width="w-20" onChange={v => setField('epCd', v)} />；开盘<InlineParam pkey="epOpenNoOpenMin" value={(form as any).epOpenNoOpenMin ?? null} defaultValue={dp.epOpenNoOpenMin || 3} unit="分" step={1} onChange={v => setField('epOpenNoOpenMin', v)} />、尾盘<InlineParam pkey="epTailNoOpenMin" value={(form as any).epTailNoOpenMin ?? null} defaultValue={dp.epTailNoOpenMin || 30} unit="分" step={1} onChange={v => setField('epTailNoOpenMin', v)} />内禁开新仓；撮合滑点<InlineParam pkey="epSlip" value={(form as any).epSlip ?? null} defaultValue={dp.epSlip || 0.0005} unit="" step={0.0001} width="w-20" onChange={v => setField('epSlip', v)} />（0.0005=0.05%）；方向
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={(form as any).epDirection ?? "both"}
                  onChange={e => setForm(prev => ({ ...prev, epDirection: e.target.value }))}>
                  <option value="both">双向</option><option value="long">仅低吸</option><option value="short">仅高抛</option>
                </select>。
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑤ 过滤</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p>两个可开关的入场过滤器，都是为了不在坏时机进场。</p><p className="mt-1"><b>真逐笔否决器</b>：用真实逐笔主动买卖盘，当"主动买占比 &lt; 阈值"时否决低吸(防接飞刀，高抛对称)；关=不启用。</p><p className="mt-1"><b>单边ER闸</b>：Kaufman 效率比 = |净位移|÷总行程 ∈[0,1]，越接近1越单边。效率比 ≥ 阈值判为单边趋势，禁止逆势接刀；窗口(秒)=算ER的回看时长；关=不启用。</p></TooltipContent></Tooltip>：真逐笔否决器
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epVetoEnable ?? 1)}
                  onChange={e => setForm(prev => ({ ...prev, epVetoEnable: +e.target.value }))}>
                  <option value="1">开</option><option value="0">关</option>
                </select>
                （主动买占比 &lt;<InlineParam pkey="epVetoMin" value={(form as any).epVetoMin ?? null} defaultValue={dp.epVetoMin || 0.45} unit="" step={0.05} onChange={v => setField('epVetoMin', v)} />时否决低吸）。{/* [20260717下线] 单边ER闸(epEr*)历史从未咬合(tick窗ER分布99%<0.2), 面板摘除; 现役方向效率=趋势闸⑨+单边日守卫 */}
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑨ 趋势闸（滚动窗单边禁逆势·双窗过线版）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p>近 W 分钟价格效率比 |首尾差|÷路径和 ≥阈值，且价格在 VWAP 单边侧 → 禁逆势腿<b>新开仓</b>。窗口未形成（约开盘30分钟）不判，与 W_OPEN 开盘窗无冲突。与 ⑦S4（日级粗刀，回测否决保持影子）互补：这把滚动窗刀通过了 IS+OOS 双窗验证。</p></TooltipContent></Tooltip>：模式
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epTrendGateEnable ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epTrendGateEnable: parseFloat(e.target.value) }))}>
                  <option value="0">关</option><option value="0.5">影子(只记录)</option><option value="1">生效</option>
                </select>
                ，滚动窗<InlineParam pkey="epTrendGateWin" value={(form as any).epTrendGateWin ?? null} defaultValue={(dp as any).epTrendGateWin ?? 120} unit="分" step={30} onChange={v => setField('epTrendGateWin', v)} />、效率比≥<InlineParam pkey="epTrendGateThresh" value={(form as any).epTrendGateThresh ?? null} defaultValue={(dp as any).epTrendGateThresh ?? 0.5} unit="" step={0.05} onChange={v => setField('epTrendGateThresh', v)} />。
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑩ 反手FLIP与双槽SLOT2（赢家加注·冒险版20260716）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p><b>反手FLIP</b>：持腿期间出现反向C轨级破锚+速度+盘口三票事件→平旧腿，90秒内价格仍在破锚侧且偏离触发价≤保护线→开反向腿。</p><p className="mt-1"><b>双槽SLOT2</b>：仅赢家加注—一腿已武装且浮盈达标才开同向二腿（缩尺寸+紧止损），同时一腿止损抬至保本；禁输家摊平。</p><p className="mt-1">两机制均热参一键撤回；熔断：单股flip日亏≤-1500自动关、二腿日亏≤-2000自动关（cron 5分钟）。</p></TooltipContent></Tooltip>：反手
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epFlipEnable ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epFlipEnable: +e.target.value }))}>
                  <option value="0">关</option><option value="1">开</option>
                </select>
                ，最小持腿<InlineParam pkey="epFlipMinHoldSec" value={(form as any).epFlipMinHoldSec ?? null} defaultValue={(dp as any).epFlipMinHoldSec ?? 120} unit="秒" step={30} onChange={v => setField('epFlipMinHoldSec', v)} />、每日≤<InlineParam pkey="epFlipMaxDay" value={(form as any).epFlipMaxDay ?? null} defaultValue={(dp as any).epFlipMaxDay ?? 2} unit="次" step={1} onChange={v => setField('epFlipMaxDay', v)} />、冷静<InlineParam pkey="epFlipCdSec" value={(form as any).epFlipCdSec ?? null} defaultValue={(dp as any).epFlipCdSec ?? 1200} unit="秒" step={300} width="w-20" onChange={v => setField('epFlipCdSec', v)} />、价格保护<InlineParam pkey="epFlipPxProtFrac" value={(form as any).epFlipPxProtFrac ?? null} defaultValue={(dp as any).epFlipPxProtFrac ?? 0.004} unit="" step={0.001} width="w-20" onChange={v => setField('epFlipPxProtFrac', v)} />（0.004=0.4%）。
                <div className="mt-1">双槽
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epSlot2Enable ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epSlot2Enable: +e.target.value }))}>
                  <option value="0">关</option><option value="1">开</option>
                </select>
                ，加注门槛（一腿浮盈）≥<InlineParam pkey="epSlot2MinFav" value={(form as any).epSlot2MinFav ?? null} defaultValue={(dp as any).epSlot2MinFav ?? 0.008} unit="" step={0.002} width="w-20" onChange={v => setField('epSlot2MinFav', v)} />、二腿尺寸<InlineParam pkey="epSlot2Size" value={(form as any).epSlot2Size ?? null} defaultValue={(dp as any).epSlot2Size ?? 0.5} unit="×lot" step={0.1} onChange={v => setField('epSlot2Size', v)} />、每日≤<InlineParam pkey="epSlot2MaxDay" value={(form as any).epSlot2MaxDay ?? null} defaultValue={(dp as any).epSlot2MaxDay ?? 2} unit="次" step={1} onChange={v => setField('epSlot2MaxDay', v)} />、一腿保本线<InlineParam pkey="epSlot2BeFrac" value={(form as any).epSlot2BeFrac ?? null} defaultValue={(dp as any).epSlot2BeFrac ?? 0.002} unit="" step={0.001} width="w-20" onChange={v => setField('epSlot2BeFrac', v)} />、二腿止损×<InlineParam pkey="epSlot2StopMult" value={(form as any).epSlot2StopMult ?? null} defaultValue={(dp as any).epSlot2StopMult ?? 0.8} unit="" step={0.1} onChange={v => setField('epSlot2StopMult', v)} />。</div>
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑪ OSC振荡指数闸（今日适不适合做T·做市型T0层2）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p>不预测方向，实时衡量"今天像不像锯齿场"：30分钟折返次数(40%)+1小时VWAP穿越(30%)+ATR/价差比(30%)合成0-100。低于阈值→今日不宜做T，禁新开仓（平仓不受影响）。窗口未形成(开盘~15分钟)不判。</p></TooltipContent></Tooltip>：模式
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epOscEnable ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epOscEnable: parseFloat(e.target.value) }))}>
                  <option value="0">关</option><option value="0.5">影子(只记录)</option><option value="1">生效</option>
                </select>
                ，阈值≥<InlineParam pkey="epOscMin" value={(form as any).epOscMin ?? null} defaultValue={(dp as any).epOscMin ?? 55} unit="分" step={5} onChange={v => setField('epOscMin', v)} />、折返幅度<InlineParam pkey="epOscZzFrac" value={(form as any).epOscZzFrac ?? null} defaultValue={(dp as any).epOscZzFrac ?? 0.0015} unit="" step={0.0005} width="w-20" onChange={v => setField('epOscZzFrac', v)} />（0.0015=0.15%）。
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑫ 护栏两刀（禁贴边逆势腿·0716晨-3704实证）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p>刀①极值距离：现价距日低&lt;比例×Rp 禁开空腿（贴地板做空=接飞刀），镜像距日高禁追多。刀②区间成熟度：开盘30分钟内价格贴日高/低时，需日内区间已张开≥比例×Rp 才允许开腿（新日未展开时贴边即险）。拦截即回滚模块状态，与趋势闸同款。</p></TooltipContent></Tooltip>：模式
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epGuardEnable ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epGuardEnable: parseFloat(e.target.value) }))}>
                  <option value="0">关</option><option value="0.5">影子(只记录)</option><option value="1">生效</option>
                </select>
                ，极值距离<InlineParam pkey="epNearExtGuard" value={(form as any).epNearExtGuard ?? null} defaultValue={(dp as any).epNearExtGuard ?? 0.15} unit="×Rp" step={0.05} width="w-20" onChange={v => setField('epNearExtGuard', v)} />、成熟度<InlineParam pkey="epRangeMatureFrac" value={(form as any).epRangeMatureFrac ?? null} defaultValue={(dp as any).epRangeMatureFrac ?? 0.30} unit="×Rp" step={0.05} width="w-20" onChange={v => setField('epRangeMatureFrac', v)} />。
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑧ 回踩入场（治确认溢价·买在半山腰）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p>A轨确认信号出现后不立即市价追，等价格<b>回踩</b>到确认价∓该比例再进场；等待超时且价格未跑远则按现价兜底进（下界=原行为）；跑远则放弃该单。W_OPEN/C轨追势单不走回踩。</p><p className="mt-1">0=关闭（确认即市价，原行为）。</p></TooltipContent></Tooltip>：回踩<InlineParam pkey="epEntryRetraceFrac" value={(form as any).epEntryRetraceFrac ?? null} defaultValue={(dp as any).epEntryRetraceFrac ?? 0} unit="" step={0.001} width="w-20" onChange={v => setField('epEntryRetraceFrac', v)} />（0.004=0.4%，0=关），等待<InlineParam pkey="epEntryWaitSec" value={(form as any).epEntryWaitSec ?? null} defaultValue={(dp as any).epEntryWaitSec ?? 60} unit="秒" step={10} onChange={v => setField('epEntryWaitSec', v)} />，放弃线<InlineParam pkey="epEntryGiveupFrac" value={(form as any).epEntryGiveupFrac ?? null} defaultValue={(dp as any).epEntryGiveupFrac ?? 0.006} unit="" step={0.001} width="w-20" onChange={v => setField('epEntryGiveupFrac', v)} />。
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑦ S4方向闸（单边日禁逆势新开）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p>开盘N分钟后，若当日方向效率 |现价−开盘|÷(高−低) ≥阈值 且振幅≥下限，判定单边日，禁止逆势腿<b>新开仓</b>（平仓/止损/顺势不受影响）。</p><p className="mt-1"><b>模式</b>：关 / 影子（只记录会拦谁，不真拦）/ 生效。⚠️ 接管时间勿低于30分钟，否则会没收 W_OPEN 开盘窗弹药。</p></TooltipContent></Tooltip>：模式
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epRegimeAdopt ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epRegimeAdopt: parseFloat(e.target.value) }))}>
                  <option value="0">关</option><option value="0.5">影子(只记录)</option><option value="1">生效</option>
                </select>
                ，方向效率≥<InlineParam pkey="epRegimeEffMin" value={(form as any).epRegimeEffMin ?? null} defaultValue={(dp as any).epRegimeEffMin ?? 60} unit="%" step={5} onChange={v => setField('epRegimeEffMin', v)} />、当日振幅≥<InlineParam pkey="epRegimeMinRangePct" value={(form as any).epRegimeMinRangePct ?? null} defaultValue={(dp as any).epRegimeMinRangePct ?? 2} unit="%" step={0.5} onChange={v => setField('epRegimeMinRangePct', v)} />、开盘<InlineParam pkey="epRegimeOpenMin" value={(form as any).epRegimeOpenMin ?? null} defaultValue={(dp as any).epRegimeOpenMin ?? 30} unit="分" step={5} onChange={v => setField('epRegimeOpenMin', v)} />后接管。
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/30">
                <span className="font-semibold text-foreground">⑥ 开盘极值武器 W_OPEN（ABC三轨·有界实验）</span><Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="h-2.5 w-2.5 inline ml-1 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="top" className="max-w-md text-xs"><p><b>三轨赛跑</b>：A=常规W1(300秒预热)；B=事件反转轨(冲高后回撤反转开枪)；C=事件顺势轨(破锚追势, 低开瀑布形态)。谁先达成先启动谁。</p><p className="mt-1"><b>Rp</b>=昨日实测振幅%(夜批数据字段, 0=回退R基准)。所有幅度阈值均 ×Rp 自适应。</p><p className="mt-1">每股每腿每日限1发、半仓、独立止损；全池累亏≤-10000自动总闸关停(fubao cron每5分钟)。</p></TooltipContent></Tooltip>：模式
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epWopenEnable ?? 0)}
                  onChange={e => setForm(prev => ({ ...prev, epWopenEnable: +e.target.value }))}>
                  <option value="0">关</option><option value="1">仅信号</option><option value="2">自动开火</option>
                </select>
                ，Rp=<InlineParam pkey="epWopenRp" value={(form as any).epWopenRp ?? null} defaultValue={(dp as any).epWopenRp ?? 0} unit="%" step={0.1} onChange={v => setField('epWopenRp', v)} />（昨日实测振幅·夜批自动更新）。
                <div className="mt-1">B轨反转：开火下界<InlineParam pkey="epWopenMinSod" value={(form as any).epWopenMinSod ?? null} defaultValue={(dp as any).epWopenMinSod ?? 34386} unit="秒(当日)" step={60} width="w-20" onChange={v => setField('epWopenMinSod', v)} />（34386=9:33:06），跟踪窗至<InlineParam pkey="epWopenWinEnd" value={(form as any).epWopenWinEnd ?? null} defaultValue={(dp as any).epWopenWinEnd ?? 36000} unit="秒" step={300} width="w-20" onChange={v => setField('epWopenWinEnd', v)} />（36000=10:00）；冲高≥<InlineParam pkey="epWopenSurgeFrac" value={(form as any).epWopenSurgeFrac ?? null} defaultValue={(dp as any).epWopenSurgeFrac ?? 0.5} unit="×Rp" step={0.05} onChange={v => setField('epWopenSurgeFrac', v)} />、峰龄≥<InlineParam pkey="epWopenPeakAgeSec" value={(form as any).epWopenPeakAgeSec ?? null} defaultValue={(dp as any).epWopenPeakAgeSec ?? 150} unit="秒" step={10} onChange={v => setField('epWopenPeakAgeSec', v)} />、回撤≥<InlineParam pkey="epWopenRetraceFrac" value={(form as any).epWopenRetraceFrac ?? null} defaultValue={(dp as any).epWopenRetraceFrac ?? 0.15} unit="×Rp" step={0.01} onChange={v => setField('epWopenRetraceFrac', v)} />、方向tick占比≥<InlineParam pkey="epWopenTickRatio" value={(form as any).epWopenTickRatio ?? null} defaultValue={(dp as any).epWopenTickRatio ?? 0.6} unit="" step={0.05} onChange={v => setField('epWopenTickRatio', v)} />时开枪（高开镜像低吸）；止盈激活<InlineParam pkey="epWopenActFrac" value={(form as any).epWopenActFrac ?? null} defaultValue={(dp as any).epWopenActFrac ?? 0.15} unit="×Rp" step={0.05} onChange={v => setField('epWopenActFrac', v)} />、回撤出场<InlineParam pkey="epWopenTrailFrac" value={(form as any).epWopenTrailFrac ?? null} defaultValue={(dp as any).epWopenTrailFrac ?? 0.5} unit="×激活" step={0.1} onChange={v => setField('epWopenTrailFrac', v)} />、止损<InlineParam pkey="epWopenStopFrac" value={(form as any).epWopenStopFrac ?? null} defaultValue={(dp as any).epWopenStopFrac ?? 0.35} unit="×Rp" step={0.05} onChange={v => setField('epWopenStopFrac', v)} />、仓位<InlineParam pkey="epWopenQtyFrac" value={(form as any).epWopenQtyFrac ?? null} defaultValue={(dp as any).epWopenQtyFrac ?? 0.5} unit="" step={0.1} onChange={v => setField('epWopenQtyFrac', v)} />。</div>
                <div className="mt-1">C轨破锚追势：实弹
                <select className="mx-0.5 h-6 text-xs bg-primary/5 border border-primary/30 rounded px-1"
                  value={String((form as any).epWopenCAuto ?? 1)}
                  onChange={e => setForm(prev => ({ ...prev, epWopenCAuto: +e.target.value }))}>
                  <option value="1">开</option><option value="0">仅信号</option>
                </select>
                ；破锚≥<InlineParam pkey="epWopenCBreakFrac" value={(form as any).epWopenCBreakFrac ?? null} defaultValue={(dp as any).epWopenCBreakFrac ?? 0.25} unit="×Rp" step={0.05} onChange={v => setField('epWopenCBreakFrac', v)} />、<InlineParam pkey="epWopenCMomSec" value={(form as any).epWopenCMomSec ?? null} defaultValue={(dp as any).epWopenCMomSec ?? 90} unit="秒" step={10} onChange={v => setField('epWopenCMomSec', v)} />内跌速≥<InlineParam pkey="epWopenCMomFrac" value={(form as any).epWopenCMomFrac ?? null} defaultValue={(dp as any).epWopenCMomFrac ?? 0.20} unit="×Rp" step={0.05} onChange={v => setField('epWopenCMomFrac', v)} />、抛压票（主动买&lt;<InlineParam pkey="epWopenCActbuyMax" value={(form as any).epWopenCActbuyMax ?? null} defaultValue={(dp as any).epWopenCActbuyMax ?? 0.40} unit="" step={0.05} onChange={v => setField('epWopenCActbuyMax', v)} />或tick占比≥<InlineParam pkey="epWopenCTickRatio" value={(form as any).epWopenCTickRatio ?? null} defaultValue={(dp as any).epWopenCTickRatio ?? 0.70} unit="" step={0.05} onChange={v => setField('epWopenCTickRatio', v)} />）三票齐时追势开枪；峰值保护帽<InlineParam pkey="epWopenCExtCapFrac" value={(form as any).epWopenCExtCapFrac ?? null} defaultValue={(dp as any).epWopenCExtCapFrac ?? 0.30} unit="×Rp" step={0.05} onChange={v => setField('epWopenCExtCapFrac', v)} />（冲高超帽让位B轨）。</div>
                <div className="mt-1">A轨开盘让位（sp15·20260714回测+43%）：开盘<InlineParam pkey="epOpenGateMin" value={(form as any).epOpenGateMin ?? null} defaultValue={(dp as any).epOpenGateMin ?? 30} unit="分" step={5} onChange={v => setField('epOpenGateMin', v)} />内 W1 入场阈值×<InlineParam pkey="epOpenSpMult" value={(form as any).epOpenSpMult ?? null} defaultValue={(dp as any).epOpenSpMult ?? 1.5} unit="" step={0.1} onChange={v => setField('epOpenSpMult', v)} />（收紧A轨接飞刀，给B/C轨留开火窗）。</div>
              </div>
            </div>
            <p className="text-[10px] text-amber-400/80">⚠️ 回测多数股票 OOS≈负, 检测方向命中≈50%(掷硬币), SIM 实盘验证中; 调参前先参考实盘模拟数据。</p>
          </div>
        )}
        <div className="text-[10px] text-muted-foreground/60 text-center">
          一轮完成后，Agent进入新一轮监控周期，可根据实际情况切换方向进行交易
        </div>

        {/* 券商绑定 - ⚠️ 当前仅作展示，引擎端未读取该字段 */}
        <div className="px-1 pb-2 opacity-60">
          <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1.5">
            <Landmark className="h-3 w-3" />券商 & 行情源绑定
            <span className="text-[9px] text-amber-400 font-normal">⚠️ 仅展示，实际使用全局配置</span>
          </p>
          <p className="text-[10px] text-muted-foreground/70 mb-2">
            真实生效的券商/行情源在 <a href="/broker" className="underline hover:text-primary">接口配置页</a> 统一设置，此处个股级别绑定未实现。
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground mb-1 block">下单券商（仅展示）</label>
              <select disabled className="w-full h-8 text-xs rounded-md border border-input bg-background/50 px-2 cursor-not-allowed"
                value={form.brokerName ?? "xingye"}>
                <option value="xingye">兴业证券</option>
                <option value="haitung">海通证券</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground mb-1 block">行情源（仅展示）</label>
              <select disabled className="w-full h-8 text-xs rounded-md border border-input bg-background/50 px-2 cursor-not-allowed"
                value={form.marketDataSource ?? "mqtt_l2"}>
                <option value="mqtt_l2">MQTT L2（base32.cn，优先）</option>
                <option value="nats_l2">NATS L2（第三方）</option>
                <option value="xtquant">XtQuant L1（SMT-Q）</option>
                <option value="tencent_l1">腾讯 L1</option>
              </select>
            </div>
          </div>
        </div>
        {/* [2026-06-25 Cat1] 止损/风控微调 per-stock 参数 (空=全局默认) — zigzag 模式不显示 (其参数走 ZigZag tab) */}
        {form.positionMode !== 'zigzag_fusion' && (
        <details className="border rounded p-2 text-xs mt-2">
          <summary className="cursor-pointer font-semibold text-muted-foreground">止损 / 风控微调参数 (per-stock, 空=全局默认)</summary>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {(([
              ["atrTrailingK","ATR追踪K"],["s2MinProfitPct","S2最小毛利%"],["forceMarketDeviationMult","砸市价倍数"],
              ["timeStopProfitPct","时停盈利%"],["timeStopAtrMax","时停ATR上限"],["timeStopElapsedFrac","时停经过比"],
              ["volStopAtrPct","波动止损ATR"],["entryRequireTrend","入场顺势0/1"],["trendStrictSkip","严格趋势拦0/1"],
              ["afternoonS1PauseStartHm","午后停S1始HHMM"],["afternoonS1PauseEndHm","午后停S1止HHMM"],
            ]) as [string,string][]).map(([k,label]) => (
              <label key={k} className="flex flex-col gap-0.5">
                <span className="text-[10px] text-muted-foreground">{label}</span>
                <input type="number" step="any" className="border rounded px-1 py-0.5 bg-background"
                  value={(form as any)[k] ?? ""}
                  onChange={e => setField(k, e.target.value === "" ? null : +e.target.value)} />
              </label>
            ))}
          </div>
        </details>
        )}
        <DialogFooter className="flex items-center justify-between">
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground"
            onClick={() => {
              const reset: any = {};
              Object.keys(form).forEach(k => reset[k] = null);
              setForm(reset);
            }}>
            <RotateCcw className="h-3 w-3 mr-1" />全部恢复默认
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>取消</Button>
            <Button size="sm" onClick={() => onSave(form)} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}保存
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== 多日盈亏走势图组件 ==========
function PnlTrendChart({ monitorId, open }: { monitorId: number; open: boolean }) {
  const [trendDays, setTrendDays] = useState(30);
  const { data: trendData, isLoading: trendLoading } = trpc.agentMonitor.dailyPnlTrend.useQuery(
    { monitorId, days: trendDays },
    { enabled: open && !!monitorId }
  );

  const trend: any[] = (trendData as any)?.trend || (Array.isArray(trendData) ? trendData : []);
  const maxPnl = Math.max(...trend.map((d: any) => Math.abs(d.dailyPnl || 0)), 1);

  // 计算累计盈亏
  let cumPnl = 0;
  const trendWithCum = trend.map((d: any) => {
    cumPnl += (d.dailyPnl || 0);
    return { ...d, cumPnl };
  });
  const maxCum = Math.max(...trendWithCum.map((d: any) => Math.abs(d.cumPnl)), 1);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LineChart className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-medium">多日盈亏走势</span>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30, 60].map(d => (
            <Button key={d} variant={trendDays === d ? "default" : "ghost"} size="sm" className="h-6 text-[10px] px-2"
              onClick={() => setTrendDays(d)}>{d}天</Button>
          ))}
        </div>
      </div>

      {trendLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : trend.length > 0 ? (
        <div className="space-y-3">
          {/* 每日盈亏柱状图 */}
          <div className="p-3 rounded-lg bg-muted/10 border border-border/30">
            <div className="text-[10px] text-muted-foreground mb-2">每日盈亏 (元)</div>
            <div className="flex items-end gap-[2px] h-[100px]">
              {trendWithCum.map((d: any, i: number) => {
                const pnl = d.dailyPnl || 0;
                const h = Math.max(Math.abs(pnl) / maxPnl * 45, 2);
                return (
                  <TooltipProvider key={i}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex-1 flex flex-col items-center justify-end h-full relative">
                          <div className="absolute top-0 left-0 right-0 h-[45px]" />
                          {pnl >= 0 ? (
                            <div className="w-full rounded-t-sm bg-gain/70 transition-all" style={{ height: `${h}px`, marginBottom: '45px' }} />
                          ) : (
                            <div className="w-full rounded-b-sm bg-loss/70 transition-all" style={{ height: `${h}px`, marginTop: '45px' }} />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        <p>{d.date}</p>
                        <p className={pnl >= 0 ? "text-gain" : "text-loss"}>日盈亏: {pnl >= 0 ? "+" : ""}¥{pnl.toFixed(2)}</p>
                        <p>交易: {d.tradeCount}笔</p>
                        <p className={d.cumPnl >= 0 ? "text-gain" : "text-loss"}>累计: {d.cumPnl >= 0 ? "+" : ""}¥{d.cumPnl.toFixed(2)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
              <span>{trend[0]?.date}</span>
              <span>{trend[trend.length - 1]?.date}</span>
            </div>
          </div>

          {/* 累计盈亏曲线 */}
          <div className="p-3 rounded-lg bg-muted/10 border border-border/30">
            <div className="text-[10px] text-muted-foreground mb-2">累计盈亏曲线 (元)</div>
            <svg viewBox={`0 0 ${trendWithCum.length * 10} 80`} className="w-full h-[80px]" preserveAspectRatio="none">
              <line x1="0" y1="40" x2={trendWithCum.length * 10} y2="40" stroke="currentColor" strokeOpacity="0.1" strokeDasharray="2" />
              <polyline
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="1.5"
                points={trendWithCum.map((d: any, i: number) => {
                  const x = i * 10 + 5;
                  const y = 40 - (d.cumPnl / maxCum) * 35;
                  return `${x},${y}`;
                }).join(" ")}
              />
              {/* 填充区域 */}
              <polygon
                fill="hsl(var(--primary))"
                fillOpacity="0.1"
                points={[
                  `5,40`,
                  ...trendWithCum.map((d: any, i: number) => {
                    const x = i * 10 + 5;
                    const y = 40 - (d.cumPnl / maxCum) * 35;
                    return `${x},${y}`;
                  }),
                  `${(trendWithCum.length - 1) * 10 + 5},40`,
                ].join(" ")}
              />
            </svg>
            <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
              <span>{trend[0]?.date}</span>
              <span className={cumPnl >= 0 ? "text-gain font-medium" : "text-loss font-medium"}>
                累计: {cumPnl >= 0 ? "+" : ""}¥{cumPnl.toFixed(2)}
              </span>
              <span>{trend[trend.length - 1]?.date}</span>
            </div>
          </div>

          {/* 汇总统计 */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-2 rounded bg-muted/20 text-center">
              <div className="text-[10px] text-muted-foreground">盈利天数</div>
              <div className="text-sm font-bold text-gain">{trend.filter((d: any) => (d.dailyPnl || 0) > 0).length}</div>
            </div>
            <div className="p-2 rounded bg-muted/20 text-center">
              <div className="text-[10px] text-muted-foreground">亏损天数</div>
              <div className="text-sm font-bold text-loss">{trend.filter((d: any) => (d.dailyPnl || 0) < 0).length}</div>
            </div>
            <div className="p-2 rounded bg-muted/20 text-center">
              <div className="text-[10px] text-muted-foreground">单日最大盈</div>
              <div className="text-sm font-bold text-gain">+¥{Math.max(...trend.map((d: any) => d.dailyPnl || 0), 0).toFixed(0)}</div>
            </div>
            <div className="p-2 rounded bg-muted/20 text-center">
              <div className="text-[10px] text-muted-foreground">单日最大亏</div>
              <div className="text-sm font-bold text-loss">¥{Math.min(...trend.map((d: any) => d.dailyPnl || 0), 0).toFixed(0)}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <LineChart className="h-8 w-8 mb-3 opacity-30" />
          <p className="text-sm">暂无盈亏数据</p>
          <p className="text-xs mt-1 opacity-60">引擎运行后将自动统计每日盈亏</p>
        </div>
      )}
    </div>
  );
}

// ========== 交易记录弹窗（增强版：带统计+盈亏走势） ==========
function TradeRecordsDialog({ open, onOpenChange, monitor }: { open: boolean; onOpenChange: (open: boolean) => void; monitor: any }) {
  const { data, isLoading } = trpc.agentMonitor.tradeRecordsWithStats.useQuery(
    { monitorId: monitor?.id || 0, limit: 100 },
    { enabled: open && !!monitor?.id }
  );

  const records = data?.records || [];
  const stats = data?.stats;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            交易记录 - {monitor?.stockName} ({monitor?.stockCode})
          </DialogTitle>
          <DialogDescription className="text-xs">
            引擎交易记录明细、盈亏统计与多日走势
          </DialogDescription>
        </DialogHeader>

        {/* [Claude 改 2026-05-10] 上下布局：统计卡片 → 盈亏走势（折叠）→ 交易明细 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto overscroll-contain" style={{WebkitOverflowScrolling:"touch",minHeight:0}}>
            <>
            {/* 统计卡片 */}
            {stats && stats.totalTrades > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                  <div className="text-[10px] text-muted-foreground">总交易</div>
                  <div className="text-sm font-bold">{stats.totalTrades} 笔</div>
                  <div className="text-[10px] text-muted-foreground">{stats.buyCount} 买 / {stats.sellCount} 卖</div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                  <div className="text-[10px] text-muted-foreground">总盈亏</div>
                  <div className={`text-sm font-bold ${stats.totalPnl >= 0 ? "text-gain" : "text-loss"}`}>
                    {stats.totalPnl >= 0 ? "+" : ""}¥{stats.totalPnl.toFixed(2)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">手续费: ¥{stats.totalFees.toFixed(2)}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                  <div className="text-[10px] text-muted-foreground">胜率</div>
                  <div className="text-sm font-bold">{stats.winRate.toFixed(1)}%</div>
                  <Progress value={stats.winRate} className="h-1 mt-1" />
                  <div className="text-[10px] text-muted-foreground">{stats.winCount} 盈 / {stats.lossCount} 亏</div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                  <div className="text-[10px] text-muted-foreground">平均盈亏</div>
                  <div className={`text-sm font-bold ${stats.avgPnl >= 0 ? "text-gain" : "text-loss"}`}>
                    {stats.avgPnl >= 0 ? "+" : ""}¥{stats.avgPnl.toFixed(2)}
                  </div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                  <div className="text-[10px] text-muted-foreground">极值</div>
                  <div className="text-[10px]">
                    <span className="text-gain">最大盈: +¥{stats.maxProfit.toFixed(2)}</span>
                  </div>
                  <div className="text-[10px]">
                    <span className="text-loss">最大亏: ¥{stats.maxLoss.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* 交易明细表 */}
            {records.length > 0 ? (
              <div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs">时间</TableHead>
                      <TableHead className="text-xs">场景</TableHead>
                      <TableHead className="text-xs">方向</TableHead>
                      <TableHead className="text-xs text-right">价格</TableHead>
                      <TableHead className="text-xs text-right">数量</TableHead>
                      <TableHead className="text-xs text-right">金额</TableHead>
                      <TableHead className="text-xs text-right">盈亏</TableHead>
                      <TableHead className="text-xs text-right">盈亏率</TableHead>
                      <TableHead className="text-xs">原因</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.map((t: any) => (
                      <TableRow key={t.id} className="border-border/30">
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatBJ(t.createdAt, "datetime")}
                        </TableCell>
                        <TableCell>
                          {t.scenarioType ? (
                            <div className="flex items-center gap-1">
                              <Badge className={`text-[9px] ${t.scenarioType === "s1" ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                                {t.scenarioType === "s1" ? "S1" : "S2"}
                              </Badge>
                              {t.roundId !== null && t.roundId !== undefined && (
                                <span className="text-[9px] text-muted-foreground">R{t.roundId + 1}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[9px] text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${t.direction === "buy" ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                            {t.direction === "buy" ? "买入" : "卖出"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">¥{t.price?.toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-right">{t.quantity}</TableCell>
                        <TableCell className="text-xs text-right font-mono">¥{t.amount?.toFixed(0)}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${t.profitLoss > 0 ? "text-gain" : t.profitLoss < 0 ? "text-loss" : ""}`}>
                          {t.profitLoss && t.profitLoss !== 0 ? `${t.profitLoss >= 0 ? "+" : ""}¥${t.profitLoss.toFixed(2)}` : "-"}
                        </TableCell>
                        <TableCell className={`text-xs text-right ${t.profitLossPct > 0 ? "text-gain" : t.profitLossPct < 0 ? "text-loss" : ""}`}>
                          {t.profitLossPct && t.profitLossPct !== 0 ? `${t.profitLossPct >= 0 ? "+" : ""}${t.profitLossPct.toFixed(2)}%` : "-"}
                        </TableCell>
                        <TableCell>
                          {t.tradeReason ? (
                            <Badge variant="outline" className={`text-[9px] ${
                              t.tradeReason === "stopLoss" ? "text-red-400 border-red-400/30" :
                              t.tradeReason === "timeout" ? "text-amber-400 border-amber-400/30" :
                              t.tradeReason === "limitFilled" ? "text-blue-400 border-blue-400/30" :
                              t.tradeReason === "forceClose" ? "text-purple-400 border-purple-400/30" :
                              "text-green-400 border-green-400/30"
                            }`}>
                              {t.tradeReason === "stopLoss" ? "止损" : t.tradeReason === "timeout" ? "超时" : t.tradeReason === "limitFilled" ? "限价成交" : t.tradeReason === "forceClose" ? "做平" : "确认"}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px]">{t.isDryRun ? "模拟" : "实盘"}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <History className="h-8 w-8 mb-3 opacity-30" />
                <p className="text-sm">暂无交易记录</p>
                <p className="text-xs mt-1 opacity-60">引擎运行后的交易将自动记录在此</p>
              </div>
            )}
              {/* 盈亏走势 折叠区 */}
              <TrendSection monitorId={monitor?.id || 0} open={open} />
          </>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── 盈亏走势折叠组件 [Claude 改 2026-05-10]
function TrendSection({ monitorId, open }: { monitorId: number; open: boolean }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border/30 rounded-lg mb-3 mt-1">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/20 rounded-lg transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <LineChart className="h-3.5 w-3.5 text-primary" />
          盈亏走势
        </span>
        <span className="text-muted-foreground text-[10px]">{expanded ? "▲ 收起" : "▼ 展开"}</span>
      </button>
      {expanded && (
        <div className="px-2 pb-3 border-t border-border/20">
          <PnlTrendChart monitorId={monitorId} open={open} />
        </div>
      )}
    </div>
  );
}

// ========== 引擎运行日志弹窗 ==========
function EngineLogDialog({ open, onOpenChange, monitor, isTradingHours = false }: { open: boolean; onOpenChange: (open: boolean) => void; monitor: any; isTradingHours?: boolean }) {
  const [logFilter, setLogFilter] = useState("all");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // 根据当前tab构建logTypes过滤参数
  const logTypesForQuery = logFilter === "all" ? undefined : [logFilter];

  const { data, isLoading, isFetching, refetch } = trpc.agentMonitor.engineLogs.useQuery(
    { monitorId: monitor?.id || 0, limit: 100, logTypes: logTypesForQuery },
    { enabled: open && !!monitor?.id, refetchInterval: open ? smartInterval(5000, isTradingHours, 30000) : false }
  );

  const logs = data?.logs || [];
  const filteredLogs = logs; // 服务端已经按logTypes过滤了

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = 0; // logs are desc order, newest at top
    }
  }, [filteredLogs.length, autoScroll]);

  const logTypeOptions = [
    { value: "all", label: "全部" },
    { value: "trade_buy", label: "买入" },
    { value: "trade_sell", label: "卖出" },
    { value: "signal", label: "信号" },
    { value: "observe", label: "观察" },
    { value: "direction", label: "方向" },
    { value: "basis_update", label: "基准" },
    { value: "stop_loss", label: "止损" },
    { value: "pause", label: "暂停" },
    { value: "error", label: "错误" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* [移动端紧凑化 20260813 Steven] 手机上原样式每条日志占半屏(长英文串被挤成竖排), 241条只见1条 */}
      <DialogContent className="max-w-3xl w-[96vw] sm:w-full h-[88vh] sm:max-h-[85vh] sm:h-auto p-3 sm:p-6 gap-2 sm:gap-4 flex flex-col overflow-hidden">
        <DialogHeader className="space-y-0 shrink-0">
          <DialogTitle className="flex items-center gap-1.5 text-sm sm:text-lg min-w-0">
            <Terminal className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
            <span className="truncate">运行日志 · {monitor?.stockName}({monitor?.stockCode})</span>
          </DialogTitle>
          {/* 说明文字移动端隐藏: 手机上占两行, 信息价值低 */}
          <DialogDescription className="text-xs hidden sm:block">
            实时展示引擎每次tick的决策过程、基准价变化和交易执行
          </DialogDescription>
        </DialogHeader>

        {/* 过滤器: 移动端单行横向滚动(原 flex-wrap 在手机上折成三行) */}
        <div className="flex items-center gap-1 overflow-x-auto sm:flex-wrap sm:gap-2 shrink-0 -mx-1 px-1">
          {logTypeOptions.map(opt => (
            <Button key={opt.value} variant={logFilter === opt.value ? "default" : "ghost"} size="sm" className="h-6 text-[10px] px-2 shrink-0"
              onClick={() => setLogFilter(opt.value)}>
              {opt.label}
            </Button>
          ))}
          <div className="hidden sm:block flex-1" />
          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 shrink-0" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-3 w-3 sm:mr-1 ${isFetching ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">刷新</span>
          </Button>
          <Button variant={autoScroll ? "default" : "ghost"} size="sm" className="h-6 text-[10px] px-2 shrink-0"
            onClick={() => setAutoScroll(!autoScroll)}>
            <span className="sm:hidden">滚动{autoScroll ? "开" : "关"}</span>
            <span className="hidden sm:inline">{autoScroll ? "自动滚动 ON" : "自动滚动 OFF"}</span>
          </Button>
        </div>

        {/* 日志列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filteredLogs.length > 0 ? (
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto sm:max-h-[500px] space-y-px sm:space-y-0.5 font-mono text-[11px] sm:text-xs bg-black/20 rounded-lg p-1.5 sm:p-2 border border-border/30">
            {filteredLogs.map((log: any) => {
              const typeInfo = LOG_TYPE_MAP[log.logType] || LOG_TYPE_MAP.tick;
              const TypeIcon = typeInfo.icon;
              const isTradeLog = log.logType === "trade_buy" || log.logType === "trade_sell";
              return (
                // 移动端两行(元信息行 + 正文整宽行), 桌面端保持单行 —— 修长英文串被挤成竖排
                <div key={log.id} className={`flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1.5 py-0.5 sm:py-1 px-1.5 sm:px-2 rounded hover:bg-white/5 ${isTradeLog ? "bg-white/5 border-l-2 border-primary/50" : ""}`}>
                  <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 min-w-0">
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {formatBJ(log.createdAt, "time")}
                    </span>
                    <TypeIcon className={`h-3 w-3 shrink-0 ${typeInfo.color}`} />
                    <Badge variant="outline" className={`text-[8px] px-1 shrink-0 ${typeInfo.color}`}>{typeInfo.label}</Badge>
                    {log.dataSource && (
                      // 数据源徽章移动端隐藏: 手机上挤占正文宽度, 排障时用桌面端看
                      <Badge variant="outline" className={`hidden sm:inline-flex text-[8px] px-1 shrink-0 ${log.dataSource === "proxy" ? "text-green-400 border-green-400/30" : "text-yellow-400 border-yellow-400/30"}`}>
                        {(() => { const ds = log.dataSource; const isL2 = ds === "nats_l2" || ds === "mqtt_l2" || ds === "direct_push" || log.marketDataLevel === "L2"; const sourceLabel = ds === "direct_push" ? "XQ" : ds === "nats_l2" ? "NATS" : ds === "mqtt_l2" ? "MQTT" : ds === "proxy" ? "XQ" : ds === "xtquant" ? "XQ" : "TX"; return `${isL2 ? "L2" : "L1"}·${sourceLabel}`; })()}
                      </Badge>
                    )}
                    {log.direction && (
                      <Badge variant="outline" className={`text-[8px] px-1 shrink-0 ${log.direction === "up" ? "text-gain" : "text-loss"}`}>
                        {log.direction === "up" ? "↑" : "↓"}
                      </Badge>
                    )}
                    {log.basisPrice && (
                      // 移动端基准价并入元信息行(桌面端仍在行尾)
                      <span className="sm:hidden text-[10px] text-cyan-400/70 whitespace-nowrap ml-auto">基准¥{log.basisPrice.toFixed(2)}</span>
                    )}
                  </div>
                  <span className="text-muted-foreground flex-1 min-w-0 whitespace-pre-wrap break-all sm:break-words leading-snug">
                    {log.reason || "-"}
                    {log.tradePrice && (
                      <span className="ml-1.5 text-foreground">
                        ¥{log.tradePrice.toFixed(2)} x{log.tradeQuantity}股
                        {log.tradePnl !== null && log.tradePnl !== undefined && (
                          <span className={log.tradePnl >= 0 ? "text-gain ml-1" : "text-loss ml-1"}>
                            {log.tradePnl >= 0 ? "+" : ""}¥{log.tradePnl.toFixed(2)}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                  {log.basisPrice && (
                    <span className="hidden sm:inline text-[10px] text-cyan-400/70 shrink-0 whitespace-nowrap">基准¥{log.basisPrice.toFixed(2)}</span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Terminal className="h-8 w-8 mb-3 opacity-30" />
            <p className="text-sm">暂无运行日志</p>
            <p className="text-xs mt-1 opacity-60">启动引擎后将自动记录决策过程</p>
          </div>
        )}

        <div className="text-[10px] text-muted-foreground text-right">
          共 {data?.total || 0} 条日志 · 显示最近 {filteredLogs.length} 条 · 每5秒自动刷新
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== 引擎状态实时面板 ==========
function EngineDetailDialog({ open, onOpenChange, monitor, defaultParams, isTradingHours = false }: { open: boolean; onOpenChange: (open: boolean) => void; monitor: any; defaultParams: any; isTradingHours?: boolean }) {
  // [Claude 2026-05-18] React Hook #300 修复: 不能在 hook 之前 early return
  const { data: internalState } = trpc.agentMonitor.engineInternalState.useQuery(
    { monitorId: monitor?.id ?? 0 },
    { enabled: !!monitor && open && !!monitor.id, refetchInterval: open ? smartInterval(3000, isTradingHours, 30000) : false }
  );

  if (!monitor) return null;

  const statusInfo = ENGINE_STATUS_MAP[internalState?.engineStatus || monitor.engineStatus] || ENGINE_STATUS_MAP.idle;
  const StatusIcon = statusInfo.icon;

  // 方向流程步骤定义
  const s1Steps = [
    { num: 1, label: "检测回调", desc: `从高点回调超过 ${internalState?.activeParams?.s1CallbackPct ?? 0.5}%，设为基准价` },
    { num: 2, label: "观察确认", desc: `${internalState?.activeParams?.s1ObserveWindowSec ?? 60}秒窗口确认，跌幅不超 ${internalState?.activeParams?.s1ConfirmDropMaxPct ?? 1.0}% 则卖出` },
    { num: 3, label: "卖出挂买", desc: `卖出成交价成为新基准价，以低于基准价 ${internalState?.activeParams?.limitOrderOffsetPct ?? 1.5}% 挂买入` },
    { num: 4, label: "超时处理", desc: `等待 ${internalState?.activeParams?.limitOrderTimeoutSec ?? 600}秒未成交，以当前市价买入` },
    { num: 5, label: "止损保护", desc: `股价反涨超过卖出价 ${internalState?.activeParams?.s1StopLossRisePct ?? 1.0}%，立即买入止损` },
  ];
  const s2Steps = [
    { num: 1, label: "检测反弹", desc: `从低点反弹超过 ${internalState?.activeParams?.s2BouncePct ?? 0.5}%，设为基准价` },
    { num: 2, label: "观察确认", desc: `${internalState?.activeParams?.s2ObserveWindowSec ?? 60}秒窗口确认，涨幅达 ${internalState?.activeParams?.s2ConfirmRiseMinPct ?? 1.0}% 则买入` },
    { num: 3, label: "买入挂卖", desc: `买入成交价成为新基准价，以高于基准价 ${internalState?.activeParams?.limitOrderOffsetPct ?? 1.5}% 挂卖出` },
    { num: 4, label: "超时处理", desc: `等待 ${internalState?.activeParams?.limitOrderTimeoutSec ?? 600}秒未成交，以当前市价卖出` },
    { num: 5, label: "止损保护", desc: `股价继续跌低于买入价 ${internalState?.activeParams?.s2StopLossDropPct ?? 1.0}%，立即卖出止损` },
  ];

  const scenario = internalState?.scenario;
  const activeSteps = scenario === "s1" ? s1Steps : scenario === "s2" ? s2Steps : null;
  const stepNumber = internalState?.stepNumber || 0;

  // 倒计时格式化
  const formatCountdown = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return null;
    const m = Math.floor(seconds / 60);
    const s = Math.ceil(seconds % 60);
    return m > 0 ? `${m}分${s}秒` : `${s}秒`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            引擎实时状态 - {monitor.stockName} ({monitor.stockCode})
          </DialogTitle>
          <DialogDescription className="text-xs">
            实时显示引擎内部运行状态、当前场景步骤和倒计时（每2秒刷新）
          </DialogDescription>
        </DialogHeader>

        {/* 引擎状态概览 */}
        <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <StatusIcon className="h-4 w-4" />
              <Badge className={`text-[10px] ${statusInfo.color}`}>{statusInfo.label}</Badge>
              {scenario && (
                <Badge className={`text-[10px] ${scenario === "s1" ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                  {scenario === "s1" ? "方向一：先卖后买" : "方向二：先买后卖"}
                </Badge>
              )}
              {internalState?.isFirstTrade && (
                <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-400/30">首次交易</Badge>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{internalState?.engineMessage || monitor.engineMessage || "等待中"}</p>
          {internalState?.currentStep && (
            <p className="text-xs font-medium text-primary mt-1">
              → 步骤{stepNumber}: {internalState.currentStep}
            </p>
          )}
        </div>

        {/* 核心状态指标 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="p-2.5 rounded-lg bg-muted/20 border border-border/30 text-center">
            <div className="text-[10px] text-muted-foreground">基准价</div>
            <div className="text-sm font-bold font-mono">
              {internalState?.currentBasis ? `¥${Number(internalState.currentBasis).toFixed(2)}` : "未建立"}
            </div>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/20 border border-border/30 text-center">
            <div className="text-[10px] text-muted-foreground">今日轮次</div>
            <div className="text-sm font-bold">
              {internalState?.todayRounds || 0} / {internalState?.maxDailyRounds || 5}
            </div>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/20 border border-border/30 text-center">
            <div className="text-[10px] text-muted-foreground">今日盈亏</div>
            <div className={`text-sm font-bold ${(internalState?.todayProfit || 0) >= 0 ? "text-gain" : "text-loss"}`}>
              {(internalState?.todayProfit || 0) >= 0 ? "+" : ""}¥{(internalState?.todayProfit || 0).toFixed(2)}
            </div>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/20 border border-border/30 text-center">
            <div className="text-[10px] text-muted-foreground">持仓</div>
            <div className="text-sm font-bold">{internalState?.currentHolding || 0} 股</div>
          </div>
        </div>

        {/* 倒计时区域 */}
        {(internalState?.observeCountdown !== null || internalState?.fillCountdown !== null || internalState?.cooldownCountdown !== null) && (
          <div className="space-y-2">
            {internalState?.observeCountdown !== null && internalState?.observeCountdown !== undefined && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <Timer className="h-4 w-4 text-blue-400 animate-pulse" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-blue-300">观察窗口倒计时</div>
                  <div className="text-[10px] text-blue-400/70">窗口结束时判断是否执行交易</div>
                </div>
                <span className="text-lg font-bold font-mono text-blue-300">{formatCountdown(internalState.observeCountdown)}</span>
              </div>
            )}
            {internalState?.fillCountdown !== null && internalState?.fillCountdown !== undefined && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Timer className="h-4 w-4 text-amber-400 animate-pulse" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-amber-300">配对交易等待</div>
                  <div className="text-[10px] text-amber-400/70">
                    等待{internalState?.fillDirection === "buy" ? "买回" : "卖出"}，基准价 ¥{internalState?.fillBasisPrice?.toFixed(2) || "-"}
                  </div>
                </div>
                <span className="text-lg font-bold font-mono text-amber-300">{formatCountdown(internalState.fillCountdown)}</span>
              </div>
            )}
            {internalState?.cooldownCountdown !== null && internalState?.cooldownCountdown !== undefined && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                <ShieldAlert className="h-4 w-4 text-red-400" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-red-300">止损冷却中</div>
                  <div className="text-[10px] text-red-400/70">连续止损 {internalState?.consecutiveStopLoss || 0} 次，冷却后恢复</div>
                </div>
                <span className="text-lg font-bold font-mono text-red-300">{formatCountdown(internalState.cooldownCountdown)}</span>
              </div>
            )}
          </div>
        )}

        {/* 追踪数据 */}
        {(internalState?.trackingHigh || internalState?.trackingLow) && (
          <div className="grid grid-cols-2 gap-2">
            {internalState?.trackingHigh && (
              <div className="p-2 rounded bg-gain/10 border border-gain/20">
                <div className="text-[10px] text-gain/70">追踪最高价</div>
                <div className="text-sm font-bold font-mono text-gain">¥{internalState.trackingHigh.toFixed(2)}</div>
              </div>
            )}
            {internalState?.trackingLow && (
              <div className="p-2 rounded bg-loss/10 border border-loss/20">
                <div className="text-[10px] text-loss/70">追踪最低价</div>
                <div className="text-sm font-bold font-mono text-loss">¥{internalState.trackingLow.toFixed(2)}</div>
              </div>
            )}
          </div>
        )}

        {/* 场景流程步骤展示 */}
        {activeSteps && (
          <div className="space-y-1">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              {scenario === "s1" ? <TrendingUp className="h-3 w-3 text-gain" /> : <TrendingDown className="h-3 w-3 text-loss" />}
              {scenario === "s1" ? "方向一：追高回调" : "方向二：探底反弹"}
            </h4>
            <div className="space-y-0.5">
              {activeSteps.map(step => {
                const isActive = step.num === stepNumber;
                const isPast = step.num < stepNumber;
                return (
                  <div key={step.num} className={`flex items-start gap-2 p-1.5 rounded text-xs ${
                    isActive ? "bg-primary/10 border border-primary/30" : isPast ? "opacity-50" : "opacity-70"
                  }`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                      isActive ? "bg-primary text-primary-foreground" : isPast ? "bg-muted text-muted-foreground" : "bg-muted/50 text-muted-foreground"
                    }`}>
                      {isPast ? <Check className="h-3 w-3" /> : step.num}
                    </div>
                    <div>
                      <span className={`font-medium ${isActive ? "text-primary" : ""}`}>{step.label}</span>
                      <span className="text-muted-foreground ml-1">{step.desc}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 生效参数摘要 */}
        {internalState?.activeParams && (
          <details className="group">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground flex items-center gap-1 py-1">
              <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
              当前生效参数
            </summary>
            <div className="grid grid-cols-2 gap-1.5 mt-2">
              {[
                { label: "每笔股数", value: `${internalState.activeParams.tradeLotSize} 股` },
                { label: "最大轮次", value: `${internalState.activeParams.maxDailyRounds} 轮` },
                { label: "尾盘保护", value: `${internalState.activeParams.tailProtectMinutes} 分钟` },
                { label: "滑点保护", value: `${internalState.activeParams.slippageProtectPct}%` },
                { label: "方向一回调触发", value: `${internalState.activeParams.s1CallbackPct}%` },
                { label: "方向一确认跌幅上限", value: `${internalState.activeParams.s1ConfirmDropMaxPct}%` },
                { label: "方向一止损上涨", value: `${internalState.activeParams.s1StopLossRisePct}%` },
                { label: "方向二反弹触发", value: `${internalState.activeParams.s2BouncePct}%` },
                { label: "方向二确认涨幅下限", value: `${internalState.activeParams.s2ConfirmRiseMinPct}%` },
                { label: "方向二止损下跌", value: `${internalState.activeParams.s2StopLossDropPct}%` },
                { label: "限价偏移", value: `${internalState.activeParams.limitOrderOffsetPct ?? 1.5}%` },
                { label: "限价超时", value: `${internalState.activeParams.limitOrderTimeoutSec ?? 600}秒` },
              ].map(item => (
                <div key={item.label} className="flex justify-between items-center p-1.5 rounded bg-muted/20">
                  <span className="text-[10px] text-muted-foreground">{item.label}</span>
                  <span className="text-[11px] font-mono">{item.value}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ========== 回测对比弹窗（场景化参数展示） ==========
function BacktestCompareDialog({ open, onOpenChange, monitor }: { open: boolean; onOpenChange: (open: boolean) => void; monitor: any }) {
  const [days, setDays] = useState(30);
  const [result, setResult] = useState<any>(null);

  const backtestCompare = trpc.agentMonitor.backtestCompare.useMutation({
    onSuccess: (data: any) => {
      setResult(data);
      toast.success("回测对比完成");
    },
    onError: (err: any) => toast.error(`回测失败: ${err.message}`),
  });

  const handleRun = () => {
    if (!monitor?.id) return;
    backtestCompare.mutate({ monitorId: monitor.id, days });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-primary" />
            回测对比 - {monitor?.stockName} ({monitor?.stockCode})
          </DialogTitle>
          <DialogDescription className="text-xs">
            使用当前场景化引擎参数对历史数据进行回测，并与实际交易结果对比
          </DialogDescription>
        </DialogHeader>

        {/* 参数选择 */}
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">回测天数</Label>
            <div className="flex gap-1">
              {[30, 60, 90, 180].map(d => (
                <Button key={d} variant={days === d ? "default" : "outline"} size="sm" className="h-7 text-xs px-3"
                  onClick={() => setDays(d)}>
                  {d}天
                </Button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={handleRun} disabled={backtestCompare.isPending}>
            {backtestCompare.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
            开始回测
          </Button>
        </div>

        {/* 当前场景化参数展示 */}
        {result?.params && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium flex items-center gap-1">
              <Settings2 className="h-3 w-3" />当前引擎参数
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* 方向一参数 */}
              <div className="p-2.5 rounded-lg bg-gain/5 border border-gain/20">
                <div className="text-[10px] font-medium text-gain mb-1.5 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />方向一：追高回调
                </div>
                <div className="grid grid-cols-2 gap-1 text-[10px]">
                  <div>回调触发: <span className="font-mono">{result.params.s1CallbackPct}%</span></div>
                  <div>观察窗口: <span className="font-mono">{result.params.s1ObserveWindowSec}秒</span></div>
                  <div>确认跌幅上限: <span className="font-mono">{result.params.s1ConfirmDropMaxPct}%</span></div>
                  <div>买回等待: <span className="font-mono">{result.params.s1FillWaitSec}秒</span></div>
                  <div>买回目标跌幅: <span className="font-mono">{result.params.s1BuybackDropPct}%</span></div>
                  <div>止损上涨: <span className="font-mono">{result.params.s1StopLossRisePct}%</span></div>
                </div>
              </div>
              {/* 方向二参数 */}
              <div className="p-2.5 rounded-lg bg-loss/5 border border-loss/20">
                <div className="text-[10px] font-medium text-loss mb-1.5 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" />方向二：探底反弹
                </div>
                <div className="grid grid-cols-2 gap-1 text-[10px]">
                  <div>反弹触发: <span className="font-mono">{result.params.s2BouncePct}%</span></div>
                  <div>观察窗口: <span className="font-mono">{result.params.s2ObserveWindowSec}秒</span></div>
                  <div>确认涨幅下限: <span className="font-mono">{result.params.s2ConfirmRiseMinPct}%</span></div>
                  <div>卖出等待: <span className="font-mono">{result.params.s2FillWaitSec}秒</span></div>
                  <div>卖出目标涨幅: <span className="font-mono">{result.params.s2SellRisePct}%</span></div>
                  <div>止损下跌: <span className="font-mono">{result.params.s2StopLossDropPct}%</span></div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 text-[10px] px-1">
              <div className="text-muted-foreground">每笔股数: <span className="text-foreground font-mono">{result.params.tradeLotSize}</span></div>
              <div className="text-muted-foreground">最大轮次: <span className="text-foreground font-mono">{result.params.maxDailyRounds}</span></div>
            </div>
          </div>
        )}

        {/* 对比结果 */}
        {result && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                对比结果
              </h4>

              {/* 核心指标对比表 */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs">指标</TableHead>
                      <TableHead className="text-xs text-right">回测结果</TableHead>
                      <TableHead className="text-xs text-right">实际交易</TableHead>
                      <TableHead className="text-xs text-center">对比</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { label: "总收益率", backtest: `${result.backtest?.totalReturn?.toFixed(2) || 0}%`, actual: result.actual?.totalTrades > 0 ? `${(result.actual.totalPnl / (result.params.tradeLotSize * 20) * 100).toFixed(2)}%` : "暂无数据" },
                      { label: "交易次数", backtest: `${result.backtest?.totalTrades || 0}`, actual: `${result.actual?.totalTrades || 0}` },
                      { label: "胜率", backtest: `${result.backtest?.winRate?.toFixed(1) || 0}%`, actual: `${result.actual?.winRate?.toFixed(1) || 0}%` },
                      { label: "盈亏比", backtest: `${result.backtest?.profitLossRatio?.toFixed(2) || 0}`, actual: result.actual?.winCount > 0 && result.actual?.lossCount > 0 ? `${(result.actual.maxProfit / Math.abs(result.actual.maxLoss)).toFixed(2)}` : "-" },
                      { label: "最大回撤", backtest: `${result.backtest?.maxDrawdown?.toFixed(2) || 0}%`, actual: "-" },
                      { label: "夏普比率", backtest: `${result.backtest?.sharpeRatio?.toFixed(2) || 0}`, actual: "-" },
                      { label: "总盈亏", backtest: `¥${((result.backtest?.totalReturn || 0) / 100 * result.params.tradeLotSize * 20).toFixed(0)}`, actual: `¥${result.actual?.totalPnl?.toFixed(2) || 0}` },
                    ].map(row => (
                      <TableRow key={row.label} className="border-border/30">
                        <TableCell className="text-xs font-medium">{row.label}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{row.backtest}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{row.actual}</TableCell>
                        <TableCell className="text-center">
                          {row.actual !== "暂无数据" && row.actual !== "-" ? (
                            <Badge variant="outline" className="text-[9px]">可对比</Badge>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* 回测交易明细 */}
              {result.backtest?.trades && result.backtest.trades.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-xs font-medium flex items-center gap-1 py-1">
                    <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                    回测交易明细 ({result.backtest.trades.length} 笔)
                  </summary>
                  <ScrollArea className="max-h-[250px] mt-2">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/50">
                          <TableHead className="text-xs">日期</TableHead>
                          <TableHead className="text-xs">方向</TableHead>
                          <TableHead className="text-xs text-right">价格</TableHead>
                          <TableHead className="text-xs text-right">盈亏</TableHead>
                          <TableHead className="text-xs">原因</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.backtest.trades.map((t: any, i: number) => (
                          <TableRow key={i} className="border-border/30">
                            <TableCell className="text-xs">{t.date}</TableCell>
                            <TableCell>
                              <Badge className={`text-[10px] ${t.type === "buy" ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                                {t.type === "buy" ? "买入" : "卖出"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">¥{t.price?.toFixed(2)}</TableCell>
                            <TableCell className={`text-xs text-right ${t.pnl > 0 ? "text-gain" : t.pnl < 0 ? "text-loss" : ""}`}>
                              {t.pnl ? `${t.pnl >= 0 ? "+" : ""}${t.pnlPct?.toFixed(2)}%` : "-"}
                            </TableCell>
                            <TableCell className="text-xs max-w-[300px] whitespace-pre-wrap break-words">{t.reason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </details>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// [Claude 2026-05-14] AI 建议条 - 监控卡片底部显示最新自动优化记录
function AIAdviceBar({ monitorId }: { monitorId: number }) {
  const { data: logs } = trpc.screening.autoOptimizeLog.useQuery(
    { limit: 50, days: 1 },
    { staleTime: 30000, refetchInterval: 60000 }
  );
  const myLog = (logs || []).find((l: any) => l.monitorId === monitorId);
  if (!myLog) return null;
  
  const time = new Date(myLog.createdAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
  const icon = myLog.shouldPause ? "⏸" : myLog.newTemplate ? "🔄" : "💡";
  const color = myLog.shouldPause ? "text-loss" : myLog.newTemplate ? "text-warning" : "text-primary";
  
  return (
    <div className={`flex items-start gap-1.5 text-[10px] px-2 py-1 mt-1 rounded bg-muted/20 border-l-2 ${myLog.shouldPause ? "border-loss" : "border-primary"}`}>
      <span className="shrink-0">{icon}</span>
      <span className={`${color} font-medium shrink-0`}>{time}</span>
      <span className="text-muted-foreground line-clamp-1 break-all">{myLog.reason}</span>
    </div>
  );
}

// ========== 量化交易首页 ==========


// [Steven 6/1 方案 C] LLM 实时辅助决策 banner (蓝色, 区分方案 A 的橙色)
function LLMAdviceBanner() {
  const utils = trpc.useUtils();
  const { data: advices } = trpc.trading.listLLMAdvices.useQuery(
    { status: 'pending', limit: 10 },
    { staleTime: 30000, refetchInterval: 60000 }
  );
  const applyMut = trpc.trading.applyLLMAdvice.useMutation({
    onSuccess: (r: any) => {
      toast.success(r.message);
      utils.trading.listLLMAdvices.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });
  const dismissMut = trpc.trading.dismissLLMAdvice.useMutation({
    onSuccess: () => utils.trading.listLLMAdvices.invalidate(),
  });
  const list = (advices as any[]) || [];
  if (list.length === 0) return null;

  return (
    <div className="rounded-lg border-2 border-blue-500/50 bg-blue-500/5 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-blue-400">🤖</span>
        <span className="text-sm font-semibold text-blue-400">
          LLM 实时辅助决策 ({list.length} 条 pending)
        </span>
      </div>
      <div className="space-y-2">
        {list.map((a: any) => {
          const conf = Number(a.confidence || 0);
          const confColor = conf >= 0.7 ? 'text-emerald-400' : conf >= 0.5 ? 'text-yellow-400' : 'text-orange-400';
          return (
            <div key={a.id} className="flex items-start gap-2 p-2 rounded bg-card/40 border border-blue-500/20 text-xs">
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium">
                  <span>{a.stockCode}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/40">{a.scenarioType}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600/30">{a.verdict}</span>
                  <span className={"text-[10px] " + confColor}>conf: {(conf * 100).toFixed(0)}%</span>
                </div>
                <div className="text-muted-foreground mt-0.5">{a.reason}</div>
                {a.paramChanges && a.paramChanges.length > 0 && (
                  <div className="text-blue-300 mt-1 text-[10px]">
                    📊 建议: {a.paramChanges.map((pc: any) => `${pc.name}→${pc.newValue} (Δ${(pc.delta * 100).toFixed(1)}%)`).join(', ')}
                  </div>
                )}
                {a.conflictWith && (
                  <div className="text-yellow-400 mt-1 text-[10px]">⚠️ 冲突: {a.conflictWith}</div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  className="px-2 py-1 rounded bg-blue-600/30 hover:bg-blue-600/50 text-blue-100 text-[10px]"
                  disabled={applyMut.isLoading || a.verdict === 'no_action' || conf < 0.5}
                  onClick={() => {
                    if (conf < 0.7 && !window.confirm(`置信度 ${(conf*100).toFixed(0)}% 偏低, 仍要应用?`)) return;
                    applyMut.mutate({ adviceId: a.id });
                  }}
                >
                  采用
                </button>
                <button
                  className="px-2 py-1 rounded bg-muted/40 hover:bg-muted/60 text-muted-foreground text-[10px]"
                  disabled={dismissMut.isLoading}
                  onClick={() => dismissMut.mutate({ adviceId: a.id })}
                >
                  忽略
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// [Steven 6/1 方案 A] 盘中信号失效预警 banner
function SignalRejectionAlertBanner() {
  const utils = trpc.useUtils();
  const { data: alerts } = trpc.trading.listSignalRejectionAlerts.useQuery(
    { status: 'pending', limit: 10 },
    { staleTime: 30000, refetchInterval: 60000 }
  );
  const applyMut = trpc.trading.applySignalRejectionAlert.useMutation({
    onSuccess: (r: any) => {
      toast.success(r.message);
      utils.trading.listSignalRejectionAlerts.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });
  const dismissMut = trpc.trading.dismissSignalRejectionAlert.useMutation({
    onSuccess: () => utils.trading.listSignalRejectionAlerts.invalidate(),
  });

  const list = (alerts as any[]) || [];
  if (list.length === 0) return null;

  return (
    <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/5 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-amber-400">🚨</span>
        <span className="text-sm font-semibold text-amber-400">
          盘中信号失效预警 ({list.length} 条 pending)
        </span>
      </div>
      <div className="space-y-2">
        {list.map((a: any) => (
          <div key={a.id} className="flex items-start gap-2 p-2 rounded bg-card/40 border border-amber-500/20 text-xs">
            <div className="flex-1">
              <div className="font-medium">{a.stockCode}</div>
              <div className="text-muted-foreground mt-0.5">{a.alertMessage}</div>
              {a.suggestedParam && (
                <div className="text-amber-300 mt-1">💡 建议: <code className="bg-muted/40 px-1 rounded">{a.suggestedParam}</code> {a.suggestedAdjustment}</div>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                className="px-2 py-1 rounded bg-amber-600/30 hover:bg-amber-600/50 text-amber-100 text-[10px]"
                disabled={applyMut.isLoading || !a.suggestedParam}
                onClick={() => applyMut.mutate({ alertId: a.id })}
              >
                应用建议
              </button>
              <button
                className="px-2 py-1 rounded bg-muted/40 hover:bg-muted/60 text-muted-foreground text-[10px]"
                disabled={dismissMut.isLoading}
                onClick={() => dismissMut.mutate({ alertId: a.id })}
              >
                忽略
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TradingHome({ onSwitchTab }: { onSwitchTab?: (tab: string) => void }) {
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();

  // [20260812 Steven UX] 实盘/模拟双视图: localStorage('t0-view-mode') 持久化, 首次默认 'live'
  const [viewMode, setViewMode] = useState<'live' | 'sim'>(() => {
    try { return localStorage.getItem('t0-view-mode') === 'sim' ? 'sim' : 'live'; } catch { return 'live'; }
  });
  useEffect(() => { try { localStorage.setItem('t0-view-mode', viewMode); } catch { /* 隐私模式等场景静默 */ } }, [viewMode]);

  const { data: config } = trpc.config.get.useQuery(undefined, { staleTime: 300000 });
  // [Claude 2026-05-08] 本地 tradeMode 显示（自动优化对话框需要）
  const tradeModeDisplay = (() => {
    const mode = (config as any)?.tradeMode || 'SIM_ONLY';
    if (mode === 'LIVE_TRADE') return { label: 'LIVE_TRADE', cls: 'border-red-500/50 text-red-400 bg-red-500/10' };
    if (mode === 'LIVE_OBSERVE') return { label: 'LIVE_OBSERVE', cls: 'border-amber-500/50 text-amber-400 bg-amber-500/10' };
    return { label: 'SIM_ONLY', cls: 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' };
  })();
  const { data: watchlistData, refetch: _refetchWatchlist } = trpc.watchlist.list.useQuery(undefined, { staleTime: 60000 });
  const _isTHGlobal = checkTradingHours().isTradingHours;
  const isTradingHoursGlobal = _isTHGlobal;
  // === 合并轮询：monitors + engineStatus + circuitBreaker 三合一 ===
  // 交易时段5s刷新，非交易时段30s，替代原来3个独立轮询（5s+10s+15s），减少约67%请求
  // [Claude 2026-05-07] 持仓列表，供"添加监控"区域使用
  const { data: positionsDataRaw } = trpc.positions.list.useQuery(undefined, {
    staleTime: 30000,
    refetchInterval: 60000,
  });
  // [Steven 2026-05-28 P0 防御] positions.list 历史返回格式不一致 (数组 / {positions,...} 对象)
  // 统一规整成数组, 防止 .some/.filter/.map 崩溃
  const positionsData: any[] = Array.isArray(positionsDataRaw)
    ? positionsDataRaw
    : ((positionsDataRaw as any)?.positions && Array.isArray((positionsDataRaw as any).positions)
        ? (positionsDataRaw as any).positions
        : []);
  const { data: dashboardData, refetch: refetchMonitors } = trpc.agentMonitor.dashboard.useQuery(undefined, {
    refetchInterval: smartInterval(5000, isTradingHoursGlobal, 30000),
    staleTime: 4000,
  });
  const monitors = dashboardData?.monitors;
  const engineStatus = dashboardData?.engineStatus;
  const cbStatus = dashboardData?.circuitBreaker;
  // [20260812 Steven UX] 复用统一实盘判定(agent_monitors.tradeMode==='live', 其余皆模拟),
  // 双视图过滤集合: _viewMonitors=网格/日志口径(含test盘), _liveCount/_simCount=展示计数
  const _isLiveMon = (m: any) => String((m as any).tradeMode || '').toLowerCase() === 'live';
  const _viewMonitors = useMemo(() => (monitors || []).filter((m: any) => (viewMode === 'live') === _isLiveMon(m)), [monitors, viewMode]);
  const _liveCount = (monitors || []).filter((m: any) => _isLiveMon(m)).length;
  const _simCount = (monitors || []).length - _liveCount;
  // 服务器资源监控（交易时段（60秒，非交易时段（5分钟）
  const resourceRefetchInterval = smartInterval(60000, isTradingHoursGlobal, 300000);
  // 延迟加载资源监控，避免首屏因跨VPN请求引擎而卡顿（2秒后才启用）
  const [resourcesEnabled, setResourcesEnabled] = useState(false);
  useEffect(() => { const t = setTimeout(() => setResourcesEnabled(true), 2000); return () => clearTimeout(t); }, []);
  const { data: resourcesData } = trpc.tradingProxy.resources.useQuery(undefined, { enabled: resourcesEnabled, refetchInterval: resourcesEnabled ? 30000 : false, retry: 0, staleTime: 25000 });
  const updateBandwidth = trpc.tradingProxy.updateBandwidthLimit.useMutation({
    onSuccess: () => { toast.success("带宽上限已更新"); setShowResourceConfigDialog(false); },
    onError: (err) => toast.error(err.message),
  });
  const updateResourceConfig = trpc.tradingProxy.updateResourceConfig.useMutation({
    onSuccess: () => { toast.success("资源监控配置已更新"); setShowResourceConfigDialog(false); },
    onError: (err) => toast.error(err.message),
  });
  const [showResourceConfigDialog, setShowResourceConfigDialog] = useState(false);
  const [showBandwidthDialog, setShowBandwidthDialog] = useState(false);
  const [bandwidthInput, setBandwidthInput] = useState("");
  const [intervalTradingInput, setIntervalTradingInput] = useState("60");
  const [intervalIdleInput, setIntervalIdleInput] = useState("300");

  const [addStockCode, setAddStockCode] = useState("");
  const [addStockName, setAddStockName] = useState("");
  const [addBrokerName, setAddBrokerName] = useState("xingye");
  const [addMarketDataSource, setAddMarketDataSource] = useState("xtquant");
  const [showAddDialog, setShowAddDialog] = useState(false);
  // [R4属主 20260817] 添加监控必须先定实盘/模拟。默认跟随当前视图(viewMode), 但**实盘需显式再确认**。
  const [addTradeMode, setAddTradeMode] = useState<'live' | 'simulation'>('simulation');
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  // 搜索防抖
  useEffect(() => {
    const t = setTimeout(() => setDebouncedKeyword(searchKeyword), 300);
    return () => clearTimeout(t);
  }, [searchKeyword]);
  const { data: searchResults } = trpc.stockAnalysis.search.useQuery(
    { keyword: debouncedKeyword, limit: 8 },
    { enabled: debouncedKeyword.length >= 1 }
  );
  const [editingMonitor, setEditingMonitor] = useState<any>(null);
  const [showParamDialog, setShowParamDialog] = useState(false);
  const [recordMonitor, setRecordMonitor] = useState<any>(null);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [detailMonitor, setDetailMonitor] = useState<any>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [logMonitor, setLogMonitor] = useState<any>(null);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [compareMonitor, setCompareMonitor] = useState<any>(null);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [replayMonitor, setReplayMonitor] = useState<any>(null);
  const [showReplayDialog, setShowReplayDialog] = useState(false);
  const [atrMonitor, setAtrMonitor] = useState<any>(null);
  // [Claude 2026-05-08] 单股自动优化二次确认 dialog 状态
  const [optimizeMonitor, setOptimizeMonitor] = useState<any>(null);  // 触发对话框的 monitor
  const [optimizePreview, setOptimizePreview] = useState<any>(null);  // dryRun 预览结果
  const [optimizeStep, setOptimizeStep] = useState<0 | 1 | 2>(0);  // 1=预览, 2=应用确认
  const [optimizeAllowLive, setOptimizeAllowLive] = useState(false);  // LIVE 模式确认勾选
  const [showAtrDialog, setShowAtrDialog] = useState(false);
  // 删除确认状态
  const [deleteConfirmMonitor, setDeleteConfirmMonitor] = useState<any>(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0=未开始 1=第一次确认 2=第二次确认
  // 启动确认状态
  const [startConfirmMonitor, setStartConfirmMonitor] = useState<any>(null);
  const [restartOverride, setRestartOverride] = useState<any>(null);  // [P1.5-3] 重启时自定义额度
  // 停止前持仓做平检查状态
  const [stopConfirmMonitor, setStopConfirmMonitor] = useState<any>(null);

  const addMonitor = trpc.agentMonitor.add.useMutation({
    onSuccess: () => { refetchMonitors(); toast.success("已添加到 Agent 监控列表"); },
    onError: (err) => toast.error(err.message),
  });
  // [Claude 2026-05-18] syncFromPositions mutation 已删除 (UI 按钮取消, 仅保留 09:23 cron)
  // [Claude 2026-05-08] 单股自动优化 mutation
  const runOptimizeOne = trpc.screening.runAutoOptimize.useMutation({
    onSuccess: (data: any) => {
      if (data.dryRun) {
        setOptimizePreview(data);
      } else {
        toast.success(`已应用 ${data.executed} 项调整`);
        setOptimizeMonitor(null); setOptimizeStep(0); setOptimizePreview(null); setOptimizeAllowLive(false);
        refetchMonitors();
      }
    },
    onError: (e: any) => toast.error(e.message),
  });
  const removeMonitor = trpc.agentMonitor.remove.useMutation({
    onSuccess: (data: any) => { 
      console.log('[DELETE] removeMonitor success:', data);
      refetchMonitors(); 
      if (data.softDeleted) {
        toast.success("已移入历史持仓（有交易记录，保留历史数据）");
      } else {
        toast.success("已从监控列表移除");
      }
      setDeleteConfirmMonitor(null);
      setDeleteStep(0);
    },
    onError: (err) => {
      console.error('[DELETE] removeMonitor error:', err);
      toast.error('删除失败: ' + err.message);
      setDeleteConfirmMonitor(null);
      setDeleteStep(0);
    },
  });
  const toggleRunning = trpc.agentMonitor.toggleRunning.useMutation({
    onSuccess: (_, vars) => { refetchMonitors(); toast.success(vars.isRunning ? "启动指令已下发，等待引擎端执行..." : "停止指令已下发"); },
    onError: (err) => toast.error(err.message),
  });
  const updateParams = trpc.agentMonitor.updateParams.useMutation({
    onSuccess: () => { refetchMonitors(); setShowParamDialog(false); toast.success("交易参数已保存，其他设备刷新页面后生效", { duration: 4000 }); },
    onError: (err) => toast.error(err.message),
  });
  const resumeAgent = trpc.agentMonitor.resume.useMutation({
    onSuccess: () => { refetchMonitors(); toast.success("Agent 已恢复运行"); },
    onError: (err) => toast.error(err.message),
  });
  const stoppedMonitors = useMemo(() => _viewMonitors.filter((m: any) => m.isRunning !== 1), [_viewMonitors]);
  const batchStartAll = trpc.agentMonitor.batchStartAll.useMutation({
    onMutate: (input) => {
      const monitorIds: number[] = (input?.monitorIds ?? []).map(Number);
      setStartingMonitorIds(new Set<number>(monitorIds));
    },
    onSuccess: (data: any) => {
      setStartingMonitorIds(new Set());
      refetchMonitors();
      toast.success(`${data.started} 只股票的启动指令已下发，等待引擎端执行...`);
      if (data.failed > 0) toast.error(`${data.failed} 只指令下发失败: ${data.failedStocks?.join(', ')}`);
    },
    onError: (err) => {
      setStartingMonitorIds(new Set());
      toast.error(err.message);
    },
  });

  // 智能批量重启：服务端仅处理当前视图白名单，达上限自动翻倍且保留当日轮次。
  const batchRestartAllSmart = trpc.agentMonitor.batchRestartAllSmart.useMutation({
    onMutate: (input) => {
      const monitorIds: number[] = (input?.monitorIds ?? []).map(Number);
      setStartingMonitorIds(new Set<number>(monitorIds));
    },
    onSuccess: (data: any) => {
      setStartingMonitorIds(new Set());
      refetchMonitors();
      if (data.mode === "executed") {
        const msg = `已重启 ${data.startedSuccess} 只 (其中 ${data.doubled} 只已翻倍额度)`;
        if (data.startedFail > 0) toast.error(`${msg}, ${data.startedFail} 只失败`);
        else toast.success(msg);
      } else {
        toast.success("已分析重启目标, 请确认翻倍");
      }
    },
    onError: (err) => {
      setStartingMonitorIds(new Set());
      toast.error(err.message);
    },
  });
  // [Claude 2026-05-21 P1.5-3] 单股自定义重启
  const restartMonitorWithOverride = trpc.agentMonitor.restartMonitorWithOverride.useMutation({
    onSuccess: (data: any) => {
      refetchMonitors();
      if (data.success) {
        toast.success(`监控已重启, maxRounds: ${data.oldValues?.maxDailyRounds}→${data.newValues?.maxDailyRounds}`);
      } else {
        toast.error(data.error || "重启失败");
      }
    },
    onError: (err) => toast.error(err.message),
  });
  const forceCloseUnfilled = trpc.engineUpdate.forceCloseUnfilled.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message);
        data.results.forEach((r: any) => {
          if (r.success) toast.success(`${r.stockName}: ${r.message}`);
          else toast.error(`${r.stockName}: ${r.message}`);
        });
      } else {
        toast.error(data.message);
      }
      utils.agentMonitor.list.invalidate();
    },
    onError: (err) => toast.error(`做平失败: ${err.message}`),
  });
  const batchStopAll = trpc.agentMonitor.batchStopAll.useMutation({
    onSuccess: (data: any) => {
      refetchMonitors();
      toast.success(`已停止 ${data.stopped} 只股票的 Agent 引擎${data.skipped > 0 ? `，${data.skipped} 只未做平股票保留运行` : ''}`);
    },
    onError: (err) => toast.error(err.message),
  });
  const batchApplyTemplate = trpc.agentMonitor.batchApplyTemplate.useMutation({
    onSuccess: (data: any) => { refetchMonitors(); toast.success(`已将模板应用到 ${data.applied} 只股票`); setShowBatchTemplateDialog(false); },
    onError: (err) => toast.error(err.message),
  });

  const [showBatchTemplateDialog, setShowBatchTemplateDialog] = useState(false);
  const [batchTemplateId, setBatchTemplateId] = useState("");
  const { data: templates } = trpc.agentMonitor.paramTemplates.useQuery();

  // 手动重置日内状态
  const [showDailyResetConfirm, setShowDailyResetConfirm] = useState(false);
  const dailyReset = trpc.scheduler.triggerDailyReset.useMutation({
    onSuccess: (data: any) => {
      refetchMonitors();
      toast.success(`已重置 ${data.resetCount ?? 0} 只股票的日内状态（方向/基准价/盈亏/轮次）`);
      setShowDailyResetConfirm(false);
    },
    onError: (err) => { toast.error(`重置失败: ${err.message}`); setShowDailyResetConfirm(false); },
  });

  // [R4属主 20260817] (stockCode, tradeMode) 才是身份, stockCode 不是。
  //   本文件原先在 3 处各建一次 monitorCodes, 且 monitorMap 同码后者静默覆盖前者
  //   —— 与 server 侧 realTodayPnl 原 GROUP BY stockCode 是同一根因。此处收敛为单点。
  const _modeOf = (m: any) => (String(m?.tradeMode || '').toLowerCase() === 'live' ? 'live' : 'sim');
  const monitorsByCode = useMemo(() => {
    const mp = new Map<string, any[]>();
    for (const m of ((monitors || []) as any[])) {
      const k = String(m.stockCode);
      if (!mp.has(k)) mp.set(k, []);
      (mp.get(k) as any[]).push(m);
    }
    return mp;
  }, [monitors]);
  const monitorCodes = useMemo(() => new Set(monitorsByCode.keys()), [monitorsByCode]);
  // 单值取用处仍给一个 monitor, 但**优先实盘那条**, 不再"数组里靠后的赢"
  const monitorMapTop = useMemo(() => new Map<string, any>(
    Array.from(monitorsByCode.entries()).map(([k, arr]) =>
      [k, (arr as any[]).find((x: any) => _modeOf(x) === 'live') || (arr as any[])[0]])), [monitorsByCode]);
  const monitorModeLabel = (code: string) => {
    const arr = (monitorsByCode.get(String(code)) || []) as any[];
    const hasLive = arr.some((x: any) => _modeOf(x) === 'live');
    const hasSim = arr.some((x: any) => _modeOf(x) === 'sim');
    return hasLive && hasSim ? '实盘+模拟' : hasLive ? '实盘' : hasSim ? '模拟' : '';
  };
  const runningMonitors = useMemo(() => _viewMonitors.filter((m: any) => m.isRunning === 1), [_viewMonitors]);
  const runningCount = runningMonitors.length;
  // 批量停止前检查未做平持仓
  const [showBatchStartConfirm, setShowBatchStartConfirm] = useState(false);
  const [startingMonitorIds, setStartingMonitorIds] = useState<Set<number>>(new Set());
  const [showBatchStopConfirm, setShowBatchStopConfirm] = useState(false);
  const unfilledMonitors = useMemo(() => runningMonitors.filter((m: any) => m.engineStatus === 'waiting_fill'), [runningMonitors]);
  const [limitPriceMode, setLimitPriceMode] = useState(false);
  const [limitPriceValue, setLimitPriceValue] = useState('');
  const pausedCount = _viewMonitors.filter((m: any) => m.isPaused === 1).length;
  // 监控列表筛选
  const [monitorFilter, setMonitorFilter] = useState<'all' | 'running' | 'unfilled' | 'stopped'>('all');
  // [20260812 Steven UX] 网格过滤基线=_viewMonitors(实盘/模拟视图), 二次筛运行状态
  const filteredMonitors = useMemo(() => {
    const list = _viewMonitors;
    switch (monitorFilter) {
      case 'running': return list.filter((m: any) => m.isRunning === 1);
      case 'unfilled': return list.filter((m: any) => m.isRunning === 1 && m.engineStatus === 'waiting_fill');
      case 'stopped': return list.filter((m: any) => m.isRunning !== 1);
      default: return list;
    }
  }, [_viewMonitors, monitorFilter]);
  // [20260804 Steven令] 批量实时行情: 名称旁显示现价+涨跌幅, 15s轮询, 行情源瞬断静默降级
  const _lqCodes = useMemo(() => Array.from(new Set((filteredMonitors || []).map((m: any) => String(m.stockCode || "")).filter(Boolean))), [filteredMonitors]);
  const { data: liveQuotes } = trpc.agentMonitor.liveQuotes.useQuery(
    { codes: _lqCodes }, { refetchInterval: 15000, enabled: _lqCodes.length > 0 });
  const stoppedCount = _viewMonitors.filter((m: any) => m.isRunning !== 1).length;
  // [K 步 2026-04-29] 汇总统计剔除 addSource='test' 的高频测试盘 (避免污染策略统计)
  const _statMonitors = (monitors || []).filter((m: any) => m.addSource !== 'test');
  // [20260812 Steven UX] 实盘/模拟双视图: 统计口径跟随 viewMode (不含test盘, 与既有 _statMonitors 口径一致)
  const _scopeMonitors = useMemo(() => _statMonitors.filter((m: any) => (viewMode === 'live') === _isLiveMon(m)), [_statMonitors, viewMode]);
  const _scopeTotals = {
    profit: _scopeMonitors.reduce((s: number, m: any) => s + (displayTodayPnl(m) || 0), 0),
    trades: _scopeMonitors.reduce((s: number, m: any) => s + (m.todayTradeCount || 0), 0),
    rounds: _scopeMonitors.reduce((s: number, m: any) => s + (m.todayRounds || 0), 0),
    amount: _scopeMonitors.reduce((s: number, m: any) => s + (Number((m as any).todayAmount) || 0), 0),
  };
  // [20260717 Steven] 底栏股/ETF分列: 服务端已给每monitor挂todayAmount(单边), 前端按品种前缀聚合
  const _isEtfCode = (c: string) => ['51', '15', '16', '56', '58'].includes((c || '').slice(0, 2));
  const _clsAgg = (list: any[]) => ({
    rounds: list.reduce((s: number, m: any) => s + (m.todayRounds || 0), 0),
    trades: list.reduce((s: number, m: any) => s + (m.todayTradeCount || 0), 0),
    amount: list.reduce((s: number, m: any) => s + (Number((m as any).todayAmount) || 0), 0),
    profit: list.reduce((s: number, m: any) => s + (displayTodayPnl(m) || 0), 0),
  });
  const _byClassBar = {
    stock: _clsAgg(_scopeMonitors.filter((m: any) => !_isEtfCode(m.stockCode))),
    etf: _clsAgg(_scopeMonitors.filter((m: any) => _isEtfCode(m.stockCode))),
  };
  const _fmtWan = (v: number) => v >= 10000 ? `${(v / 10000).toFixed(1)}万` : String(Math.round(v));
  // [2026-06-02 删除 observe 模式] tradeMode 简化为 sim/smtq 两态
  const { data: ifConfigForBadge } = trpc.interfaceConfig.get.useQuery(undefined, { staleTime: 30000 });
  const _tradeMode = (ifConfigForBadge as any)?.tradeMode || 'simulation';
  const _tradeModeBadge = _tradeMode === 'smtq'
    ? { label: '⚡实盘交易', variant: 'destructive' as const, className: 'bg-red-500/15 text-red-400 border-red-500/30 animate-pulse' }
    : { label: '模拟', variant: 'outline' as const, className: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };


  const handleAddFromWatchlist = (w: any) => { addMonitor.mutate({ stockCode: w.stockCode, stockName: w.stockName, addSource: "watchlist" }); };
  // [Claude 2026-05-07] 自选股删除 mutation（T+0交易页和T+0选股页双向同步）
  const removeWatchlist = trpc.watchlist.remove.useMutation({
    onSuccess: () => { refetchWatchlistData(); toast.success("已从自选股移除"); },
    onError: (e: any) => toast.error(e.message),
  });
  const refetchWatchlistData = () => { _refetchWatchlist(); };
  const handleManualAdd = () => {
    // [Claude 20260522 HOTFIX] 防御性 ?.trim() 双保险, 即使 state 异常变 undefined 也不崩
    if (!addStockCode?.trim()) { toast.error("请输入股票代码"); return; }
    // [Steven 2026-06-03 修复] 不再用 stockCode 当 stockName fallback (broker 持仓加入监控时会把 300589 当名称传)
    // server 会用腾讯 API 查 _realName, stockName 空时用 _realName 兜底 (新逻辑)
    // [R4属主 20260817] 模式随对话框选择传下去, 不再让后端默认值背锅
    addMonitor.mutate({ stockCode: addStockCode.trim(), stockName: addStockName?.trim() || '', addSource: "manual", brokerName: addBrokerName, marketDataSource: addMarketDataSource, tradeMode: addTradeMode } as any);
    setAddStockCode(""); setAddStockName(""); setShowAddDialog(false);
  };
  const handleSaveParams = (params: any) => { if (!editingMonitor) return; updateParams.mutate({ id: editingMonitor.id, ...params }); };

  const tradingTimePhase = engineStatus?.tradingTime?.phase || "closed";
  const tradingTimeMsg = engineStatus?.tradingTime?.message || "未知";
  const isTradingHours = engineStatus?.tradingTime?.isTradingHours || false;

  return (
    <div className="space-y-6">
      {/* [Steven 6/1 方案 A] 盘中信号失效预警 banner (14:30 终审 / 一键应用) */}
      <SignalRejectionAlertBanner />

      {/* [Steven 6/1 方案 C] LLM 实时辅助决策 banner */}
      <LLMAdviceBanner />

      {/* [20260812 Steven UX] 实盘/模拟双视图分段切换 (数据卡上方, 全页统计/监控/日志统一口径) */}
      <div className="flex justify-center">
        <div className="inline-flex items-center rounded-lg border border-border/60 bg-muted/20 p-1 gap-1">
          <button
            onClick={() => setViewMode('live')}
            className={`flex items-center gap-1.5 px-5 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              viewMode === 'live'
                ? 'bg-red-500/15 text-red-400 border border-red-500/40 shadow-sm'
                : 'text-muted-foreground hover:text-foreground border border-transparent'
            }`}
          >
            ⚡实盘
            <span className={`text-[10px] font-normal ${viewMode === 'live' ? 'text-red-400/80' : 'text-muted-foreground/60'}`}>{_liveCount}</span>
          </button>
          <button
            onClick={() => setViewMode('sim')}
            className={`flex items-center gap-1.5 px-5 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              viewMode === 'sim'
                ? 'bg-blue-500/15 text-blue-400 border border-blue-500/40 shadow-sm'
                : 'text-muted-foreground hover:text-foreground border border-transparent'
            }`}
          >
            模拟
            <span className={`text-[10px] font-normal ${viewMode === 'sim' ? 'text-blue-400/80' : 'text-muted-foreground/60'}`}>{_simCount}</span>
          </button>
        </div>
      </div>

      {/* [20260811 Steven UX v2] 统一吸顶数据卡: 盈利汇总+当日交易数据合并(原PC条/底部快捷条退役) */}
      {(() => {
        // [20260812 Steven UX] 统一吸顶卡统计口径跟随实盘/模拟视图 (顶部分段切换驱动, 原手动scope chip移除)
        const _mbToday = _scopeTotals.profit;
        // [20260812 Steven] 累计/当月=T0权威账本(engine_logs.tradePnl, 自20260729建账); totalProfit(旧成本价口径,含底仓浮盈)弃用
        const _mbTotal = _scopeMonitors.reduce((a: number, m: any) => a + Number(m.t0CumPnl || 0), 0);
        const _mbMonth = _scopeMonitors.reduce((a: number, m: any) => a + Number(m.t0MonthPnl || 0), 0);
        const _mbTrades = _scopeTotals.trades;
        const _mbRounds = _scopeTotals.rounds;
        const _mbAmount = _scopeTotals.amount;
        const _mbNetPct = _scopeTotals.amount > 0 ? _scopeTotals.profit / _scopeTotals.amount * 100 : 0;
        const _mbRun = _viewMonitors.filter((m: any) => m.isRunning === 1).length;
        const _mc = (v: number) => v >= 0 ? 'text-red-400' : 'text-emerald-400';
        return (
          <div className="sticky top-0 z-40 rounded-lg border border-border/50 bg-background/95 backdrop-blur px-3 py-2 shadow-md space-y-1.5">
            <div className="flex items-end gap-4 flex-wrap">
              <div><div className="text-[10px] text-muted-foreground leading-none mb-0.5">当日盈利</div><div className={"text-lg font-mono font-bold leading-none " + _mc(_mbToday)}>{_mbToday >= 0 ? '+' : ''}¥{Math.round(_mbToday)}</div></div>
              <div><div className="text-[10px] text-muted-foreground leading-none mb-0.5" title="引擎T0净盈亏权威账本(engine_logs), 自2026-07-29建账">累计T0净(7/29起)</div><div className={"text-lg font-mono font-bold leading-none " + _mc(_mbTotal)}>{_mbTotal >= 0 ? '+' : ''}¥{Math.round(_mbTotal)}</div></div>
              <div><div className="text-[10px] text-muted-foreground leading-none mb-0.5" title="引擎T0净盈亏权威账本(engine_logs)">当月T0净</div><div className={"text-lg font-mono font-bold leading-none " + _mc(_mbMonth)}>{_mbMonth >= 0 ? '+' : ''}¥{Math.round(_mbMonth)}</div></div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{_mbRun}/{_viewMonitors.length}运行</span>
              </div>
            </div>
            <div className="flex items-center gap-x-3 gap-y-1 text-[11px] font-mono flex-wrap text-muted-foreground">
              <span>{_mbTrades}笔·{_mbRounds}轮</span>
              <span>¥{_fmtWan(_mbAmount)}</span>
              <span className={_mc(_mbNetPct)}>{_mbNetPct >= 0 ? '+' : ''}{_mbNetPct.toFixed(2)}%</span>
              {_byClassBar.stock.trades > 0 && (
                <span className="flex items-center gap-1"><span className="px-1 rounded bg-blue-500/15 text-blue-400 text-[10px] font-sans">股</span>{_byClassBar.stock.rounds}轮·¥{_fmtWan(_byClassBar.stock.amount)}<span className={_mc(_byClassBar.stock.profit)}>{_byClassBar.stock.profit >= 0 ? '+' : ''}{Math.round(_byClassBar.stock.profit)}</span></span>
              )}
              {_byClassBar.etf.trades > 0 && (
                <span className="flex items-center gap-1"><span className="px-1 rounded bg-purple-500/15 text-purple-400 text-[10px] font-sans">ETF</span>{_byClassBar.etf.rounds}轮·¥{_fmtWan(_byClassBar.etf.amount)}<span className={_mc(_byClassBar.etf.profit)}>{_byClassBar.etf.profit >= 0 ? '+' : ''}{Math.round(_byClassBar.etf.profit)}</span></span>
              )}
            </div>
            <details>
                <summary className="text-xs text-primary cursor-pointer select-none">逐票明细(今/累) ▾</summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1.5">
                  {_scopeMonitors.map((m: any) => {
                    const _tp = displayTodayPnl(m) || 0;
                    const _cp = Number(m.t0CumPnl || 0);
                    return (
                      <div key={m.id} className="flex items-center justify-between rounded border border-border/40 bg-background/60 px-2 py-1 text-xs">
                        <span className="truncate mr-2">{m.stockName}<span className="text-muted-foreground/50 ml-1">{m.stockCode}</span></span>
                        <span className="font-mono whitespace-nowrap"><span className={_mc(_tp)}>{_tp >= 0 ? '+' : ''}{Math.round(_tp)}</span><span className="text-muted-foreground/50 mx-0.5">/</span><span className={_mc(_cp)}>{_cp >= 0 ? '+' : ''}{Math.round(_cp)}</span></span>
                      </div>
                    );
                  })}
                </div>
              </details>
          </div>
        );
      })()}

      {/* [Steven 2026-05-28] 熔断警告已移到 T+0 标题栏 (实盘观察右侧), 此处独立 Card 移除 */}

      {/* [Steven 2026-05-28] PC 端汇总条 (hidden lg:flex). 移动端用 MobileQuickBar (lg:hidden), 两者互斥不重复 */}
      {(() => {
        const _pcRunning = _viewMonitors.filter((m: any) => m.isRunning === 1).length;
        const _pcTotal = _viewMonitors.length;
        // [20260812 Steven UX] PC汇总条(隐藏)口径跟随实盘/模拟视图 (前端分组聚合)
        const _pcProfit = _scopeTotals.profit;
        const _pcTrades = _scopeTotals.trades;
        const _pcRounds = _scopeTotals.rounds;
        const _pcAmount = _scopeTotals.amount;
        const _pcNetPct = _scopeTotals.amount > 0 ? _scopeTotals.profit / _scopeTotals.amount * 100 : 0;
        const _pcProfitColor = _pcProfit >= 0 ? "text-red-400" : "text-emerald-400";
        const _pcNetColor = _pcNetPct >= 0 ? "text-red-400" : "text-emerald-400";
        return (
          <div className="hidden">{/* [20260811 UX v2] PC汇总条并入统一数据卡, 待清理窗移除 */}
            <div className="flex items-center gap-1.5">
              <Bot className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{_pcRunning}/{_pcTotal}</span>
              {_pcRunning > 0 && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
              <span className="text-xs text-muted-foreground ml-1">运行/监控</span>
            </div>
            <div className="h-4 w-px bg-border/50" />
            <div className="flex items-center gap-1.5">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">今日盈利</span>
              <span className={`text-sm font-mono font-semibold ${_pcProfitColor}`}>
                {_pcProfit >= 0 ? "+" : ""}¥{Math.round(_pcProfit)}
              </span>
            </div>
            {_pcTrades > 0 && (
              <>
                <div className="h-4 w-px bg-border/50" />
                <div className="flex items-center gap-1.5">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{_pcTrades}笔{_pcRounds > 0 ? ` · ${_pcRounds}轮` : ""}</span>
                </div>
              </>
            )}
            {_pcAmount > 0 && (
              <>
                <div className="h-4 w-px bg-border/50" />
                <div className="flex items-center gap-1.5 text-sm font-mono">
                  <span className="text-xs text-muted-foreground">成交额</span>
                  <span className="text-muted-foreground">¥{_pcAmount >= 10000 ? `${(_pcAmount / 10000).toFixed(1)}万` : Math.round(_pcAmount)}</span>
                  <span className={_pcNetColor}>{_pcNetPct >= 0 ? "+" : ""}{_pcNetPct.toFixed(3)}%</span>
                </div>
              </>
            )}
            {(_byClassBar.stock.trades > 0 || _byClassBar.etf.trades > 0) && (
              <>
                <div className="h-4 w-px bg-border/50" />
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="flex items-center gap-1">
                    <span className="px-1 rounded bg-blue-500/15 text-blue-400 text-[10px] font-sans">股</span>
                    <span className="text-muted-foreground">{_byClassBar.stock.rounds}轮·¥{_fmtWan(_byClassBar.stock.amount)}</span>
                    <span className={_byClassBar.stock.profit >= 0 ? "text-red-400" : "text-emerald-400"}>{_byClassBar.stock.profit >= 0 ? "+" : ""}{Math.round(_byClassBar.stock.profit)}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="px-1 rounded bg-purple-500/15 text-purple-400 text-[10px] font-sans">ETF</span>
                    <span className="text-muted-foreground">{_byClassBar.etf.rounds}轮·¥{_fmtWan(_byClassBar.etf.amount)}</span>
                    <span className={_byClassBar.etf.profit >= 0 ? "text-red-400" : "text-emerald-400"}>{_byClassBar.etf.profit >= 0 ? "+" : ""}{Math.round(_byClassBar.etf.profit)}</span>
                  </span>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* [20260811 Steven UX] 引擎状态面板已上移至页面标题行迷你徽章(点击展开/异常自动展开) */}

      {/* [Steven 2026-05-25] 重启历史入口已移到 EngineStatusPanel 主备节点详情底部, 独立卡片移除 */}

      {/* [v2.0 2026-04-29] 引擎主备状态面板 - 已禁用, 因为 EngineStatusPanel 已含相同信息 */}
      {/* <EngineHARolePanel /> */}

      {/* 今日做T适合度面板已移除 - 仅在 T+0 选股页保留 */}

      {/* [20260812 Steven UX] Agent 自动优化面板区块已撤销 */}
      {/* 交易异常诊断面板 (无异常时自动隐藏) */}
      <TradingDiagnosisPanel />

      {/* [Steven 2026-05-28 11:30] 5 个顶部卡片已移除. 数据在悬浮栏显示. */}
      {/* [Steven 2026-05-28 12:19] 熔断警告突出在第一行 (页面顶部), 见上方 EngineStatusPanel 之前 */}


      {/* Agent 监控列表 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                Agent 监控列表 ({_viewMonitors.length})
              </span>
              <Button variant="outline" size="sm" className="h-7 text-xs lg:hidden" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-3 w-3 mr-1" />添加
              </Button>
            </div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {/* [Claude 2026-05-18] 删除"从持仓同步"按钮: 重复功能, 09:23 cron 自动执行已覆盖 */}
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs text-gain border-gain/30 hover:bg-gain/10"
                    onClick={() => setShowBatchStartConfirm(true)} disabled={batchStartAll.isPending || batchRestartAllSmart.isPending || stoppedMonitors.length === 0}>
                    {(batchStartAll.isPending || batchRestartAllSmart.isPending) ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <PlayCircle className="h-3 w-3 mr-1" />}全部启动
                  </Button>
                </TooltipTrigger><TooltipContent><p className="text-xs">一键启动所有未运行的 Agent</p></TooltipContent></Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs text-loss border-loss/30 hover:bg-loss/10"
                    onClick={() => {
                      if (unfilledMonitors.length > 0) {
                        setShowBatchStopConfirm(true);
                      } else {
                        batchStopAll.mutate({ monitorIds: runningMonitors.map((m: any) => m.id) });
                      }
                    }} disabled={batchStopAll.isPending || runningCount === 0}>
                    {batchStopAll.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <StopCircle className="h-3 w-3 mr-1" />}全部停止
                  </Button>
                </TooltipTrigger><TooltipContent><p className="text-xs">一键停止所有运行中的 Agent</p></TooltipContent></Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => setShowBatchTemplateDialog(true)} disabled={(monitors || []).length === 0}>
                    <Wand2 className="h-3 w-3 mr-1" />批量模板
                  </Button>
                </TooltipTrigger><TooltipContent><p className="text-xs">将参数模板批量应用到所有股票</p></TooltipContent></Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs text-amber-400 border-amber-400/30 hover:bg-amber-400/10"
                    onClick={() => setShowDailyResetConfirm(true)} disabled={dailyReset.isPending || (monitors || []).length === 0 || runningCount > 0}>
                    {dailyReset.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}重置日内
                  </Button>
                </TooltipTrigger><TooltipContent><p className="text-xs">手动重置所有Agent的日内状态（方向/基准价/盈亏/轮次），需先停止所有引擎</p></TooltipContent></Tooltip>
              </TooltipProvider>
              <Button variant="outline" size="sm" className="h-7 text-xs hidden lg:flex" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-3 w-3 mr-1" />手动添加
              </Button>
            </div>
          </CardTitle>
          <CardDescription className="text-xs">
            每只股票可独立设置T+0交易参数、启停引擎、查看引擎状态、运行日志和交易记录
          </CardDescription>
          {/* 筛选标签 */}
          <div className="flex items-center gap-1 mt-2 px-4 lg:px-6 flex-wrap">
            {[
              { key: 'all' as const, label: '全部', count: _viewMonitors.length },
              { key: 'running' as const, label: '运行中', count: runningCount, color: 'text-gain' },
              { key: 'unfilled' as const, label: '未做平', count: unfilledMonitors.length, color: 'text-purple-400' },
              { key: 'stopped' as const, label: '已停止', count: stoppedCount, color: 'text-muted-foreground' },
            ].map(f => (
              <button key={f.key} onClick={() => setMonitorFilter(f.key)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  monitorFilter === f.key
                    ? 'bg-primary/15 text-primary border border-primary/30'
                    : 'bg-accent/30 text-muted-foreground hover:bg-accent/50 border border-transparent'
                }`}>
                {f.label}
                {f.count > 0 && (
                  <span className={`ml-1 ${monitorFilter === f.key ? 'text-primary' : (f.color || 'text-muted-foreground')}`}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {(monitors || []).length > 0 ? (
            <>
              {/* 筛选结果为空提示 */}
              {filteredMonitors.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  当前筛选条件下没有股票
                  <button className="ml-2 text-primary hover:underline" onClick={() => setMonitorFilter('all')}>查看全部</button>
                </div>
              )}
              {/* 桌面端表格视图 */}
              {filteredMonitors.length > 0 && (
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs">代码</TableHead>
                      <TableHead className="text-xs">名称</TableHead>
                      <TableHead className="text-xs">券商/行情</TableHead>
                      <TableHead className="text-xs">模板</TableHead>
                      <TableHead className="text-xs text-center">启停</TableHead>
                      <TableHead className="text-xs">状态</TableHead>
                      <TableHead className="text-xs">方向</TableHead>
                      <TableHead className="text-xs text-right">基准价</TableHead>
                      <TableHead className="text-xs text-right">持仓</TableHead>
                      <TableHead className="text-xs text-right">今日盈利</TableHead>
                      <TableHead className="text-xs text-right">轮次</TableHead>
                      <TableHead className="text-xs text-center">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMonitors.map((m: any) => {
                      const isStarting = startingMonitorIds.has(m.id);
                      const effectiveEngineStatus = m.isRunning !== 1 ? "stopped" : (m.engineStatus || "idle"); const statusInfo = isStarting ? ENGINE_STATUS_MAP.starting : (ENGINE_STATUS_MAP[effectiveEngineStatus] || ENGINE_STATUS_MAP.idle);
                      const StatusIcon = statusInfo.icon;
                      return (
                        <TableRow key={m.id} className={`border-border/30 hover:bg-accent/20 ${m.isPaused === 1 ? "bg-yellow-500/5" : ""}`}>
                          <TableCell className="text-xs font-mono">{m.stockCode}</TableCell>
                          <TableCell className="text-xs font-medium">
                            <div className="flex items-center gap-1">
                              {m.stockName}
                              {m.recentAutoOptimize && (
                                <span title={`Agent 自动调整: ${m.recentAutoOptimize.reason}`}>
                                  <Zap className="h-3 w-3 text-amber-400" />
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {String((m as any).tradeMode || '').toLowerCase() === 'live'
                                ? <Badge variant="outline" className="text-[9px] px-1 border-red-500/60 text-red-400 bg-red-500/10 font-semibold">实盘</Badge>
                                : <Badge variant="outline" className="text-[9px] px-1 border-blue-500/30 text-blue-400/70">模拟</Badge>}
                              {(() => {
                                const isRealPos = (positionsData || []).some((p: any) => p.stockCode === m.stockCode);
                                if (isRealPos) {
                                  return (
                                    <Badge variant="outline" className={`text-[9px] px-1 ${m.brokerName === 'haitung' ? 'border-cyan-500/50 text-cyan-400' : 'border-amber-500/50 text-amber-400'}`}>
                                      {m.brokerName === 'haitung' ? '海通' : '兴业'}
                                    </Badge>
                                  );
                                }
                                return (
                                  <Badge variant="outline" className="text-[9px] px-1 border-gray-500/50 text-gray-400">
                                    模拟
                                  </Badge>
                                );
                              })()}
                              <Badge variant="outline" className={`text-[9px] px-1 ${m.marketDataSource === 'mqtt_l2' ? 'border-cyan-500/50 text-cyan-400' : m.marketDataSource === 'nats_l2' ? 'border-purple-500/50 text-purple-400' : 'border-gray-500/50 text-gray-400'}`}>
                                {m.marketDataSource === 'mqtt_l2' ? 'L2·MQTT' : m.marketDataSource === 'nats_l2' ? 'L2·NATS' : m.marketDataSource === 'xtquant' ? 'L1·XQ' : 'L1·TX'}
                              </Badge>
                              <Badge variant="outline" className={`text-[9px] px-1 ${m.addSource === 'position' ? 'border-green-500/50 text-green-400' : m.addSource === 'test' ? 'border-purple-500/50 text-purple-400' : 'border-gray-500/50 text-gray-400'}`} title={m.addSource === 'position' ? '实盘持仓（可真实下单）' : m.addSource === 'test' ? '高频测试盘（不计入策略统计）' : '手工添加（仅模拟）'}>
                                {m.addSource === 'position' ? '💰持仓' : m.addSource === 'watchlist' ? '📋观察' : m.addSource === 'screening' ? '🔍选股' : m.addSource === 'test' ? '🧪测试' : '🧪模拟'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => { const tl = templateLabel(m.activeTemplate, { short: true }); return (
                            <Badge variant="outline" className={`text-[9px] px-1.5 ${tl.color}`}>{tl.label}</Badge>
                            ); })()}
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch checked={m.isRunning === 1} onCheckedChange={(checked) => {
                              if (checked) { setStartConfirmMonitor(m); } else {
                                if (m.engineStatus === 'waiting_fill') {
                                  setStopConfirmMonitor(m);
                                } else {
                                  toggleRunning.mutate({ id: m.id, isRunning: false });
                                }
                              }
                            }} className="scale-75" />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Badge className={`text-[9px] px-1.5 ${statusInfo.color}`}>
                                <StatusIcon className="h-2.5 w-2.5 mr-0.5" />{statusInfo.label}
                              </Badge>
                              {m.isPaused === 1 && (
                                <Button variant="ghost" size="sm" className="h-5 px-1 text-[9px] text-yellow-400"
                                  onClick={() => resumeAgent.mutate({ id: m.id })}>
                                  <RotateCcw className="h-2.5 w-2.5 mr-0.5" />恢复
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(m.currentDirection === "up" || m.currentDirection === "long") ? (
                              <Badge className="text-[9px] bg-gain/20 text-gain"><ArrowUp className="h-2.5 w-2.5 mr-0.5" />涨</Badge>
                            ) : (m.currentDirection === "down" || m.currentDirection === "short") ? (
                              <Badge className="text-[9px] bg-loss/20 text-loss"><ArrowDown className="h-2.5 w-2.5 mr-0.5" />跌</Badge>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono">{m.currentBasis ? `¥${m.currentBasis.toFixed(2)}` : "-"}</TableCell>
                          <TableCell className="text-xs text-right">
                            {(() => { const h = displayHolding(m, _tradeMode); return h > 0 ? <span className="text-primary font-medium">{h}</span> : <span className="text-muted-foreground">0</span>; })()}
                          </TableCell>
                          <TableCell className={`text-xs text-right font-mono font-medium ${(displayTodayPnl(m) || 0) >= 0 ? "text-gain" : "text-loss"}`}>
                            {(displayTodayPnl(m) || 0) >= 0 ? "+" : ""}¥{(displayTodayPnl(m) || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-xs text-right">
                            {m.todayRounds || 0}/{m.maxDailyRounds || engineStatus?.defaultParams?.maxDailyRounds || 5}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-0.5">
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setDetailMonitor(m); setShowDetailDialog(true); }}>
                                  <Gauge className="h-3.5 w-3.5 text-blue-400" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">引擎状态</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setLogMonitor(m); setShowLogDialog(true); }}>
                                  <Terminal className="h-3.5 w-3.5 text-cyan-400" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">运行日志</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingMonitor(m); setShowParamDialog(true); }}>
                                  <Settings2 className="h-3.5 w-3.5 text-primary" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">交易参数</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setRecordMonitor(m); setShowRecordDialog(true); }}>
                                  <History className="h-3.5 w-3.5 text-primary" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">交易记录</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setReplayMonitor(m); setShowReplayDialog(true); }}>
                                  <Film className="h-3.5 w-3.5 text-emerald-400" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">引擎回放</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setOptimizeMonitor(m); setOptimizeStep(1); setOptimizePreview(null); }}>
                                  <Zap className="h-3.5 w-3.5 text-amber-400" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">自动优化</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setAtrMonitor(m); setShowAtrDialog(true); }}>
                                  <Brain className="h-3.5 w-3.5 text-purple-400" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">ATR学习</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setCompareMonitor(m); setShowCompareDialog(true); }}>
                                  <GitCompare className="h-3.5 w-3.5 text-orange-400" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">回测对比</p></TooltipContent></Tooltip></TooltipProvider>
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); console.log('[DELETE] click delete btn, monitor:', m.id, m.stockName, 'isRunning:', m.isRunning); setDeleteConfirmMonitor(m); setDeleteStep(1); }}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button></TooltipTrigger><TooltipContent><p className="text-xs">删除</p></TooltipContent></Tooltip></TooltipProvider>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              )}

              {/* 移动端卡片视图 */}
              {filteredMonitors.length > 0 && (
              <div className="lg:hidden space-y-2 p-3">
                {filteredMonitors.map((m: any) => {
                   const isStarting = startingMonitorIds.has(m.id);
                   const effectiveEngineStatus2 = m.isRunning !== 1 ? "stopped" : (m.engineStatus || "idle"); const statusInfo = isStarting ? ENGINE_STATUS_MAP.starting : (ENGINE_STATUS_MAP[effectiveEngineStatus2] || ENGINE_STATUS_MAP.idle);
                   const StatusIcon = statusInfo.icon;
                  return (
                    <SwipeCard
                      key={m.id}
                      leftAction={{
                        label: "删除",
                        icon: <Trash2 className="h-3.5 w-3.5" />,
                        color: "bg-red-500",
                        onAction: () => { console.log('[DELETE] swipe delete, monitor:', m.id, m.stockName); setDeleteConfirmMonitor(m); setDeleteStep(1); },
                      }}
                      rightAction={{
                        label: m.isRunning === 1 ? "停止引擎" : "启动引擎",
                        icon: m.isRunning === 1 ? <StopCircle className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />,
                        color: m.isRunning === 1 ? "bg-amber-500" : "bg-emerald-500",
                        onAction: () => {
                          if (m.isRunning === 1) {
                            if (m.engineStatus === 'waiting_fill') {
                              setStopConfirmMonitor(m);
                            } else {
                              toggleRunning.mutate({ id: m.id, isRunning: false });
                            }
                          } else {
                            setStartConfirmMonitor(m);
                          }
                        },
                      }}
                    >
                    <div className={`rounded-lg border border-border/50 p-3 space-y-2.5 ${m.isPaused === 1 ? "bg-yellow-500/5 border-yellow-500/20" : "bg-card/50"}`}>
                      {/* [Claude 2026-05-08] 代码+名称分两行（手机端长名字更清爽） */}
                      <div className="space-y-0.5">
                        <div className="text-[11px] font-mono text-muted-foreground">{m.stockCode}</div>
                        <div className="text-base font-semibold leading-tight">{m.stockName}
                          {(liveQuotes as any)?.[m.stockCode] && (
                            <span className={`ml-2 text-sm font-mono ${((liveQuotes as any)[m.stockCode].pct >= 0) ? "text-red-400" : "text-green-500"}`}>
                              {(liveQuotes as any)[m.stockCode].price}
                              <span className="ml-1">{((liveQuotes as any)[m.stockCode].pct >= 0 ? "+" : "") + (liveQuotes as any)[m.stockCode].pct.toFixed(2)}%</span>
                            </span>
                          )}</div>
                      </div>
                      {/* 第二行：所有 badge 标签（实时T0/Agent调整/券商/数据源/状态/模板/适合度/轮次等） */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {String((m as any).tradeMode || '').toLowerCase() === 'live'
                            ? <Badge variant="outline" className="text-[9px] px-1 border-red-500/60 text-red-400 bg-red-500/10 font-semibold">实盘</Badge>
                            : <Badge variant="outline" className="text-[9px] px-1 border-blue-500/30 text-blue-400/70">模拟</Badge>}
                          {/* [20260707 #11] 夜间真tick适合度评级徽标 (ok绿/watch黄/exit红) */}
                          {(m as any).fitnessNightly && (m as any).fitnessNightly.tag !== 'na' && (
                            <span
                              title={`夜评(${(m as any).fitnessNightly.date}): ${(m as any).fitnessNightly.detail}`}
                              className={`text-[9px] px-1 py-0.5 rounded border ${
                                (m as any).fitnessNightly.tag === 'ok' ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10'
                                : (m as any).fitnessNightly.tag === 'watch' ? 'text-amber-400 border-amber-500/40 bg-amber-500/10'
                                : 'text-red-400 border-red-500/40 bg-red-500/10'
                              }`}
                            >
                              {(m as any).fitnessNightly.tag === 'ok' ? 'T0适合' : (m as any).fitnessNightly.tag === 'watch' ? '今日观望' : '建议退出'}
                            </span>
                          )}
                          {m.recentAutoOptimize && (
                            <span title={`Agent 自动调整: ${m.recentAutoOptimize.reason}`}>
                              <Zap className="h-3 w-3 text-amber-400" />
                            </span>
                          )}
                          {(() => {
                            const isRealPos = (positionsData || []).some((p: any) => p.stockCode === m.stockCode);
                            if (isRealPos) {
                              return (
                                <Badge variant="outline" className={`text-[9px] px-1 ${m.brokerName === 'haitung' ? 'border-cyan-500/50 text-cyan-400' : 'border-amber-500/50 text-amber-400'}`}>
                                  {m.brokerName === 'haitung' ? '海通' : '兴业'}
                                </Badge>
                              );
                            }
                            return (
                              <Badge variant="outline" className="text-[9px] px-1 border-gray-500/50 text-gray-400">
                                模拟
                              </Badge>
                            );
                          })()}
                          <Badge variant="outline" className={`text-[9px] px-1 ${m.marketDataSource === 'mqtt_l2' ? 'border-cyan-500/50 text-cyan-400' : m.marketDataSource === 'nats_l2' ? 'border-purple-500/50 text-purple-400' : 'border-gray-500/50 text-gray-400'}`}>
                            {m.marketDataSource === 'mqtt_l2' ? 'MQTT' : m.marketDataSource === 'nats_l2' ? 'NATS' : 'L1'}
                          </Badge>
                          {/* [Steven 2026-06-04] T+1 持仓过夜功能已移除 */}
                          <Tooltip><TooltipTrigger asChild>
                            <Badge className={`text-[9px] px-1.5 ${statusInfo.color} cursor-help`}>
                              <StatusIcon className="h-2.5 w-2.5 mr-0.5" />{statusInfo.label}
                            </Badge>
                          </TooltipTrigger>{(statusInfo as any).tip && <TooltipContent side="top" className="max-w-xs text-xs"><p>{(statusInfo as any).tip}</p></TooltipContent>}</Tooltip>
                          {(() => { const tl = templateLabel(m.activeTemplate, { short: true }); return (
                          <Badge variant="outline" className={`text-[9px] px-1 ${tl.color}`}>{tl.label}</Badge>
                          ); })()}
                          <StockSuitabilityBadge stockCode={m.stockCode} monitorId={m.id} />
                          <Tooltip><TooltipTrigger asChild>
                            <Badge variant="outline" className={`text-[9px] px-1 cursor-help ${
                              (m.todayRounds || 0) >= (m.maxDailyRounds || engineStatus?.defaultParams?.maxDailyRounds || 5)
                                ? 'border-red-500/50 text-red-400'
                                : (m.todayRounds || 0) > 0
                                  ? 'border-blue-500/50 text-blue-400'
                                  : 'border-muted-foreground/30 text-muted-foreground'
                            }`}>
                              🔄{m.todayRounds || 0}/{m.maxDailyRounds || engineStatus?.defaultParams?.maxDailyRounds || 5}
                            </Badge>
                          </TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>今日已完成轮次/当日最大轮次。达到上限后引擎自动停止，次日重置。</p></TooltipContent></Tooltip>
                          {m.isPaused === 1 && (
                            <Button variant="ghost" size="sm" className="h-5 px-1 text-[9px] text-yellow-400"
                              onClick={() => resumeAgent.mutate({ id: m.id })}>
                              <RotateCcw className="h-2.5 w-2.5 mr-0.5" />恢复
                            </Button>
                          )}
                        </div>
                        <Switch checked={m.isRunning === 1} onCheckedChange={(checked) => {
                          if (checked) { setStartConfirmMonitor(m); } else {
                            if (m.engineStatus === 'waiting_fill') {
                              setStopConfirmMonitor(m);
                            } else {
                              toggleRunning.mutate({ id: m.id, isRunning: false });
                            }
                          }
                        }} className="scale-75" />
                      </div>

                      {/* 第二行：核心数据 */}
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <Tooltip><TooltipTrigger asChild><div className="text-[10px] text-muted-foreground cursor-help">方向 <Info className="h-2 w-2 inline" /></div></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当日T+0交易方向：「上涨↑」表示今天股价相比开盘价偏强，走场景一（先卖后买）；「下跌↓」则走场景二（先买后卖）。方向一旦确定，当日不切换。</p></TooltipContent></Tooltip>
                          <div className="mt-0.5">
                            {(m.currentDirection === "up" || m.currentDirection === "long") ? (
                              <Badge className="text-[9px] bg-gain/20 text-gain"><ArrowUp className="h-2.5 w-2.5 mr-0.5" />涨</Badge>
                            ) : (m.currentDirection === "down" || m.currentDirection === "short") ? (
                              <Badge className="text-[9px] bg-loss/20 text-loss"><ArrowDown className="h-2.5 w-2.5 mr-0.5" />跌</Badge>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">—</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <Tooltip><TooltipTrigger asChild><div className="text-[10px] text-muted-foreground cursor-help">基准价 <Info className="h-2 w-2 inline" /></div></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当轮交易的参考价格：首笔交易的成交价，用于计算配对目标价格（偏移一定比例挂限价单）。每轮完成后更新为新的成交价。</p></TooltipContent></Tooltip>
                          <div className="text-xs font-mono mt-0.5">{m.currentBasis ? `¥${m.currentBasis.toFixed(2)}` : "-"}</div>
                        </div>
                        <div>
                          <Tooltip><TooltipTrigger asChild><div className="text-[10px] text-muted-foreground cursor-help">持仓 <Info className="h-2 w-2 inline" /></div></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>{_tradeMode === 'simulation' ? '模拟持仓 (每日开盘统一 10000 起点, 盘中虚拟买卖更新)' : '真实账户持仓 (broker 同步)'}。T+0引擎在此基础上做轮动操作。</p></TooltipContent></Tooltip>
                          <div className="text-xs mt-0.5">
                            {(() => { const h = displayHolding(m, _tradeMode); return h > 0 ? <span className="text-primary font-medium">{h}</span> : <span className="text-muted-foreground">0</span>; })()}
                          </div>
                        </div>
                        <div>
                          <Tooltip><TooltipTrigger asChild><div className="text-[10px] text-muted-foreground cursor-help">今日盈利{(m.dailyProfitTarget > 0) ? "/目标" : ""} <Info className="h-2 w-2 inline" /></div></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p>当日T+0轮动交易的累计净盈亏。{m.dailyProfitTarget > 0 ? `目标 ¥${Number(m.dailyProfitTarget).toFixed(0)}, 达成 ${displayTodayPnl(m) > 0 ? Math.round(displayTodayPnl(m) / m.dailyProfitTarget * 100) : 0}%` : '在参数设置里可设盈利目标'}</p></TooltipContent></Tooltip>
                          <div className={`text-xs font-mono font-medium mt-0.5 ${(displayTodayPnl(m) || 0) >= 0 ? "text-gain" : "text-loss"}`}>
                            {(displayTodayPnl(m) || 0) >= 0 ? "+" : ""}¥{(displayTodayPnl(m) || 0).toFixed(2)}
                            {m.dailyProfitTarget > 0 && (
                              <span className="text-[10px] text-muted-foreground font-normal ml-1">/ ¥{Number(m.dailyProfitTarget).toFixed(0)}</span>
                            )}
                          </div>
                          {m.dailyProfitTarget > 0 && displayTodayPnl(m) > 0 && (
                            <div className="h-1 bg-muted/30 rounded-full mt-1 overflow-hidden">
                              <div 
                                className={`h-full transition-all ${displayTodayPnl(m) >= m.dailyProfitTarget ? 'bg-gain' : displayTodayPnl(m) >= m.dailyProfitTarget * 0.8 ? 'bg-warning' : 'bg-primary'}`}
                                style={{ width: `${Math.min(100, displayTodayPnl(m) / m.dailyProfitTarget * 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* [Claude 2026-05-14] AI 建议条 - 显示最新自动优化记录 */}
                      <AIAdviceBar monitorId={m.id} />

                      {/* 第三行：操作按钮 */}
                      <div className="flex items-center gap-1 flex-wrap border-t border-border/30 pt-2">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setDetailMonitor(m); setShowDetailDialog(true); }}>
                          <Gauge className="h-3 w-3 mr-1 text-blue-400" />状态
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setLogMonitor(m); setShowLogDialog(true); }}>
                          <Terminal className="h-3 w-3 mr-1 text-cyan-400" />日志
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setEditingMonitor(m); setShowParamDialog(true); }}>
                          <Settings2 className="h-3 w-3 mr-1 text-primary" />参数
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setRecordMonitor(m); setShowRecordDialog(true); }}>
                          <History className="h-3 w-3 mr-1 text-primary" />记录
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setReplayMonitor(m); setShowReplayDialog(true); }}>
                          <Film className="h-3 w-3 mr-1 text-emerald-400" />回放
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setOptimizeMonitor(m); setOptimizeStep(1); setOptimizePreview(null); }} title="基于实时打分自动调整模板/参数">
                          <Zap className="h-3 w-3 mr-1 text-amber-400" />优化
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setAtrMonitor(m); setShowAtrDialog(true); }}>
                          <Brain className="h-3 w-3 mr-1 text-purple-400" />ATR
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => { setCompareMonitor(m); setShowCompareDialog(true); }}>
                          <GitCompare className="h-3 w-3 mr-1 text-orange-400" />回测
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); console.log('[DELETE] mobile click delete btn, monitor:', m.id, m.stockName); setDeleteConfirmMonitor(m); setDeleteStep(1); }}>
                          <Trash2 className="h-3 w-3 mr-1" />删除
                        </Button>
                      </div>
                      {/* 操作建议已移至独立Tab，不在卡片内重复展示 */}
                    </div>
                    </SwipeCard>
                  );
                })}
              </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bot className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">暂无 Agent 监控股票</p>
              <p className="text-xs mt-1 opacity-60">从下方自选股列表点击添加，或手动添加股票代码</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* [Steven 2026-05-26 23:30 修] 持仓股票 - 改用 broker 真实持仓 (brokerPositions, 来福 broker_account_sync.py 每 5min 拉真账户)
          原 bug:
            1. 用 agent_monitors.currentHolding → 删监控就空了, 不能反映真实账户
            2. "X 只今日实盘成交" badge 在 sim/observe 模式下不准 (没真下单)
          修复:
            - 数据源换 dashboardData.brokerPositions (broker 真实持仓快照)
            - 未在 monitors 中的股票显示"加入监控"按钮
            - 删除"今日实盘成交" badge (实盘观察期间不下实单, 这个不准确) */}
      {(() => {
        // [Steven 2026-06-03 修复] 之前 sim/observe 不显示 broker 持仓卡片 (假定显示 monitor 模拟持仓重复)
        // 但删 observe 后 SIM 模式下 monitor 显示固定 10000, 用户看不到真实账户持仓 → 抱怨"持仓股票列表看不到"
        // 修复: 任何模式下只要 brokerPositions 有数据就显示 (只读快照, 无副作用)
        const brokerPositions: any[] = (dashboardData as any)?.brokerPositions || [];
        const brokerSyncedAt: string | null = (dashboardData as any)?.brokerSyncedAt || null;
        if (brokerPositions.length === 0) return null;
        // [R4属主 20260817] 身份判定已收敛到顶层单点(monitorsByCode / monitorMapTop /
        //   monitorModeLabel), 此处不再本地重建, 免得各块口径漂移。
        const monitorMap = monitorMapTop;
        const unmonitoredCount = brokerPositions.filter((p: any) => !monitorCodes.has(p.stockCode)).length;
        return (
          <Card className="bg-card border-border/50 border-dashed">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2 flex-wrap">
                <Wallet className="h-4 w-4 text-amber-400" />
                <span>持仓股票</span>
                <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">共 {brokerPositions.length} 只</Badge>
                {unmonitoredCount > 0 && (
                  <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-500/30">{unmonitoredCount} 只未监控</Badge>
                )}
                <span className="text-[10px] text-muted-foreground font-normal ml-auto">
                  来自券商真账户{brokerSyncedAt ? ` · ${new Date(brokerSyncedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Shanghai' })}` : ''}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="space-y-2">
                {brokerPositions.map((p: any) => {
                  const inMonitor = monitorCodes.has(p.stockCode);
                  const m = monitorMap.get(p.stockCode);
                  const profit = Number(p.profit || 0);
                  const profitColor = profit > 0 ? 'text-emerald-400' : profit < 0 ? 'text-red-400' : 'text-muted-foreground';
                  return (
                    <div key={p.stockCode}
                         className={`py-2.5 px-3 rounded-lg border ${
                           inMonitor ? 'bg-muted/20 border-border/30' : 'bg-blue-500/5 border-blue-500/30'
                         }`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-semibold flex-1 min-w-0 truncate">
                          {(m as any)?.stockName || p.stockName || p.stockCode}
                        </span>
                        <span className="text-[10px] text-muted-foreground font-mono">{p.stockCode}</span>
                        {inMonitor ? (
                          <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/40 px-1.5">
                            <Check className="h-2.5 w-2.5 mr-0.5" />已监控·{monitorModeLabel(p.stockCode)}
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] px-2 border-primary/40 text-primary hover:bg-primary/10"
                            onClick={() => {
                              setAddStockCode(p.stockCode || '');
                              setAddStockName((m as any)?.stockName || '');
                              setShowAddDialog(true);
                            }}
                          >
                            <Plus className="h-2.5 w-2.5 mr-0.5" />加入监控
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] flex-wrap">
                        <span className="text-primary font-mono">{(p.volume || 0).toLocaleString()} 股</span>
                        {p.availableVolume !== undefined && p.availableVolume !== p.volume && (
                          <span className="text-muted-foreground">可用 {(p.availableVolume || 0).toLocaleString()}</span>
                        )}
                        {p.avgPrice > 0 && (
                          <span className="text-muted-foreground">成本 ¥{Number(p.avgPrice).toFixed(2)}</span>
                        )}
                        {p.marketValue > 0 && (
                          <span className="text-muted-foreground">市值 ¥{Math.round(Number(p.marketValue)).toLocaleString()}</span>
                        )}
                        {profit !== 0 && (
                          <span className={profitColor}>
                            {profit > 0 ? '+' : ''}¥{profit.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* [原] 持仓股票 - 全部显示，已监控的标记出来 [Claude 2026-05-07] (保留备用, 触发条件改成无 monitors 时才显示) */}
      {(!monitors || monitors.length === 0) && positionsData && positionsData.length > 0 && (() => {
        // [R4属主 20260817] 原此处再建一次 monitorCodes 并遮蔽顶层同名量, 口径易漂移; 已删, 用顶层。
        const unmonitoredCount = positionsData.filter((p: any) => !monitorCodes.has(p.stockCode)).length;
        return (
          <Card className="bg-card border-border/50 border-dashed">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2 flex-wrap">
                <Wallet className="h-4 w-4 text-amber-400" />
                <span>持仓股票</span>
                <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">共 {positionsData.length} 只</Badge>
                {unmonitoredCount > 0 && (
                  <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-500/30">{unmonitoredCount} 只未监控</Badge>
                )}
                <span className="text-[10px] text-muted-foreground font-normal ml-auto">来自券商真实持仓</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="space-y-2">
                {positionsData.map((p: any) => {
                  const pnlPct = p.profitLossPct ?? 0;
                  const pnlColor = pnlPct > 0 ? 'text-emerald-400' : pnlPct < 0 ? 'text-red-400' : 'text-muted-foreground';
                  const inMonitor = monitorCodes.has(p.stockCode);
                  return (
                    <div key={p.stockCode}
                         className={`py-2.5 px-3 rounded-lg border transition-colors ${
                           inMonitor
                             ? 'bg-emerald-500/5 border-emerald-500/30'
                             : 'bg-muted/20 border-border/30 hover:bg-muted/30'
                         }`}>
                      {/* 第一行：股票名 + 代码 + 评分 */}
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-semibold flex-1 min-w-0 truncate">
                          {p.stockName || p.stockCode}
                        </span>
                        <span className="text-[11px] font-mono text-muted-foreground shrink-0">{p.stockCode}</span>
                      </div>
                      {/* 第二行：持仓信息 + 盈亏 + 监控状态/按钮 */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] text-muted-foreground">
                          {(p.quantity || 0).toLocaleString()} 股
                        </span>
                        {p.avgCost > 0 && (
                          <span className="text-[11px] text-muted-foreground">
                            · 成本 ¥{p.avgCost.toFixed(2)}
                          </span>
                        )}
                        {pnlPct !== 0 && (
                          <span className={`text-[11px] font-medium ${pnlColor}`}>
                            · {pnlPct > 0 ? '+' : ''}{pnlPct.toFixed(2)}%
                          </span>
                        )}
                        {inMonitor ? (
                          <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/40 px-1.5 ml-auto shrink-0">
                            <Check className="h-2.5 w-2.5 mr-0.5" />已监控·{monitorModeLabel(p.stockCode)}
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] px-2 border-primary/40 text-primary hover:bg-primary/10 ml-auto shrink-0"
                            onClick={() => {
                              // [Claude 20260522 HOTFIX] 防 undefined: 异常持仓 p.stockCode 可能缺失,导致渲染崩溃 U.trim()
                              setAddStockCode(p.stockCode || '');
                              setAddStockName(p.stockName || '');
                              setShowAddDialog(true);
                            }}
                          >
                            <Plus className="h-2.5 w-2.5 mr-0.5" />添加监控
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

            {/* 引擎状态消息区域 */}
      {_viewMonitors.some((m: any) => m.isRunning === 1 && m.engineMessage) && (
        <Card className="bg-card/50 border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium flex items-center gap-2">
              <CircleDot className="h-3.5 w-3.5 text-primary animate-pulse" />
              引擎实时消息
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {_viewMonitors.filter((m: any) => m.isRunning === 1 && m.engineMessage).map((m: any) => (
                <div key={m.id} className="flex items-start gap-2 text-xs p-2 rounded bg-muted/20">
                  <Badge variant="outline" className="text-[9px] shrink-0 mt-0.5">{m.stockName}</Badge>
                  <span className="text-[9px] text-muted-foreground/60 shrink-0 mt-0.5 font-mono">
                    {m.engineMessageAt ? formatBJ(m.engineMessageAt, "dateTime") : formatBJ(new Date(), "time")}
                  </span>
                  <span className="text-muted-foreground break-words flex-1">{m.engineMessage}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 服务器资源监控卡片 */}
      {resourcesData?.connected && resourcesData.data && (() => {
        const res = resourcesData.data;
        const cpuPct = res.cpu?.percent ?? 0;
        const memPct = res.memory?.percent ?? 0;
        const bwDown = res.network?.downloadMbps ?? 0;
        const bwUp = res.network?.uploadMbps ?? 0;
        const bwLimit = res.network?.bandwidthLimitMbps ?? 0;
        const bwUsagePct = bwLimit > 0 ? Math.min(100, ((bwDown + bwUp) / bwLimit) * 100) : 0;
        const diskPct = res.disk?.percent ?? 0;
        const getColor = (pct: number) => pct >= 90 ? "text-destructive" : pct >= 70 ? "text-amber-400" : "text-gain";
        const getBarColor = (pct: number) => pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-amber-400" : "bg-gain";
        return (
          <Card className="bg-card/50 border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Server className="h-3.5 w-3.5 text-primary" />
                  服务器资源
                </span>
                <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5 text-muted-foreground" onClick={() => { setBandwidthInput(bwLimit > 0 ? String(bwLimit) : "3"); setShowResourceConfigDialog(true); }}>
                  <Settings2 className="h-3 w-3 mr-1" />{bwLimit > 0 ? `带宽: ${bwLimit}M` : '资源设置'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {cpuPct === 0 && memPct === 0 && diskPct === 0 && bwDown === 0 && bwUp === 0 && (
                <div className="text-[10px] text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded p-2 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  <span>资源数据全为0，请确认引擎端已安装 psutil：<code className="bg-muted px-1 rounded">pip install psutil</code>，安装后重启引擎生效</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* CPU */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="flex items-center gap-1 text-muted-foreground"><Cpu className="h-3 w-3" />CPU</span>
                    <span className={`font-mono font-medium ${getColor(cpuPct)}`}>{cpuPct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(cpuPct)}`} style={{ width: `${cpuPct}%` }} />
                  </div>
                  {res.cpu?.cores && <div className="text-[9px] text-muted-foreground/60">{res.cpu.cores} 核</div>}
                </div>
                {/* 内存 */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="flex items-center gap-1 text-muted-foreground"><MemoryStick className="h-3 w-3" />内存</span>
                    <span className={`font-mono font-medium ${getColor(memPct)}`}>{memPct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(memPct)}`} style={{ width: `${memPct}%` }} />
                  </div>
                  {res.memory?.totalMB != null && <div className="text-[9px] text-muted-foreground/60">{((res.memory.usedMB ?? 0) / 1024).toFixed(1)}/{(res.memory.totalMB / 1024).toFixed(1)} GB</div>}
                </div>
                {/* 带宽 */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="flex items-center gap-1 text-muted-foreground"><Wifi className="h-3 w-3" />带宽</span>
                    {bwLimit > 0 ? (
                      <span className={`font-mono font-medium ${getColor(bwUsagePct)}`}>{bwUsagePct.toFixed(0)}%</span>
                    ) : (
                      <span className="font-mono font-medium text-muted-foreground">--</span>
                    )}
                  </div>
                  {bwLimit > 0 && (
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(bwUsagePct)}`} style={{ width: `${bwUsagePct}%` }} />
                    </div>
                  )}
                  <div className="text-[9px] text-muted-foreground/60">
                    ↓{bwDown.toFixed(2)} ↑{bwUp.toFixed(2)} Mbps
                  </div>
                </div>
                {/* 磁盘 */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="flex items-center gap-1 text-muted-foreground"><HardDrive className="h-3 w-3" />磁盘</span>
                    <span className={`font-mono font-medium ${getColor(diskPct)}`}>{diskPct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(diskPct)}`} style={{ width: `${diskPct}%` }} />
                  </div>
                  {res.disk?.totalGB != null && <div className="text-[9px] text-muted-foreground/60">{res.disk.usedGB?.toFixed(1)}/{res.disk.totalGB.toFixed(1)} GB</div>}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* 带宽设置弹窗 */}
      <Dialog open={showResourceConfigDialog} onOpenChange={setShowResourceConfigDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">资源监控设置</DialogTitle>
            <DialogDescription className="text-xs">配置服务器带宽上限和资源采集频率</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">带宽上限</label>
              <div className="flex items-center gap-2">
                <Input type="number" min="1" step="0.5" value={bandwidthInput} onChange={(e) => setBandwidthInput(e.target.value)} placeholder="如 3" className="text-sm" />
                <span className="text-xs text-muted-foreground shrink-0">Mbps</span>
              </div>
              <div className="text-[10px] text-muted-foreground">常见值：1M=1, 3M=3, 5M=5, 10M=10, 100M=100</div>
            </div>
            <div className="border-t border-border/50 pt-3 space-y-3">
              <label className="text-xs font-medium text-foreground">资源采集频率</label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">交易时段</label>
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="10" step="10" value={intervalTradingInput} onChange={(e) => setIntervalTradingInput(e.target.value)} className="text-sm" />
                    <span className="text-[10px] text-muted-foreground shrink-0">秒</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground">非交易时段</label>
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="10" step="30" value={intervalIdleInput} onChange={(e) => setIntervalIdleInput(e.target.value)} className="text-sm" />
                    <span className="text-[10px] text-muted-foreground shrink-0">秒</span>
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground">交易时段 9:15-15:00，建议 30-120秒；非交易时段建议 300-600秒</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowResourceConfigDialog(false)}>取消</Button>
            <Button size="sm" disabled={updateResourceConfig.isPending} onClick={() => {
              const params: { bandwidthLimitMbps?: number; collectIntervalTrading?: number; collectIntervalIdle?: number } = {};
              if (bandwidthInput && Number(bandwidthInput) > 0) params.bandwidthLimitMbps = Number(bandwidthInput);
              if (intervalTradingInput && Number(intervalTradingInput) >= 10) params.collectIntervalTrading = Number(intervalTradingInput);
              if (intervalIdleInput && Number(intervalIdleInput) >= 10) params.collectIntervalIdle = Number(intervalIdleInput);
              if (Object.keys(params).length === 0) { toast.error("请填写至少一个配置项"); return; }
              updateResourceConfig.mutate(params);
            }}>
              {updateResourceConfig.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 自选股列表 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-400" />
              T+0 自选股 ({(watchlistData || []).length})
            </span>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate("/screening")}>
              前往 T+0 选股<ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </CardTitle>
          <CardDescription className="text-xs">点击股票右侧的 "添加监控" 按钮将其加入 Agent 监控列表</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(watchlistData || []).length > 0 ? (
            <>
              {/* 桌面端表格 */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs">代码</TableHead>
                      <TableHead className="text-xs">名称</TableHead>
                      <TableHead className="text-xs">行业</TableHead>
                      <TableHead className="text-xs text-right">添加价格</TableHead>
                      <TableHead className="text-xs">来源</TableHead>
                      <TableHead className="text-xs">添加时间</TableHead>
                      <TableHead className="text-xs text-center">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(watchlistData || []).map((w: any) => {
                      const inMonitors = monitorCodes.has(w.stockCode);
                      return (
                        <TableRow key={w.id} className="border-border/30 hover:bg-accent/20">
                          <TableCell className="text-xs font-mono">{w.stockCode}</TableCell>
                          <TableCell className="text-xs font-medium">{w.stockName}</TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs">{w.industry || "-"}</Badge></TableCell>
                          <TableCell className="text-xs text-right font-mono">{w.addPrice ? `¥${w.addPrice.toFixed(2)}` : "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px]">
                              {w.addSource === "screening" ? "T+0选股" : w.addSource === "opportunity" ? "短期掘金" : "手动"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatBJ(w.createdAt, "date")}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {w.t0Suitable === 0 && (
                                <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-400 px-1">
                                  不适合T
                                </Badge>
                              )}
                              {inMonitors ? (
                                <Badge variant="secondary" className="text-[10px]"><Check className="h-3 w-3 mr-0.5" />已监控</Badge>
                              ) : (
                                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2"
                                  onClick={() => handleAddFromWatchlist(w)} disabled={addMonitor.isPending}>
                                  <Plus className="h-3 w-3 mr-1" />添加监控
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10"
                                onClick={() => removeWatchlist.mutate({ id: w.id })} disabled={removeWatchlist.isPending}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {/* 移动端卡片 */}
              <div className="lg:hidden space-y-2 p-3">
                {(watchlistData || []).map((w: any) => {
                  const inMonitors = monitorCodes.has(w.stockCode);
                  return (
                    <div key={w.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border/50 bg-card/50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground">{w.stockCode}</span>
                          <span className="text-xs font-medium">{w.stockName}</span>
                          <Badge variant="secondary" className="text-[10px]">{w.industry || "-"}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] text-muted-foreground">¥{w.addPrice?.toFixed(2) || "-"}</span>
                          <Badge variant="outline" className="text-[9px]">
                            {w.addSource === "screening" ? "T+0选股" : w.addSource === "opportunity" ? "短期掘金" : "手动"}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">{formatBJ(w.createdAt, "date")}</span>
                        </div>
                      </div>
                      <div className="shrink-0 ml-2 flex items-center gap-1">
                        {w.t0Suitable === 0 && (
                          <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-400 px-1">不适T</Badge>
                        )}
                        {inMonitors ? (
                          <Badge variant="secondary" className="text-[10px]"><Check className="h-3 w-3 mr-0.5" />已监控</Badge>
                        ) : (
                          <Button variant="outline" size="sm" className="h-7 text-[10px] px-2"
                            onClick={() => handleAddFromWatchlist(w)} disabled={addMonitor.isPending}>
                            <Plus className="h-3 w-3 mr-1" />添加监控
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10"
                          onClick={() => removeWatchlist.mutate({ id: w.id })} disabled={removeWatchlist.isPending}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Star className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">暂无自选股</p>
              <p className="text-xs mt-1 opacity-60">
                请先前往 <span className="text-primary cursor-pointer" onClick={() => navigate("/screening")}>T+0 选股</span> 添加自选股
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 手动添加股票弹窗 - 支持实时搜索 */}
      <Dialog open={showAddDialog} onOpenChange={(v) => { setShowAddDialog(v); if (!v) { setSearchKeyword(""); setAddStockCode(""); setAddStockName(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-primary" />添加监控股票</DialogTitle>
            <DialogDescription className="text-xs">输入股票代码或名称搜索，系统将实时查询匹配结果</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* [R4属主 20260817] 模式在添加那一刻就定死: 事后再改 tradeMode 是同码双实例的来路 */}
            <div>
              <Label className="text-xs">交易模式（必选，添加后即固定）</Label>
              <div className="mt-1 inline-flex rounded-lg border border-border/60 bg-muted/20 p-1 gap-1">
                <button type="button" onClick={() => setAddTradeMode('simulation')}
                  className={`px-4 py-1 rounded-md text-xs font-semibold transition-colors ${addTradeMode === 'simulation'
                    ? 'bg-blue-500/15 text-blue-400 border border-blue-500/40'
                    : 'text-muted-foreground border border-transparent'}`}>模拟</button>
                <button type="button" onClick={() => setAddTradeMode('live')}
                  className={`px-4 py-1 rounded-md text-xs font-semibold transition-colors ${addTradeMode === 'live'
                    ? 'bg-red-500/15 text-red-400 border border-red-500/40'
                    : 'text-muted-foreground border border-transparent'}`}>⚡实盘</button>
              </div>
              {addTradeMode === 'live' && (
                <p className="text-[10px] text-amber-400 mt-1">
                  实盘 T+0 需要底仓才做得成 —— 请确认该票在券商真账户里有持仓，否则建了也跑不起来。
                </p>
              )}
            </div>
            <div className="relative">
              <Label className="text-xs">搜索股票</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input className="h-8 text-xs pl-8" placeholder="输入股票代码或名称，如 600519 或 茅台" value={searchKeyword} onChange={e => { setSearchKeyword(e.target.value); }} />
              </div>
              {searchResults && searchResults.length > 0 && searchKeyword && (
                <div className="mt-1 border border-border/50 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-popover">
                  {searchResults.map((s: any) => (
                    <button key={s.code} className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-accent/50 transition-colors ${
                      addStockCode === s.code ? "bg-primary/10 text-primary" : ""
                    } ${monitorCodes.has(s.code) ? "opacity-50" : ""}`}
                      onClick={() => { setAddStockCode(s.code); setAddStockName(s.name); }}
                      disabled={monitorCodes.has(s.code)}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{s.code}</span>
                        <span>{s.name}</span>
                      </div>
                      {monitorCodes.has(s.code) ? (
                        <Badge variant="outline" className="text-[10px] h-4">已监控</Badge>
                      ) : addStockCode === s.code ? (
                        <Check className="h-3.5 w-3.5 text-primary" />
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
              {searchKeyword && debouncedKeyword && searchResults && searchResults.length === 0 && (
                <div className="mt-1 p-3 text-xs text-muted-foreground text-center border border-border/50 rounded-lg">未找到匹配的股票</div>
              )}
            </div>
            {addStockCode && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                <Check className="h-4 w-4 text-primary" />
                <span className="text-xs">已选择：<span className="font-mono font-medium">{addStockCode}</span> {addStockName}</span>
              </div>
            )}
            <Separator />
            <div className="text-[10px] text-muted-foreground">或直接输入股票代码：</div>
            <div className="flex gap-2">
              {/* [P2-65 2026-05-23] A 股代码 6 位数字，前端过滤非数字 + 限长，避免提交格式错误 */}
              <Input className="h-8 text-xs flex-1" placeholder="股票代码 (6 位数字)" maxLength={6} inputMode="numeric"
                value={addStockCode} onChange={e => setAddStockCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
              <Input className="h-8 text-xs flex-1" placeholder="股票名称（可选）" value={addStockName} onChange={e => setAddStockName(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground mb-1 block">券商</label>
              <select className="w-full h-8 text-xs rounded-md border border-input bg-background px-2"
                value={addBrokerName} onChange={e => setAddBrokerName(e.target.value)}>
                <option value="xingye">兴业证券</option>
                <option value="haitung">海通证券</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground mb-1 block">行情源</label>
              <select className="w-full h-8 text-xs rounded-md border border-input bg-background px-2"
                value={addMarketDataSource} onChange={e => setAddMarketDataSource(e.target.value)}>
                {addBrokerName === "haitung" ? (
                  <>
                    <option value="mqtt_l2">MQTT L2（base32.cn，优先）</option>
                    <option value="nats_l2">NATS L2（第三方）</option>
                    <option value="xtquant">XtQuant L1（君弘内置）</option>
                  </>
                ) : (
                  <>
                    <option value="mqtt_l2">MQTT L2（base32.cn，优先）</option>
                    <option value="nats_l2">NATS L2（第三方）</option>
                    <option value="tencent_l1">腾讯 L1（SMT-Q兴业）</option>
                  </>
                )}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAddDialog(false)}>取消</Button>
            <Button size="sm" onClick={handleManualAdd} disabled={addMonitor.isPending || !addStockCode?.trim()}>
              {addMonitor.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}添加监控
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 弹窗组件 */}
      {/* [Steven 2026-05-26 P0 跨股 leak 修] key={editingMonitor.id} 强制 React 切股时重建 dialog,
          否则 useState 初始化值只跑一次, 后续切到 B 股保存会把 A 股的 form 状态写到 B */}
      {editingMonitor && <TradingParamDialog key={editingMonitor.id} open={showParamDialog} onOpenChange={setShowParamDialog} monitor={editingMonitor} defaultParams={engineStatus?.defaultParams} onSave={handleSaveParams} isSaving={updateParams.isPending} />}
      {recordMonitor && <TradeRecordsDialog open={showRecordDialog} onOpenChange={setShowRecordDialog} monitor={recordMonitor} />}
      {detailMonitor && <EngineDetailDialog open={showDetailDialog} onOpenChange={setShowDetailDialog} monitor={detailMonitor} defaultParams={engineStatus?.defaultParams} isTradingHours={isTradingHoursGlobal} />}
      {logMonitor && <EngineLogDialog open={showLogDialog} onOpenChange={setShowLogDialog} monitor={logMonitor} isTradingHours={isTradingHoursGlobal} />}
      {compareMonitor && <BacktestCompareDialog open={showCompareDialog} onOpenChange={setShowCompareDialog} monitor={compareMonitor} />}

      {/* 引擎回放弹窗 */}
      <Dialog open={showReplayDialog} onOpenChange={(v) => { setShowReplayDialog(v); if (!v) setReplayMonitor(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Film className="h-5 w-5 text-emerald-400" />引擎回放
            </DialogTitle>
            <DialogDescription className="text-xs">可视化回放引擎每次tick的决策过程</DialogDescription>
          </DialogHeader>
          {replayMonitor && (
            <EngineReplayPanel
              monitorId={replayMonitor.id}
              stockName={replayMonitor.stockName}
              stockCode={replayMonitor.stockCode}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ATR学习弹窗 */}
      <Dialog open={showAtrDialog} onOpenChange={(v) => { setShowAtrDialog(v); if (!v) setAtrMonitor(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-400" />波动率自适应学习
            </DialogTitle>
            <DialogDescription className="text-xs">根据历史交易结果自动微调ATR系数</DialogDescription>
          </DialogHeader>
          {atrMonitor && (
            <AtrLearningPanel
              monitorId={atrMonitor.id}
              stockName={atrMonitor.stockName}
              stockCode={atrMonitor.stockCode}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* [Claude 2026-05-08] 单股自动优化对话框：二次确认 + 实盘允许 */}
      <Dialog open={!!optimizeMonitor && optimizeStep > 0} onOpenChange={(v) => { if (!v) { setOptimizeMonitor(null); setOptimizeStep(0); setOptimizePreview(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" />
              {optimizeStep === 1 ? "自动优化 - 第 1 步：预览决策" : "自动优化 - 第 2 步：确认应用"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {optimizeMonitor && `${optimizeMonitor.stockName}（${optimizeMonitor.stockCode}）`}
            </DialogDescription>
          </DialogHeader>
          <OptimizeMonitorContent
            monitor={optimizeMonitor}
            step={optimizeStep}
            preview={optimizePreview}
            tradeMode={tradeModeDisplay.label}
            onPreviewLoaded={setOptimizePreview}
            onNext={() => setOptimizeStep(2)}
            onBack={() => setOptimizeStep(1)}
            onSuccess={() => { setOptimizeMonitor(null); setOptimizeStep(0); setOptimizePreview(null); refetchMonitors(); }}
            onCancel={() => { setOptimizeMonitor(null); setOptimizeStep(0); setOptimizePreview(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* 批量模板应用弹窗 */}
      <Dialog open={showBatchTemplateDialog} onOpenChange={setShowBatchTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              批量应用参数模板
            </DialogTitle>
            <DialogDescription className="text-xs">
              选择一个参数模板，将其应用到所有 {(monitors || []).length} 只监控股票
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(templates || []).map((tpl: any) => (
              <button key={tpl.id}
                className={`w-full p-3 rounded-lg border text-left transition-colors ${
                  batchTemplateId === tpl.id ? "border-primary bg-primary/10" : "border-border/50 hover:border-primary/50 hover:bg-primary/5"
                }`}
                onClick={() => setBatchTemplateId(tpl.id)}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{tpl.name}</div>
                  {batchTemplateId === tpl.id && <Check className="h-4 w-4 text-primary" />}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{tpl.description}</div>
                <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                  <span>回调: {tpl.params.callbackTriggerPct}%</span>
                  <span>反弹: {tpl.params.bounceTriggerPct}%</span>
                  <span>止损: {tpl.params.stopLossTriggerPct}%</span>
                  <span>每笔: {tpl.params.tradeLotSize}股</span>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowBatchTemplateDialog(false)}>取消</Button>
            <Button size="sm" disabled={!batchTemplateId || batchApplyTemplate.isPending}
              onClick={() => {
                if (!window.confirm(`⚠️ 确认应用 "${batchTemplateId}" 模板到全部 ${(monitors || []).length} 只 monitor?\n\n这会覆盖每只股的当前参数 (建议 per-monitor 单独调).`)) return;
                batchApplyTemplate.mutate({ templateId: batchTemplateId, confirmApplyToAll: true });
              }}>
              {batchApplyTemplate.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1" />}
              应用到全部 ({(monitors || []).length} 只)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 - 纯HTML实现，不依赖任何UI库组件 */}
      {deleteStep >= 1 && deleteConfirmMonitor && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => { console.log('[DELETE] backdrop click, closing'); setDeleteStep(0); setDeleteConfirmMonitor(null); }} />
          <div style={{ position: 'relative', zIndex: 10000, backgroundColor: 'var(--background, #1a1a2e)', border: '1px solid var(--border, #333)', borderRadius: '12px', padding: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', width: '100%', maxWidth: '420px', margin: '0 16px' }}>
            {deleteStep === 1 && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#f87171', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Trash2 style={{ width: 20, height: 20 }} />确认删除
                  </h3>
                  <p style={{ fontSize: '14px', color: '#a1a1aa' }}>
                    确定要删除 <span style={{ fontWeight: 700, color: '#e4e4e7' }}>{deleteConfirmMonitor?.stockName}({deleteConfirmMonitor?.stockCode})</span> 的 Agent 监控吗？
                  </p>
                  {deleteConfirmMonitor?.isRunning === 1 && (
                    <p style={{ fontSize: '12px', color: '#fbbf24', marginTop: '8px', padding: '8px', backgroundColor: 'rgba(251,191,36,0.1)', borderRadius: '6px' }}>
                      ⚠️ 该股票引擎正在运行中，删除时将自动停止引擎。
                    </p>
                  )}
                  {deleteConfirmMonitor?.totalProfit !== 0 && (
                    <p style={{ fontSize: '12px', color: '#a1a1aa', marginTop: '8px' }}>
                      累计盈亏: <span style={{ color: (deleteConfirmMonitor?.totalProfit || 0) >= 0 ? '#22c55e' : '#ef4444' }}>¥{(deleteConfirmMonitor?.totalProfit || 0).toFixed(2)}</span>
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button onClick={() => { setDeleteStep(0); setDeleteConfirmMonitor(null); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #555', backgroundColor: 'transparent', color: '#e4e4e7', cursor: 'pointer', fontSize: '14px' }}>
                    取消
                  </button>
                  <button onClick={() => { console.log('[DELETE] step1 -> step2'); setDeleteStep(2); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                    确认删除
                  </button>
                </div>
              </>
            )}
            {deleteStep === 2 && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#f87171', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Trash2 style={{ width: 20, height: 20 }} />再次确认（第二步）
                  </h3>
                  <p style={{ fontSize: '14px', color: '#a1a1aa' }}>
                    <span style={{ fontWeight: 700, color: '#f87171' }}>此操作不可撤销！</span><br />
                    确定要删除 <span style={{ fontWeight: 700, color: '#e4e4e7' }}>{deleteConfirmMonitor?.stockName}</span> 吗？
                    如果有交易记录，将自动移入历史持仓。
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button onClick={() => { setDeleteStep(0); setDeleteConfirmMonitor(null); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #555', backgroundColor: 'transparent', color: '#e4e4e7', cursor: 'pointer', fontSize: '14px' }}>
                    取消
                  </button>
                  <button 
                    disabled={removeMonitor.isPending}
                    onClick={() => {
                      console.log('[DELETE] step2 confirm, calling removeMonitor.mutate, id:', deleteConfirmMonitor.id);
                      removeMonitor.mutate({ id: deleteConfirmMonitor.id });
                    }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: removeMonitor.isPending ? '#991b1b' : '#dc2626', color: 'white', cursor: removeMonitor.isPending ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: removeMonitor.isPending ? 0.7 : 1 }}>
                    {removeMonitor.isPending ? '删除中...' : '确认删除'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 启动引擎确认对话框 (P1.5-3: 支持自定义额度) */}
      <AlertDialog open={!!startConfirmMonitor} onOpenChange={(open) => { if (!open) setStartConfirmMonitor(null); setRestartOverride(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-primary">
              <Zap className="h-5 w-5" />启动交易引擎
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  确定要启动 <span className="font-bold text-foreground">{startConfirmMonitor?.stockName}({startConfirmMonitor?.stockCode})</span> 的交易引擎吗？
                </p>
                {startConfirmMonitor && startConfirmMonitor.todayRounds >= startConfirmMonitor.maxDailyRounds && (
                  <div className="mt-3 p-3 rounded bg-amber-500/10 border border-amber-500/30">
                    <p className="text-xs text-amber-400 font-semibold mb-2">
                      ⚠️ 当前轮次已达上限 ({startConfirmMonitor.todayRounds}/{startConfirmMonitor.maxDailyRounds})
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">
                      请调整额度后再启动 (默认翻倍):
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="text-xs w-20 text-muted-foreground">每日轮次:</label>
                        <input
                          type="number"
                          defaultValue={Math.min((startConfirmMonitor.maxDailyRounds || 0) * 2, 10)}
                          onChange={(e) => setRestartOverride((prev: any) => ({ ...prev, newMaxDailyRounds: Number(e.target.value) }))}
                          className="flex-1 px-2 py-1 text-xs rounded bg-background border border-border"
                          min="0" max="10"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs w-20 text-muted-foreground">日止损额:</label>
                        <input
                          type="number"
                          defaultValue={(startConfirmMonitor.maxDailyLossAmount || 0) * 2}
                          onChange={(e) => setRestartOverride((prev: any) => ({ ...prev, newMaxDailyLossAmount: Number(e.target.value) }))}
                          className="flex-1 px-2 py-1 text-xs rounded bg-background border border-border"
                          min="0"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs w-20 text-muted-foreground">日盈利目标:</label>
                        <input
                          type="number"
                          defaultValue={(startConfirmMonitor.dailyProfitTarget || 0) * 2}
                          onChange={(e) => setRestartOverride((prev: any) => ({ ...prev, newDailyProfitTarget: Number(e.target.value) }))}
                          className="flex-1 px-2 py-1 text-xs rounded bg-background border border-border"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <p className="block mt-2 text-xs text-amber-400">启动后引擎将自动执行 T+0 交易规则，在交易时段内实时监控并自动下单。</p>
                <p className="block mt-1 text-xs text-muted-foreground">请确保已配置正确的交易参数和账户信息。</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setRestartOverride(null); }}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (restartOverride && startConfirmMonitor.todayRounds >= startConfirmMonitor.maxDailyRounds) {
                // 达上限 → 用 P1.5-3 自定义重启接口
                restartMonitorWithOverride.mutate({ id: startConfirmMonitor.id, ...restartOverride });
              } else {
                // 普通启动
                toggleRunning.mutate({ id: startConfirmMonitor.id, isRunning: true });
              }
              setStartConfirmMonitor(null);
              setRestartOverride(null);
            }}>
              <Zap className="h-3 w-3 mr-1" />确认启动
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 单个股票停止前持仓做平检查确认对话框 */}
      {stopConfirmMonitor && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setStopConfirmMonitor(null)} />
          <div style={{ position: 'relative', zIndex: 10000, backgroundColor: 'var(--background, #1a1a2e)', border: '1px solid var(--border, #333)', borderRadius: '12px', padding: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', width: '100%', maxWidth: '420px', margin: '0 16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <AlertTriangle style={{ width: 20, height: 20 }} />持仓未做平警告
              </h3>
              <p style={{ fontSize: '14px', color: '#a1a1aa' }}>
                <span style={{ fontWeight: 700, color: '#e4e4e7' }}>{stopConfirmMonitor.stockName}({stopConfirmMonitor.stockCode})</span> 当前处于
                <span style={{ fontWeight: 700, color: '#a78bfa' }}>“等待做平”</span>状态，已完成第一笔交易但尚未配对。
              </p>
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: 'rgba(168,85,247,0.1)', borderRadius: '8px', border: '1px solid rgba(168,85,247,0.2)' }}>
                <div style={{ fontSize: '13px', color: '#c4b5fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>未做平数量</span>
                  <span style={{ fontWeight: 700, fontSize: '16px', color: '#a78bfa' }}>{stopConfirmMonitor.tradeLotSize || stopConfirmMonitor.currentHolding || '-'} 股</span>
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#fbbf24', marginTop: '12px', padding: '8px', backgroundColor: 'rgba(251,191,36,0.1)', borderRadius: '6px' }}>
                ⚠️ 强制停止后该股票将不会自动完成配对交易，需要手动处理剩余持仓。
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setStopConfirmMonitor(null)}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #555', backgroundColor: 'transparent', color: '#e4e4e7', cursor: 'pointer', fontSize: '14px' }}>
                取消
              </button>
              <button
                disabled={toggleRunning.isPending}
                onClick={() => {
                  toggleRunning.mutate({ id: stopConfirmMonitor.id, isRunning: false });
                  setStopConfirmMonitor(null);
                }}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: 'white', cursor: toggleRunning.isPending ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: toggleRunning.isPending ? 0.7 : 1 }}>
                {toggleRunning.isPending ? '停止中...' : '确认停止'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量启动确认对话框 */}
      {showBatchStartConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setShowBatchStartConfirm(false)} />
          <div style={{ position: 'relative', zIndex: 10000, backgroundColor: 'var(--background, #1a1a2e)', border: '1px solid var(--border, #333)', borderRadius: '12px', padding: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', width: '100%', maxWidth: '480px', margin: '0 16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#22c55e', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <PlayCircle style={{ width: 20, height: 20 }} />全部启动确认
              </h3>
              <p style={{ fontSize: '14px', color: '#a1a1aa' }}>
                将启动以下 <span style={{ fontWeight: 700, color: '#22c55e' }}>{stoppedMonitors.length}</span> 只股票的 Agent 引擎：
              </p>
              <div style={{ marginTop: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                {stoppedMonitors.map((m: any) => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', marginBottom: '4px', backgroundColor: 'rgba(34,197,94,0.08)', borderRadius: '6px', border: '1px solid rgba(34,197,94,0.15)' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#e4e4e7' }}>{m.stockName}</span>
                      <span style={{ fontSize: '11px', color: '#71717a', marginLeft: '6px' }}>{m.stockCode}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#71717a' }}>
                      {templateLabel(m.activeTemplate, { short: true }).label}
                    </span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#22c55e', marginTop: '12px', padding: '8px', backgroundColor: 'rgba(34,197,94,0.08)', borderRadius: '6px' }}>
                启动后引擎将在交易时段内自动执行 T+0 交易规则，请确保已配置正确的交易参数和账户信息。
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowBatchStartConfirm(false)}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #555', backgroundColor: 'transparent', color: '#e4e4e7', cursor: 'pointer', fontSize: '14px' }}>
                取消
              </button>
              <button
                disabled={batchStartAll.isPending || batchRestartAllSmart.isPending}
                onClick={async () => {
                  const monitorIds = stoppedMonitors.map((m: any) => m.id);
                  try {
                    await batchRestartAllSmart.mutateAsync({ confirmDouble: true, monitorIds });
                  } catch {
                    // 智能分析不可用时仍复用批量启动；resetRounds=false，保留当日轮次。
                    batchStartAll.mutate({ monitorIds });
                  }
                  setShowBatchStartConfirm(false);
                }}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#22c55e', color: 'white', cursor: (batchStartAll.isPending || batchRestartAllSmart.isPending) ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: (batchStartAll.isPending || batchRestartAllSmart.isPending) ? 0.7 : 1 }}>
                {(batchStartAll.isPending || batchRestartAllSmart.isPending) ? '启动中...' : `智能启动 ${stoppedMonitors.length} 只 (达上限自动翻倍)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量停止前持仓做平检查确认对话框 */}
      {showBatchStopConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setShowBatchStopConfirm(false)} />
          <div style={{ position: 'relative', zIndex: 10000, backgroundColor: 'var(--background, #1a1a2e)', border: '1px solid var(--border, #333)', borderRadius: '12px', padding: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', width: '100%', maxWidth: '480px', margin: '0 16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <AlertTriangle style={{ width: 20, height: 20 }} />持仓未做平警告
              </h3>
              <p style={{ fontSize: '14px', color: '#a1a1aa' }}>
                以下 <span style={{ fontWeight: 700, color: '#a78bfa' }}>{unfilledMonitors.length}</span> 只股票当前处于“等待做平”状态，已完成第一笔交易但尚未配对：
              </p>
              <div style={{ marginTop: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                {unfilledMonitors.map((m: any) => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', marginBottom: '4px', backgroundColor: 'rgba(168,85,247,0.1)', borderRadius: '6px', border: '1px solid rgba(168,85,247,0.15)' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#e4e4e7' }}>{m.stockName}</span>
                      <span style={{ fontSize: '11px', color: '#71717a', marginLeft: '6px' }}>{m.stockCode}</span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#a78bfa' }}>{m.tradeLotSize || m.currentHolding || '-'} 股</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#fbbf24', marginTop: '12px', padding: '8px', backgroundColor: 'rgba(251,191,36,0.1)', borderRadius: '6px' }}>
                ⚠️ 强制停止后这些股票将不会自动完成配对交易，需要手动处理剩余持仓。
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => setShowBatchStopConfirm(false)}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #555', backgroundColor: 'transparent', color: '#e4e4e7', cursor: 'pointer', fontSize: '14px' }}>
                取消
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', borderRadius: '8px', border: '1px solid #22c55e', backgroundColor: 'rgba(34,197,94,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '13px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={limitPriceMode} onChange={(e) => setLimitPriceMode(e.target.checked)} style={{ accentColor: '#22c55e' }} />
                    限价做平
                  </label>
                  {limitPriceMode && (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="最低卖出价"
                      value={limitPriceValue}
                      onChange={(e) => setLimitPriceValue(e.target.value)}
                      style={{ width: '110px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#1a1a2e', color: '#e4e4e7', fontSize: '13px' }}
                    />
                  )}
                </div>
                <button
                  disabled={forceCloseUnfilled.isPending || (limitPriceMode && (!limitPriceValue || parseFloat(limitPriceValue) <= 0))}
                  onClick={() => {
                    // 二次确认：弹出浏览器原生confirm对话框
                    const confirmMsg = limitPriceMode
                      ? `确认以限价 ≥¥${limitPriceValue} 做平 ${unfilledMonitors.length} 只股票？\n\n此操作将立即执行卖出，请确认价格合理。`
                      : `确认以市价做平 ${unfilledMonitors.length} 只股票？\n\n此操作将以当前市价立即执行卖出。`;
                    if (!window.confirm(confirmMsg)) return;
                    // 生成带时间戳的确认令牌
                    const confirmToken = `FORCE_CLOSE_${Date.now()}`;
                    if (limitPriceMode && limitPriceValue) {
                      forceCloseUnfilled.mutate({ mode: 'limit', minPrice: parseFloat(limitPriceValue), confirmToken });
                      toast.info(`正在以限价 ¥${limitPriceValue} 做平 ${unfilledMonitors.length} 只股票...`);
                    } else {
                      forceCloseUnfilled.mutate({ mode: 'market', confirmToken });
                      toast.info(`正在以市价做平 ${unfilledMonitors.length} 只股票...`);
                    }
                    setShowBatchStopConfirm(false);
                    setLimitPriceMode(false);
                    setLimitPriceValue('');
                  }}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#22c55e', color: 'white', cursor: (forceCloseUnfilled.isPending || (limitPriceMode && (!limitPriceValue || parseFloat(limitPriceValue) <= 0))) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: forceCloseUnfilled.isPending ? 0.7 : 1 }}>
                  {forceCloseUnfilled.isPending ? '做平中...' : limitPriceMode ? `限价做平 (≥¥${limitPriceValue || '?'})` : '市价做平'}
                </button>
              </div>
              <button
                disabled={batchStopAll.isPending}
                onClick={() => {
                  batchStopAll.mutate({
                    excludeUnfilled: true,
                    monitorIds: runningMonitors.map((m: any) => m.id),
                  });
                  setShowBatchStopConfirm(false);
                }}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #a78bfa', backgroundColor: 'rgba(167,139,250,0.15)', color: '#a78bfa', cursor: batchStopAll.isPending ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: batchStopAll.isPending ? 0.7 : 1 }}>
                仅停止已做平的股票
              </button>
              <button
                disabled={batchStopAll.isPending}
                onClick={() => {
                  batchStopAll.mutate({ monitorIds: runningMonitors.map((m: any) => m.id) });
                  setShowBatchStopConfirm(false);
                }}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: 'white', cursor: batchStopAll.isPending ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: batchStopAll.isPending ? 0.7 : 1 }}>
                {batchStopAll.isPending ? '停止中...' : '全部停止'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 重置日内状态确认弹窗 */}
      {showDailyResetConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div style={{ position: 'relative', zIndex: 10000, backgroundColor: 'var(--background, #1a1a2e)', border: '1px solid var(--border, #333)', borderRadius: '12px', padding: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', width: '100%', maxWidth: '440px', margin: '0 16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <RotateCcw style={{ width: 20, height: 20 }} />重置日内状态确认
              </h3>
              <p style={{ fontSize: '14px', color: '#a1a1aa', lineHeight: '1.6' }}>
                将重置所有 Agent 的以下日内状态：
              </p>
              <ul style={{ fontSize: '13px', color: '#d4d4d8', marginTop: '8px', paddingLeft: '20px', lineHeight: '1.8' }}>
                <li>方向（做多/做空）→ 清空</li>
                <li>基准价 → 清空</li>
                <li>今日盈亏 → 归零</li>
                <li>今日轮次 → 归零</li>
                <li>今日交易次数 → 归零</li>
                <li>连续止损次数 → 归零</li>
                <li>暂停状态 → 清除</li>
              </ul>
              <p style={{ fontSize: '13px', color: '#f59e0b', marginTop: '12px' }}>
                ⚠️ 此操作通常在每日开盘前自动执行，手动触发仅在自动重置未生效时使用。
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setShowDailyResetConfirm(false)}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #555', backgroundColor: 'transparent', color: '#a1a1aa', cursor: 'pointer', fontSize: '14px' }}>
                取消
              </button>
              <button
                disabled={dailyReset.isPending}
                onClick={() => dailyReset.mutate()}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#f59e0b', color: '#000', cursor: dailyReset.isPending ? 'wait' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: dailyReset.isPending ? 0.7 : 1 }}>
                {dailyReset.isPending ? '重置中...' : '确认重置'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 移动端底部快捷操作栏 */}
      {/* [Steven 2026-05-26 14:11 修] 直接用后端 globalSummary (已剔除 test + 单边金额),
          前端不再独立 reduce monitorAsset (tRPC 序列化可能 strip 自定义属性, 分母为 0 → netPct 0) */}
      <MobileQuickBar
        visible={false}  /* [20260811 UX v2] 数据并入顶部统一卡, 快捷条退役(改true即恢复) */
        engineStatus={{
          total: _viewMonitors.length,
          running: _viewMonitors.filter((m: any) => m.isRunning === 1).length,
          idle: _viewMonitors.filter((m: any) => m.isRunning !== 1).length,
          todayProfit: _scopeTotals.profit,
          todayTrades: _scopeTotals.trades,
          todayRounds: _scopeTotals.rounds,
          todayAmount: _scopeTotals.amount,
          netPct: _scopeTotals.amount > 0 ? _scopeTotals.profit / _scopeTotals.amount * 100 : 0,
          byClass: _byClassBar,
          stocks: _viewMonitors.map((m: any) => ({
            id: m.id,
            code: m.stockCode,
            name: m.stockName,
            status: m.isRunning === 1 ? "running" : "idle",
            profit: displayTodayPnl(m) || 0,
            holding: m.currentHolding || 0,
          })),
        }}
        scope={viewMode}
        onScopeChange={(s) => setViewMode(s === 'sim' ? 'sim' : 'live')}
        onStockAction={(stock, action) => {
          const monitor = (monitors || []).find((m: any) => m.stockCode === stock.code);
          if (!monitor) return;
          if (action === "params") {
            setEditingMonitor(monitor);
            setShowParamDialog(true);
          } else if (action === "records") {
            setRecordMonitor(monitor);
            setShowRecordDialog(true);
          } else if (action === "detail") {
            setDetailMonitor(monitor);
            setShowDetailDialog(true);
          }
        }}
      />
      {/* 底部占位空间，防止内容被快捷栏遮挡 */}
      <div className="lg:hidden h-12" />
    </div>
  );
}

// ========== 持仓管理 ==========
function PositionManagement() {
  const _isTH = checkTradingHours().isTradingHours;
  const { data: positions, isLoading } = trpc.positions.list.useQuery(undefined, { refetchInterval: smartInterval(30000, _isTH, 120000) });
  const { data: stockParams, refetch: refetchParams } = trpc.trading.stockParams.useQuery();
  const { data: deletedMonitors } = trpc.agentMonitor.deletedHistory.useQuery();
  const [, setLocation] = useLocation();
  const [editingStock, setEditingStock] = useState<string | null>(null);
  const [paramForm, setParamForm] = useState({ bbPeriod: 20, bbStdDev: 2, tradeLotSize: 1000, takeProfitPct: 2, stopLossPct: 1.5 });
  const [posTab, setPosTab] = useState("current");
  const [historyRecordMonitor, setHistoryRecordMonitor] = useState<any>(null);

  const upsertParam = trpc.trading.upsertStockParam.useMutation({
    onSuccess: () => { refetchParams(); toast.success("参数已保存，其他设备刷新页面后生效", { duration: 4000 }); setEditingStock(null); },
    onError: (err: any) => toast.error(err.message),
  });
  const deleteParam = trpc.trading.deleteStockParam.useMutation({
    onSuccess: () => { refetchParams(); toast.success("已恢复默认参数"); },
    onError: (err: any) => toast.error(err.message),
  });

  const totalValue = positions?.reduce((s, p) => s + p.currentPrice * p.quantity, 0) || 0;
  const totalPnl = positions?.reduce((s, p) => s + p.profitLoss, 0) || 0;
  const totalCost = positions?.reduce((s, p) => s + p.avgCost * p.quantity, 0) || 0;
  const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost * 100) : 0;

  const handleEditParam = (stockCode: string, stockName: string) => {
    const existing = stockParams?.find(sp => sp.stockCode === stockCode);
    if (existing) {
      setParamForm({ bbPeriod: existing.bbPeriod, bbStdDev: existing.bbStdDev, tradeLotSize: existing.tradeLotSize, takeProfitPct: existing.takeProfitPct, stopLossPct: existing.stopLossPct });
    } else {
      setParamForm({ bbPeriod: 20, bbStdDev: 2, tradeLotSize: 1000, takeProfitPct: 2, stopLossPct: 1.5 });
    }
    setEditingStock(stockCode);
  };

  return (
    <div className="space-y-4">
      <Tabs value={posTab} onValueChange={setPosTab}>
        <TabsList className="mb-3">
          <TabsTrigger value="current">当前持仓</TabsTrigger>
          <TabsTrigger value="history">
            历史持仓
            {(deletedMonitors?.length || 0) > 0 && (
              <Badge className="ml-1.5 text-[9px] px-1 bg-muted text-muted-foreground">{deletedMonitors?.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="current">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">持仓数量</div>
            <div className="text-lg font-bold mt-1">{positions?.length || 0} 只</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">持仓市值</div>
            <div className="text-lg font-bold mt-1">¥{totalValue.toFixed(0)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">总盈亏</div>
            <div className={`text-lg font-bold mt-1 ${totalPnl >= 0 ? "text-gain" : "text-loss"}`}>
              {totalPnl >= 0 ? "+" : ""}¥{totalPnl.toFixed(2)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">盈亏比例</div>
            <div className={`text-lg font-bold mt-1 ${totalPnlPct >= 0 ? "text-gain" : "text-loss"}`}>
              {totalPnlPct >= 0 ? "+" : ""}{totalPnlPct.toFixed(2)}%
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />持仓列表
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : positions && positions.length > 0 ? (
            <>
              {/* 桌面端表格 */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs">代码</TableHead>
                      <TableHead className="text-xs">名称</TableHead>
                      <TableHead className="text-xs text-right">数量</TableHead>
                      <TableHead className="text-xs text-right">成本</TableHead>
                      <TableHead className="text-xs text-right">现价</TableHead>
                      <TableHead className="text-xs text-right">盈亏</TableHead>
                      <TableHead className="text-xs text-right">盈亏率</TableHead>
                      <TableHead className="text-xs text-center">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {positions.map((p: any) => (
                      <TableRow key={p.id} className="border-border/30">
                        <TableCell className="text-xs font-mono">{p.stockCode}</TableCell>
                        <TableCell className="text-xs font-medium">{p.stockName}</TableCell>
                        <TableCell className="text-xs text-right">{p.quantity}</TableCell>
                        <TableCell className="text-xs text-right font-mono">¥{p.avgCost.toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">¥{p.currentPrice.toFixed(2)}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${p.profitLoss >= 0 ? "text-gain" : "text-loss"}`}>
                          {p.profitLoss >= 0 ? "+" : ""}¥{p.profitLoss.toFixed(2)}
                        </TableCell>
                        <TableCell className={`text-xs text-right ${p.profitLossPct >= 0 ? "text-gain" : "text-loss"}`}>
                          {p.profitLossPct >= 0 ? "+" : ""}{p.profitLossPct.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px]"
                            onClick={() => handleEditParam(p.stockCode, p.stockName)}>
                            <Settings2 className="h-3 w-3 mr-1" />参数
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* 移动端卡片 */}
              <div className="lg:hidden space-y-2 p-3">
                {positions.map((p: any) => (
                  <div key={p.id} className="rounded-lg border border-border/50 p-3 bg-card/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">{p.stockCode}</span>
                        <span className="text-xs font-medium">{p.stockName}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 text-[10px]"
                        onClick={() => handleEditParam(p.stockCode, p.stockName)}>
                        <Settings2 className="h-3 w-3 mr-1" />参数
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 text-center">
                      <div>
                        <div className="text-[10px] text-muted-foreground">数量</div>
                        <div className="text-xs font-medium">{p.quantity}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">成本</div>
                        <div className="text-xs font-mono">¥{p.avgCost.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">盈亏</div>
                        <div className={`text-xs font-mono font-medium ${p.profitLoss >= 0 ? "text-gain" : "text-loss"}`}>
                          {p.profitLoss >= 0 ? "+" : ""}¥{p.profitLoss.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">盈亏率</div>
                        <div className={`text-xs font-medium ${p.profitLossPct >= 0 ? "text-gain" : "text-loss"}`}>
                          {p.profitLossPct >= 0 ? "+" : ""}{p.profitLossPct.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Briefcase className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">暂无持仓</p>
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="history">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />历史持仓记录
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(deletedMonitors?.length || 0) > 0 ? (
                <>
                  {/* 桌面端表格 */}
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/50">
                          <TableHead className="text-xs">代码</TableHead>
                          <TableHead className="text-xs">名称</TableHead>
                          <TableHead className="text-xs text-right">累计盈亏</TableHead>
                          <TableHead className="text-xs text-right">今日交易</TableHead>
                          <TableHead className="text-xs text-right">删除时间</TableHead>
                          <TableHead className="text-xs text-center">操作</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(deletedMonitors || []).map((m: any) => (
                          <TableRow key={m.id} className="border-border/30">
                            <TableCell className="text-xs font-mono">{m.stockCode}</TableCell>
                            <TableCell className="text-xs font-medium">{m.stockName}</TableCell>
                            <TableCell className={`text-xs text-right font-mono font-medium ${(m.totalProfit || 0) >= 0 ? "text-gain" : "text-loss"}`}>
                              {(m.totalProfit || 0) >= 0 ? "+" : ""}¥{(m.totalProfit || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-xs text-right">{m.todayTradeCount || 0} 次</TableCell>
                            <TableCell className="text-xs text-right text-muted-foreground">
                              {m.deletedAt ? formatBJ(m.deletedAt, "date") : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" className="h-6 text-[10px]"
                                onClick={() => setHistoryRecordMonitor(m)}>
                                <History className="h-3 w-3 mr-1" />交易记录
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* 移动端卡片 */}
                  <div className="lg:hidden space-y-2 p-3">
                    {(deletedMonitors || []).map((m: any) => (
                      <div key={m.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border/50 bg-card/50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-muted-foreground">{m.stockCode}</span>
                            <span className="text-xs font-medium">{m.stockName}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`text-xs font-mono font-medium ${(m.totalProfit || 0) >= 0 ? "text-gain" : "text-loss"}`}>
                              {(m.totalProfit || 0) >= 0 ? "+" : ""}¥{(m.totalProfit || 0).toFixed(2)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">{m.todayTradeCount || 0} 次交易</span>
                            <span className="text-[10px] text-muted-foreground">{m.deletedAt ? formatBJ(m.deletedAt, "date") : "-"}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] shrink-0"
                          onClick={() => setHistoryRecordMonitor(m)}>
                          <History className="h-3 w-3 mr-1" />记录
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <History className="h-10 w-10 mb-3 opacity-30" />
                  <p className="text-sm">暂无历史持仓记录</p>
                  <p className="text-xs mt-1 opacity-60">删除有交易记录的 Agent 监控后，将自动移入此处</p>
                </div>
              )}
            </CardContent>
          </Card>
          {historyRecordMonitor && <TradeRecordsDialog open={!!historyRecordMonitor} onOpenChange={(open) => { if (!open) setHistoryRecordMonitor(null); }} monitor={historyRecordMonitor} />}
        </TabsContent>
      </Tabs>

      {editingStock && (
        <Dialog open={!!editingStock} onOpenChange={() => setEditingStock(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>个股交易参数 - {editingStock}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[
                { key: "bbPeriod", label: "布林带周期", step: 1 },
                { key: "bbStdDev", label: "标准差倍数", step: 0.1 },
                { key: "tradeLotSize", label: "每笔股数", step: 100 },
                { key: "takeProfitPct", label: "止盈 (%)", step: 0.1 },
                { key: "stopLossPct", label: "止损 (%)", step: 0.1 },
              ].map(f => (
                <div key={f.key} className="flex items-center gap-3">
                  <Label className="text-xs w-28 shrink-0">{f.label}</Label>
                  <Input type="number" step={f.step} className="h-8 text-xs"
                    value={(paramForm as any)[f.key]}
                    onChange={e => setParamForm(prev => ({ ...prev, [f.key]: Number(e.target.value) }))} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => {
                const existing = stockParams?.find(sp => sp.stockCode === editingStock);
                if (existing) deleteParam.mutate({ id: existing.id });
                setEditingStock(null);
              }}>恢复默认</Button>
              <Button size="sm" onClick={() => { upsertParam.mutate({ stockCode: editingStock, stockName: editingStock, ...paramForm }); }} disabled={upsertParam.isPending}>
                {upsertParam.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}保存
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ========== 策略回测 ==========
function BacktestPanel() {
  const [, setLocation] = useLocation();
  const { data: config } = trpc.config.get.useQuery(undefined, { staleTime: 300000 });
  const [stockCode, setStockCode] = useState("");
  const [stockName, setStockName] = useState("");
  const [days, setDays] = useState(30);
  const [backtestResult, setBacktestResult] = useState<any>(null);
  const [expandedTrades, setExpandedTrades] = useState(false);

  // P0修复：改用 backtest.run（runBacktestEngine + T+0 s1/s2参数体系）
  // 原 trading.backtest 用旧版布林带策略（moduleF），与当前T+0策略完全不同
  const runBacktestMutation = trpc.backtest.run.useMutation({
    onSuccess: (data: any) => { setBacktestResult(data); toast.success("回测完成（T+0引擎）"); },
    onError: (err: any) => toast.error(`回测失败: ${err.message}`),
  });

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-primary" />策略回测
          </CardTitle>
          <CardDescription className="text-xs">使用 T+0 引擎（s1/s2参数体系）回测，基于当前默认参数，结果与真实策略一致</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">股票代码</Label>
              {/* [P2-65 2026-05-23] 同 add 入口，前端过滤非数字 + 限长 6 位 */}
              <Input className="h-8 text-xs w-32" placeholder="如 600519" maxLength={6} inputMode="numeric"
                value={stockCode} onChange={e => setStockCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">股票名称</Label>
              <Input className="h-8 text-xs w-32" placeholder="如 贵州茅台" value={stockName} onChange={e => setStockName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">回测天数</Label>
              <Input type="number" className="h-8 text-xs w-24" value={days} onChange={e => setDays(Number(e.target.value))} />
            </div>
            <Button size="sm" onClick={() => {
              if (!stockCode) { toast.error("请输入股票代码"); return; }
              runBacktestMutation.mutate({ stockCode, stockName: stockName || stockCode, days, initialCapital: 100000 });
            }} disabled={runBacktestMutation.isPending}>
              {runBacktestMutation.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
              开始回测
            </Button>
          </div>

          {backtestResult && (
            <>
              <Separator />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">总收益率</div>
                  <div className={`text-lg font-bold ${backtestResult.totalReturn >= 0 ? "text-gain" : "text-loss"}`}>
                    {backtestResult.totalReturn >= 0 ? "+" : ""}{backtestResult.totalReturn?.toFixed(2)}%
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">交易次数</div>
                  <div className="text-lg font-bold">{backtestResult.tradeCount || backtestResult.totalTrades || 0}</div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">胜率</div>
                  <div className="text-lg font-bold">{backtestResult.winRate?.toFixed(1)}%</div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">最大回撤</div>
                  <div className="text-lg font-bold text-loss">{backtestResult.maxDrawdown?.toFixed(2)}%</div>
                </div>
              </div>

              {backtestResult.trades && backtestResult.trades.length > 0 && (
                <Card className="border-border/30">
                  <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpandedTrades(!expandedTrades)}>
                    <CardTitle className="text-xs flex items-center gap-2">
                      交易明细 ({backtestResult.trades.length} 笔)
                      {expandedTrades ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </CardTitle>
                  </CardHeader>
                  {expandedTrades && (
                    <CardContent className="p-0">
                      <ScrollArea className="max-h-[300px]">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-border/50">
                              <TableHead className="text-xs">日期</TableHead>
                              <TableHead className="text-xs">方向</TableHead>
                              <TableHead className="text-xs text-right">价格</TableHead>
                              <TableHead className="text-xs text-right">数量</TableHead>
                              <TableHead className="text-xs text-right">金额</TableHead>
                              <TableHead className="text-xs text-right">盈亏率</TableHead>
                              <TableHead className="text-xs text-right">持有天数</TableHead>
                              <TableHead className="text-xs">原因</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {backtestResult.trades.map((t: any, i: number) => (
                              <TableRow key={i} className="border-border/30">
                                <TableCell className="text-xs">{t.date}</TableCell>
                                <TableCell>
                                  <Badge className={`text-[10px] ${t.type === "buy" ? "bg-gain/20 text-gain" : "bg-loss/20 text-loss"}`}>
                                    {t.type === "buy" ? "买入" : "卖出"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-right font-mono">{t.price.toFixed(2)}</TableCell>
                                <TableCell className="text-xs text-right">{t.quantity}</TableCell>
                                <TableCell className="text-xs text-right font-mono">{t.amount.toFixed(0)}</TableCell>
                                <TableCell className={`text-xs text-right font-medium ${t.pnl !== undefined ? (t.pnl >= 0 ? "text-gain" : "text-loss") : ""}`}>
                                  {t.pnl !== undefined ? `${t.pnl >= 0 ? "+" : ""}${t.pnlPct?.toFixed(2)}%` : "-"}
                                </TableCell>
                                <TableCell className="text-xs text-right">{t.holdingDays ?? "-"}</TableCell>
                                <TableCell className="text-xs max-w-[300px] whitespace-pre-wrap break-words" title={t.reason}>{t.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </CardContent>
                  )}
                </Card>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ========== 资金分配 Tab ==========
function CapitalAllocationTab() {
  return <CapitalAllocationPanel />;
}

// ========== 模拟测试面板 ==========
function SimulationPanel() {
  // staleTime: 复用主组件缓存，30s内不重复请求
  const { data: monitors } = trpc.agentMonitor.list.useQuery(undefined, { staleTime: 30000 });
  const [selectedStock, setSelectedStock] = useState("");
  const [simStatus, setSimStatus] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionId, setSessionId] = useState("");

  const startSim = trpc.agentMonitor.simulationStart.useMutation({
    onSuccess: (data: any) => {
      setSimStatus(data);
      setSessionId(data.sessionId || `sim_${selectedStock}_${Date.now()}`);
      setIsRunning(true);
      toast.success("模拟测试已启动");
    },
    onError: (err: any) => toast.error(`启动失败: ${err.message}`),
  });

  const stopSim = trpc.agentMonitor.simulationStop.useMutation({
    onSuccess: () => {
      setIsRunning(false);
      toast.success("模拟测试已停止");
    },
    onError: (err: any) => toast.error(`停止失败: ${err.message}`),
  });

  const { data: simState } = trpc.agentMonitor.simulationStatus.useQuery(
    { sessionId },
    { enabled: isRunning && !!sessionId, refetchInterval: 2000 }
  );

  useEffect(() => {
    if (simState) setSimStatus(simState);
  }, [simState]);

  const activeMonitors = monitors?.filter((m: any) => m.isActive) ?? [];

  return (
    <div className="space-y-4">
      <Card className="border-border/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Film className="h-4 w-4 text-primary" />模拟测试
          </CardTitle>
          <CardDescription className="text-xs">
            基于 SMT-Q Level-1 五档实时行情数据，使用当前交易策略参数进行模拟交易，不产生实际委托单
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 股票选择和控制 */}
          <div className="flex items-end gap-3">
            <div className="space-y-1 flex-1">
              <Label className="text-xs">选择监控股票</Label>
              <Select value={selectedStock} onValueChange={setSelectedStock}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="选择一只监控中的股票" />
                </SelectTrigger>
                <SelectContent>
                  {activeMonitors.map((m: any) => (
                    <SelectItem key={m.stockCode} value={m.stockCode}>
                      {m.stockName} ({m.stockCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isRunning ? (
              <Button size="sm" disabled={!selectedStock || startSim.isPending} onClick={() => { const m = activeMonitors.find((m: any) => m.stockCode === selectedStock); startSim.mutate({ stockCode: selectedStock, stockName: m?.stockName || selectedStock }); }}>
                {startSim.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <PlayCircle className="h-3 w-3 mr-1" />}
                启动模拟
              </Button>
            ) : (
              <Button size="sm" variant="destructive" disabled={stopSim.isPending} onClick={() => stopSim.mutate({ sessionId })}>
                {stopSim.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <StopCircle className="h-3 w-3 mr-1" />}
                停止模拟
              </Button>
            )}
          </div>

          {/* 模拟状态展示 */}
          {simStatus && (
            <div className="space-y-3">
              {/* 运行状态概览 */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                  <div className="text-[10px] text-muted-foreground">状态</div>
                  <div className="text-sm font-medium mt-1">
                    <Badge className={isRunning ? "bg-green-500/20 text-green-400" : "bg-muted text-muted-foreground"}>
                      {isRunning ? "运行中" : "已停止"}
                    </Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                  <div className="text-[10px] text-muted-foreground">当前场景</div>
                  <div className="text-sm font-medium mt-1">
                    {simStatus.scenario === 's1' ? <Badge className="bg-gain/20 text-gain">方向一·追高</Badge> :
                     simStatus.scenario === 's2' ? <Badge className="bg-loss/20 text-loss">方向二·探底</Badge> :
                     <Badge className="bg-muted text-muted-foreground">等待中</Badge>}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                  <div className="text-[10px] text-muted-foreground">当前轮次</div>
                  <div className="text-sm font-bold mt-1">R{simStatus.currentRound || 0}</div>
                </div>
                <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                  <div className="text-[10px] text-muted-foreground">模拟盈亏</div>
                  <div className={`text-sm font-bold mt-1 ${(simStatus.totalPnl || 0) >= 0 ? 'text-gain' : 'text-loss'}`}>
                    {(simStatus.totalPnl || 0) >= 0 ? '+' : ''}{(simStatus.totalPnl || 0).toFixed(2)}元
                  </div>
                </div>
              </div>

              {/* 引擎状态详情 */}
              <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                <div className="text-xs font-medium mb-2 flex items-center gap-2">
                  <Gauge className="h-3.5 w-3.5 text-primary" />引擎状态
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">当前价: </span>
                    <span className="font-mono font-medium">{simStatus.currentPrice?.toFixed(2) || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">基准价: </span>
                    <span className="font-mono font-medium">{simStatus.basisPrice?.toFixed(2) || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">步骤: </span>
                    <span className="font-medium">{simStatus.phase || '等待'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">追踪极值: </span>
                    <span className="font-mono font-medium">{simStatus.trackedExtreme?.toFixed(2) || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">行情延迟: </span>
                    <span className={`font-medium ${
                      (simStatus.latencyMs || 0) < 200 ? 'text-green-400' :
                      (simStatus.latencyMs || 0) < 500 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{simStatus.latencyMs || 0}ms</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">挂单策略: </span>
                    <span className="font-medium">
                      {simStatus.orderStrategy === 'opponent' ? '对手价' :
                       simStatus.orderStrategy === 'opponent_plus1' ? '对手价+1' :
                       simStatus.orderStrategy === 'weighted_mid' ? '加权中间价' :
                       simStatus.orderStrategy === 'smart_chase' ? '智能追单' : '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* 模拟交易记录 */}
              {simStatus.trades && simStatus.trades.length > 0 && (
                <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                  <div className="text-xs font-medium mb-2 flex items-center gap-2">
                    <History className="h-3.5 w-3.5 text-primary" />模拟交易记录 ({simStatus.trades.length}笔)
                  </div>
                  <ScrollArea className="max-h-[300px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/30">
                          <TableHead className="text-[10px]">时间</TableHead>
                          <TableHead className="text-[10px]">场景</TableHead>
                          <TableHead className="text-[10px]">方向</TableHead>
                          <TableHead className="text-[10px] text-right">价格</TableHead>
                          <TableHead className="text-[10px] text-right">数量</TableHead>
                          <TableHead className="text-[10px] text-right">盈亏</TableHead>
                          <TableHead className="text-[10px]">原因</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {simStatus.trades.map((t: any, i: number) => (
                          <TableRow key={i} className="border-border/30">
                            <TableCell className="text-[10px] font-mono">{formatBJ(t.time, "time")}</TableCell>
                            <TableCell>
                              <Badge className={`text-[9px] ${t.scenario === 's1' ? 'bg-gain/20 text-gain' : 'bg-loss/20 text-loss'}`}>
                                {t.scenario === 's1' ? 'S1' : 'S2'} R{t.round}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-[9px] ${t.side === 'buy' ? 'bg-gain/20 text-gain' : 'bg-loss/20 text-loss'}`}>
                                {t.side === 'buy' ? '买入' : '卖出'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[10px] text-right font-mono">{t.price?.toFixed(2)}</TableCell>
                            <TableCell className="text-[10px] text-right">{t.quantity}</TableCell>
                            <TableCell className={`text-[10px] text-right font-mono ${(t.pnl || 0) >= 0 ? 'text-gain' : 'text-loss'}`}>
                              {t.pnl !== undefined ? `${t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell className="text-[10px]">
                              <Badge variant="outline" className={`text-[9px] ${
                                t.reason === 'confirm' ? 'border-green-500/30 text-green-400' :
                                t.reason === 'timeout' ? 'border-yellow-500/30 text-yellow-400' :
                                t.reason === 'stopLoss' ? 'border-red-500/30 text-red-400' : ''
                              }`}>
                                {t.reason === 'confirm' ? '确认' : t.reason === 'timeout' ? '超时' : t.reason === 'stopLoss' ? '止损' : t.reason || '-'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}

              {/* 提示信息 */}
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <AlertCircle className="h-3 w-3" />
                <span>模拟测试使用 SMT-Q Level-1 五档实时行情数据，不产生实际委托单。行情延迟显示在引擎状态中。</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ========== T+0交易主页面 ==========
function DailyAdviceListTab() {
  const { data: monitors } = trpc.agentMonitor.list.useQuery(undefined, { staleTime: 30000 });
  return (<div className="space-y-4 p-1"><div><h3 className="text-base font-semibold">个股操作建议</h3><p className="text-xs text-muted-foreground mt-0.5">根据大盘环境和个股表现，AI给出当天操作建议和参数微调</p></div>{(monitors||[]).filter((m:any)=>!m.isDeleted).map((m:any)=>(<DailyAdvicePanel key={m.id} monitorId={m.id} stockName={m.stockName} />))}</div>);
}

// ========== [v2.0 2026-04-29] 引擎主备状态面板 ==========
// 显示当前主引擎 + 所有备机 + 4维健康检查 (心跳/券商/miniQMT/进程)
// 让用户判断是否适合手动接管
function EngineHARolePanel() {
  const { data: sysState, refetch } = trpc.system.getState.useQuery(undefined, {
    refetchInterval: 15000,  // 15s 自动刷新
    staleTime: 10000,
  });
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem('haPanel_expanded') !== '0'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('haPanel_expanded', expanded ? '1' : '0'); } catch {}
  }, [expanded]);

  if (!sysState) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Server className="h-4 w-4" />
            正在加载引擎主备状态...
          </div>
        </CardContent>
      </Card>
    );
  }

  const ha: any = (sysState as any).ha || {};
  const nodes: any[] = ha.nodes || [];
  const currentLeader: any = ha.currentLeader;
  const multiPrimaryDetected: boolean = !!ha.multiPrimaryDetected;
  const activeEngineHealth: string = ha.activeEngineHealth || 'unknown';

  // 状态颜色映射
  const statusColor = (s: string) => {
    if (s === 'healthy') return 'text-green-400';
    if (s === 'degraded') return 'text-amber-400';
    if (s === 'offline') return 'text-red-400';
    return 'text-muted-foreground';
  };
  const statusBg = (s: string) => {
    if (s === 'healthy') return 'bg-green-500/10 border-green-500/30';
    if (s === 'degraded') return 'bg-amber-500/10 border-amber-500/30';
    if (s === 'offline') return 'bg-red-500/10 border-red-500/30';
    return 'bg-card/50 border-border/50';
  };
  const statusLabel = (s: string) => {
    if (s === 'healthy') return '健康';
    if (s === 'degraded') return '异常';
    if (s === 'offline') return '离线';
    return '未知';
  };
  const statusIcon = (s: string) => {
    if (s === 'healthy') return '✓';
    if (s === 'degraded') return '⚠';
    if (s === 'offline') return '✗';
    return '?';
  };

  return (
    <Card className={multiPrimaryDetected ? "border-destructive/50 bg-destructive/5" : "bg-card border-border/50"}>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Server className="h-4 w-4 text-primary" />
            引擎主备状态
            {multiPrimaryDetected && (
              <Badge variant="destructive" className="text-[9px] ml-1">⚠ 多主异常</Badge>
            )}
            {!multiPrimaryDetected && currentLeader && (
              <Badge variant="outline" className={"text-[9px] ml-1 " + statusColor(currentLeader.overallStatus)}>
                {statusIcon(currentLeader.overallStatus)} {currentLeader.name}
              </Badge>
            )}
            <span className="text-[10px] text-muted-foreground" title="鼠标悬停看接管建议">
              ({nodes.filter((n: any) => n.canTakeover).length} 可接管)
            </span>
          </span>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); refetch(); }}>
            <RefreshCw className="h-3 w-3" />
          </Button>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 pb-3 space-y-2">
          {nodes.length === 0 && (
            <div className="text-xs text-muted-foreground">无引擎节点</div>
          )}
          {nodes.map((n: any) => (
            <div
              key={n.id}
              className={"rounded border p-2.5 transition-colors " + statusBg(n.overallStatus)}
              title={n.overallReason || ''}
            >
              <div className="flex items-center justify-between flex-wrap gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{n.name}</span>
                  <Badge variant={n.role === 'primary' ? 'default' : 'outline'} className="text-[9px] px-1">
                    {n.role === 'primary' ? '主' : '备'}
                  </Badge>
                  {n.isCurrentLeader && (
                    <Badge variant="default" className="text-[9px] px-1 bg-blue-500/20 text-blue-400 border-blue-500/40">
                      当前使用
                    </Badge>
                  )}
                  {n.canTakeover && (
                    <Badge variant="outline" className="text-[9px] px-1 bg-green-500/20 text-green-400 border-green-500/40">
                      ⚡ 可接管
                    </Badge>
                  )}
                  {n.isActive === 0 && (
                    <Badge variant="outline" className="text-[9px] px-1 text-muted-foreground">
                      已停用
                    </Badge>
                  )}
                  <span className={"text-xs font-medium " + statusColor(n.overallStatus)}>
                    {statusIcon(n.overallStatus)} {statusLabel(n.overallStatus)}
                  </span>
                </div>
                <span className="text-[10px] text-muted-foreground" title="心跳年龄(秒)">
                  心跳 {n.heartbeatAgeSec >= 99999 ? '∞' : n.heartbeatAgeSec + 's'}
                </span>
              </div>
              {/* 4 维 checks 详情 */}
              <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-1 text-[10px]">
                <span className={n.checks?.processAlive ? 'text-green-400' : 'text-red-400'} title="进程响应">
                  {n.checks?.processAlive ? '✓' : '✗'} 进程
                </span>
                <span className={n.checks?.heartbeatFresh ? 'text-green-400' : 'text-red-400'} title="心跳<90s">
                  {n.checks?.heartbeatFresh ? '✓' : '✗'} 心跳
                </span>
                <span className={n.checks?.brokerConnected ? 'text-green-400' : 'text-red-400'} title="券商连接">
                  {n.checks?.brokerConnected ? '✓' : '✗'} 券商
                </span>
                <span className={n.checks?.miniQmtRunning ? 'text-green-400' : 'text-red-400'} title="miniQMT 进程">
                  {n.checks?.miniQmtRunning ? '✓' : '✗'} miniQMT
                </span>
              </div>
              {n.overallReason && (
                <div className="mt-1 text-[10px] text-muted-foreground italic">
                  {n.overallReason}
                </div>
              )}
              {/* 接管按钮 (仅在可接管 + 不是当前 leader 时显示) */}
              {n.canTakeover && (
                <div className="mt-1.5 text-[10px] text-amber-300/70">
                  💡 此节点可作为故障转移目标. 请在"系统管理 / HA 切换"页面执行手动接管.
                </div>
              )}
            </div>
          ))}
          {multiPrimaryDetected && (
            <div className="rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              ⚠ <b>检测到多个 primary 引擎</b> — 这违反了 uk_one_primary 唯一约束, 通常是 04-28 类事故的征兆.
              请立即检查 engine_nodes 表并手动修复角色冲突.
            </div>
          )}
          {activeEngineHealth === 'unhealthy' && !multiPrimaryDetected && (
            <div className="rounded border border-amber-500/50 bg-amber-500/10 p-2 text-xs text-amber-200">
              ⚠ 当前主引擎不健康. 警告: <b>{ha.activeEngineWarn || '未知'}</b>.
              如果有"⚡可接管"的备机, 建议手动切换.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// [20260812 Steven UX] 单标题重构: 页内 h1 的徽章(交易模式/熔断/引擎状态)抽成独立组件,
// 桌面端保留在 h1 行, 移动端通过 portal 渲染进 DashboardLayout 顶栏 #hdr-badge-slot。
// _engOpen 状态仍属 AgentManage, 通过 props 下传 (逻辑原样)。
function HeaderBadges(props: {
  tradeModeDisplay: { label: string; cls: string };
  cbActive?: boolean;
  cbIndexChange?: number | null;
  engOpen: boolean;
  engAbnormal: boolean;
  engOnline: boolean;
  engLat: number;
  onToggleEng: () => void;
}) {
  const { tradeModeDisplay, cbActive, cbIndexChange, engOpen, engAbnormal, engOnline, engLat, onToggleEng } = props;
  return (
    <>
      <a href="/config" title="点击前往参数配置页修改交易模式（参数配置页是唯一交易模式控制入口）">
        <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity " + tradeModeDisplay.cls}>
          {tradeModeDisplay.label}
        </span>
      </a>
      {/* [Steven 2026-05-28] 熔断状态 - 仅熔断时显示在标题栏, 不独占一行 */}
      {cbActive && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-destructive/50 bg-destructive/15 text-destructive animate-pulse" title={`大盘熔断已触发, Agent 已暂停买入${cbIndexChange != null ? ` (上证 ${cbIndexChange.toFixed(2)}%)` : ''}`}>
          <Shield className="h-3 w-3" />熔断
        </span>
      )}
      {/* [20260811 Steven UX] 引擎状态迷你徽章(原大面板收纳于此, 点击展开; 异常自动展开+红脉冲) */}
      <button
        onClick={onToggleEng}
        title={engOpen ? '收起引擎状态面板' : '展开引擎状态面板'}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
          engAbnormal ? 'border-red-500/60 bg-red-500/15 text-red-400 animate-pulse'
          : engOnline ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
          : 'border-muted-foreground/30 text-muted-foreground'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${engAbnormal ? 'bg-red-400' : engOnline ? 'bg-emerald-400' : 'bg-muted-foreground/50'}`} />
        引擎{engAbnormal ? '异常' : engOnline ? '在线' : '…'}{engOnline && engLat >= 0 ? ` ${Math.round(engLat)}ms` : ''}
        <span className="text-[9px] opacity-60">{engOpen ? '▲' : '▼'}</span>
      </button>
    </>
  );
}

export default function AgentManage() {
  const [activeTab, setActiveTab] = useState("home");
  // [15b] 线切换: t0 | swing (两条线平级); ?tab=swing 深链 + /swing-trade 重定向落点
  const [_line, _setLine] = useState<"t0" | "swing">(() => {
    try {
      return new URLSearchParams(window.location.search).get("tab") === "swing" ? "swing" : "t0";
    } catch { return "t0"; }
  });
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (_line === "swing") u.searchParams.set("tab", "swing");
      else u.searchParams.delete("tab");
      window.history.replaceState(null, "", u.pathname + u.search);
    } catch { /* 历史 API 不可用不阻塞渲染 */ }
  }, [_line]);
  const { data: ifConfig } = trpc.interfaceConfig.get.useQuery(undefined, { staleTime: 30000 });
  const tradeMode = (ifConfig as any)?.tradeMode || "simulation";
  // [Steven 2026-05-28] 熔断状态移到标题栏 (实盘观察右侧), 不独占一行
  const { data: _hdrDashboard } = trpc.agentMonitor.dashboard.useQuery(undefined, {
    refetchInterval: 30000, staleTime: 10000,
  });
  const _hdrCb = (_hdrDashboard as any)?.circuitBreaker;

  const tradeModeDisplay = {
    simulation: { label: "模拟交易", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
    smtq:       { label: "⚡ 实盘交易", cls: "bg-red-500/15 text-red-400 border-red-500/30 animate-pulse" },
  }[tradeMode as 'simulation' | 'smtq'] || { label: tradeMode, cls: "bg-muted text-muted-foreground border-border" };

  // [20260811 Steven UX] 引擎状态迷你化: 标题行徽章+点击展开, 异常(非connected)自动醒目展开
  const [_engOpen, _setEngOpen] = useState(false);
  const { data: _engDash } = trpc.tradingProxy.dashboard.useQuery(undefined, { refetchInterval: 20000, retry: 1, refetchOnWindowFocus: false });
  const _engConn = (_engDash as any)?.connection?.status || 'pending';
  const _engOnline = _engConn === 'connected';
  const _engLat = Number((_engDash as any)?.latency?.ms ?? -1);
  const _engAbnormal = _engDash !== undefined && !_engOnline;
  useEffect(() => { if (_engAbnormal) _setEngOpen(true); }, [_engAbnormal]);

  // [20260812 Steven UX] 移动端: 徽章 portal 进顶栏 #hdr-badge-slot, 页内 h1 整行隐藏
  const _isMobile = useIsMobile();
  const [_hdrSlot, _setHdrSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!_isMobile) { _setHdrSlot(null); return; }
    const el = document.getElementById('hdr-badge-slot');
    _setHdrSlot((el as HTMLElement) || null);
  }, [_isMobile]);
  const _h1Hidden = _isMobile && !!_hdrSlot;
  const _headerBadges = (
    <HeaderBadges
      tradeModeDisplay={tradeModeDisplay}
      cbActive={_hdrCb?.active}
      cbIndexChange={_hdrCb?.indexChange ?? null}
      engOpen={_engOpen}
      engAbnormal={_engAbnormal}
      engOnline={_engOnline}
      engLat={_engLat}
      onToggleEng={() => _setEngOpen(v => !v)}
    />
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-xl font-bold flex items-center gap-2${_h1Hidden ? ' hidden' : ''}`}>
          <Bot className="h-5 w-5 text-primary" />自动化交易
          {(!_isMobile || !_hdrSlot) && _headerBadges}
        </h1>
        {_isMobile && _hdrSlot && createPortal(_headerBadges, _hdrSlot)}
      </div>

      {/* [15b] 线切换: 两条线平级(非标签降级); 同一时刻只渲染一条线的内容区 */}
      <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
        <button type="button" onClick={() => _setLine("t0")}
          className={`px-3 py-1 text-xs rounded-md transition ${_line === "t0" ? "bg-background shadow-sm font-semibold" : "text-muted-foreground"}`}>
          T+0 交易
        </button>
        <button type="button" onClick={() => _setLine("swing")}
          className={`px-3 py-1 text-xs rounded-md transition ${_line === "swing" ? "bg-background shadow-sm font-semibold" : "text-muted-foreground"}`}>
          波段交易
        </button>
      </div>

      {/* [15b] 常驻两线盈亏条: 两种视图下都渲染, 切线不消失(口径C, 日线不合计) */}
      <TradingOverviewStrip />
      {/* [20260811 Steven UX] 完整引擎面板: 默认收纳, 点徽章或异常时展开 */}
      {_engOpen && <EngineStatusPanel />}

      {/* [G/H Alerts 2026-04-30] 盘中异常预警 banner */}
      <AgentAnomalyBanner />

      {/* [15b] T+0 线内容区: 仅 _line==="t0" 时渲染 */}
      {_line === "t0" && (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full overflow-x-auto">
          <TabsTrigger value="home" className="text-xs"><Activity className="h-3.5 w-3.5 mr-1" />首页</TabsTrigger>
          <TabsTrigger value="sim-performance" className="text-xs"><BarChart3 className="h-3.5 w-3.5 mr-1" />模拟绩效</TabsTrigger>
          <TabsTrigger value="positions" className="text-xs"><Briefcase className="h-3.5 w-3.5 mr-1" />持仓管理</TabsTrigger>
          <TabsTrigger value="simulation" className="text-xs"><Film className="h-3.5 w-3.5 mr-1" />模拟测试</TabsTrigger>
          <TabsTrigger value="backtest" className="text-xs"><BarChart3 className="h-3.5 w-3.5 mr-1" />策略回测</TabsTrigger>
          <TabsTrigger value="param-analysis" className="text-xs"><LineChart className="h-3.5 w-3.5 mr-1" />参数分析</TabsTrigger>
          <TabsTrigger value="capital" className="text-xs"><Wallet className="h-3.5 w-3.5 mr-1" />资金分配</TabsTrigger>
          <TabsTrigger value="portfolio" className="text-xs"><Layers className="h-3.5 w-3.5 mr-1" />组合看板</TabsTrigger>
          <TabsTrigger value="daily-advice" className="text-xs"><Brain className="h-3.5 w-3.5 mr-1" />操作建议</TabsTrigger>
          <TabsTrigger value="templates" className="text-xs"><Library className="h-3.5 w-3.5 mr-1" />策略模板</TabsTrigger>
        </TabsList>
        <TabsContent value="home" className="mt-4">{activeTab === "home" && <TradingHome onSwitchTab={setActiveTab} />}</TabsContent>
        <TabsContent value="sim-performance" className="mt-4">{activeTab === "sim-performance" && <SimPerformanceDashboard />}</TabsContent>
        <TabsContent value="positions" className="mt-4">{activeTab === "positions" && <PositionManagement />}</TabsContent>
        <TabsContent value="simulation" className="mt-4">{activeTab === "simulation" && <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading...</div>}><SimulationTest /></Suspense>}</TabsContent>
        <TabsContent value="backtest" className="mt-4">{activeTab === "backtest" && <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading...</div>}><Backtest /></Suspense>}</TabsContent>
        <TabsContent value="param-analysis" className="mt-4">{activeTab === "param-analysis" && <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading...</div>}><ParamAnalysis /></Suspense>}</TabsContent>
        <TabsContent value="capital" className="mt-4">{activeTab === "capital" && <CapitalAllocationTab />}</TabsContent>
        <TabsContent value="portfolio" className="mt-4">{activeTab === "portfolio" && <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading...</div>}><Portfolio /></Suspense>}</TabsContent>
        <TabsContent value="daily-advice" className="mt-4">{activeTab === "daily-advice" && <DailyAdviceListTab />}</TabsContent>
        <TabsContent value="templates" className="mt-4">
          {activeTab === "templates" && (
            <Suspense fallback={<div className="p-8 text-center text-sm text-muted-foreground">加载策略模板库...</div>}>
              <StrategyTemplates />
            </Suspense>
          )}
        </TabsContent>
      </Tabs>
      )}

      {/* [15b] 波段线内容区: 复用 SwingTrade 页面组件, 不重写 */}
      {_line === "swing" && (
        <Suspense fallback={<div className="p-8 text-center text-sm text-muted-foreground">加载波段交易...</div>}>
          <SwingTrade />
        </Suspense>
      )}
    </div>
  );
}

// build:1778165105


// ========== [Claude 2026-05-08] 单股自动优化对话框内容组件 ==========
